import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import {
  IconPlus,
  IconRefresh,
  IconLoader2,
} from '@tabler/icons-react';
import {
  Node,
  Edge,
  addEdge,
  reconnectEdge,
  Connection,
  useNodesState,
  useEdgesState,
  NodeChange,
  EdgeChange,
  NodeTypes,
  EdgeTypes,
} from 'reactflow';
import 'reactflow/dist/style.css';
// Import types from extracted types module
import type {
  Survey,
} from './surveys/types';
import {
  BASE_NODE_STYLE,
  END_NODE_STYLE,
  BASE_X,
  CONDITIONAL_LEFT_X,
  CONDITIONAL_RIGHT_X,
  VERTICAL_SPACING,
  CONDITIONAL_EDGE_STYLE,
  CONDITIONAL_OFFSET_Y,
  createStartNode,
  createEndNode,
  INITIAL_NODES,
  INITIAL_EDGES,
} from './surveys/constants';
import {
  extractNumericId,
  isStartNode,
  isEndNode,
  ensureStartEndNodes,
  layoutLinearNodes,
  buildLinearEdgesFromNodes,
  mergeLinearEdges,
  findConnectedPathFromStartToEnd,
  extractOptionsFromLabel,
  parseQuestionText,
  determineQuestionType,
  convertFlowToSurveyFormat,
  getNodeOptions,
  removeOptionFromLabelText,
  isConditionalEdge,
  generateFlowFromTemplate,
  sortSurveysById,
  buildSurveyLink,
  renumberQuestionsInFlow,
  ensureStartToDoneConnection,
  reconnectAfterDeletion,
  canStartReachEnd,
  hasAtLeastOneQuestionInPath as hasAtLeastOneQuestionInPathFromStartToDone,
  rebuildEdgeLabelFromQuestion,
  allOptionsAreConditional,
  calculateOptimalLabelPositions,
} from './surveys/utils';
import {
  DraggableEdge,
  edgeUpdateModeRef,
  onEdgeUpdateStartRef,
  onConnectStartRef,
  edgeDragStateRef,
  globalEdgesRef,
  isEdgeDraggingRef,
  globalSetEdgesRef,
} from './surveys/components/DraggableEdge';
import { QuestionNode } from './surveys/components/QuestionNode';
import { SurveyList } from './surveys/components/SurveyList';
import api from '../lib/api';
import { toast } from 'sonner';

// Define nodeTypes and edgeTypes outside component to prevent recreation on every render
const nodeTypes: NodeTypes = { question: QuestionNode };
const edgeTypes: EdgeTypes = {
  default: DraggableEdge,
  smoothstep: DraggableEdge,
  step: DraggableEdge,
  straight: DraggableEdge,
  bezier: DraggableEdge,
};

export function ManageSurveysPage() {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);
  const [editingSurvey, setEditingSurvey] = useState<Survey | null>(null);
  const [surveyName, setSurveyName] = useState('');
  const [surveyDescription, setSurveyDescription] = useState('');
  const [surveyLink, setSurveyLink] = useState('');
  const [surveyStartDate, setSurveyStartDate] = useState('');
  const [surveyEndDate, setSurveyEndDate] = useState('');
  const [surveyDataReward, setSurveyDataReward] = useState('');
  const [surveyBrandId, setSurveyBrandId] = useState('');
  const [surveyTypeformId, setSurveyTypeformId] = useState('');
  const [surveyIconUrl, setSurveyIconUrl] = useState('');
  const [surveyIsActive, setSurveyIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>('panel1');
  const [validationErrors, setValidationErrors] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});
  
  // Validation for date range and brand ID
  const isDateRangeValid = useMemo(() => {
    if (!surveyStartDate || !surveyEndDate) return true; // Allow if dates are empty (will be caught by required check)
    const start = new Date(surveyStartDate);
    const end = new Date(surveyEndDate);
    return !isNaN(start.getTime()) && !isNaN(end.getTime()) && end >= start;
  }, [surveyStartDate, surveyEndDate]);
  
  const isBrandIdValid = useMemo(() => {
    return surveyBrandId.trim() !== '';
  }, [surveyBrandId]);
  
  // React Flow state
  const [nodes, setNodes, onNodesChangeBase] = useNodesState(INITIAL_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(INITIAL_EDGES);
  
  // Wrapped handler that clears labelPosition when nodes are moved
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    onNodesChangeBase(changes);
    
    // Clear labelPosition from edges when nodes are moved so labels update with edge movement
    // Only clear if nodes are actually being moved (not just selected)
    const hasPositionChange = changes.some(change => 
      change.type === 'position' && change.dragging === false // Node was moved (drag ended)
    );
    
    if (hasPositionChange) {
      setEdges((currentEdges) => {
        return currentEdges.map(edge => {
          // Clear labelPosition so label uses default midpoint which updates automatically
          if (edge.data?.labelPosition) {
            const { labelPosition, ...restData } = edge.data;
            return {
              ...edge,
              data: restData,
            };
          }
          return edge;
        });
      });
    }
  }, [onNodesChangeBase, setEdges]);
  
  // Validation: Check if there's at least 1 question linked from start to done
  const hasAtLeastOneQuestionInPath = useMemo(() => {
    return hasAtLeastOneQuestionInPathFromStartToDone(nodes, edges);
  }, [nodes, edges]);
  const [nodeIdCounter, setNodeIdCounter] = useState(2);
  const [connectionLineType, setConnectionLineType] = useState<'straight' | 'step' | 'smoothstep' | 'bezier'>('smoothstep');
  
  // Update global refs so edge component can access them
  useEffect(() => {
    globalEdgesRef.current = edges;
    globalSetEdgesRef.current = setEdges;
    return () => {
      globalEdgesRef.current = [];
      globalSetEdgesRef.current = null;
    };
  }, [edges, setEdges]);
  
  // Cleanup function to remove orphaned edges (edges with missing source or target nodes)
  const removeOrphanedEdges = useCallback((currentNodes: Node[], currentEdges: Edge[]): Edge[] => {
    const nodeIds = new Set(currentNodes.map(n => n.id));
    const validEdges = currentEdges.filter(edge => {
      const hasSource = nodeIds.has(edge.source);
      const hasTarget = nodeIds.has(edge.target);
      
      if (!hasSource || !hasTarget) {
        console.log('🧹 Removing orphaned edge:', {
          edgeId: edge.id,
          source: edge.source,
          target: edge.target,
          sourceExists: hasSource,
          targetExists: hasTarget,
        });
        return false;
      }
      return true;
    });
    
    if (validEdges.length !== currentEdges.length) {
      console.log(`🧹 Cleaned up ${currentEdges.length - validEdges.length} orphaned edge(s)`);
    }
    
    return validEdges;
  }, []);
  
  // Cleanup orphaned edges whenever nodes or edges change
  useEffect(() => {
    const cleanedEdges = removeOrphanedEdges(nodes, edges);
    if (cleanedEdges.length !== edges.length) {
      setEdges(cleanedEdges);
    }
  }, [nodes, edges, removeOrphanedEdges, setEdges]);
  
  // Feature 9: Trigger on any change in interactive survey editor
  // This ensures start-to-done connection and renumbers questions
  const handleFlowStructureChange = useCallback(() => {
    // Ensure start-to-done connection
    setEdges((currentEdges) => {
      const reconnectedEdges = ensureStartToDoneConnection(nodes, currentEdges);
      return reconnectedEdges;
    });
    
    // Renumber questions
    setTimeout(() => {
      setNodes((currentNodes) => {
        setEdges((currentEdges) => {
          const { nodes: renumberedNodes, edges: renumberedEdges } = renumberQuestionsInFlow(currentNodes, currentEdges);
          setNodes(renumberedNodes);
          return renumberedEdges;
        });
        return currentNodes;
      });
    }, 0);
  }, [nodes, setNodes, setEdges]);

  // Renumber questions when flow structure changes
  useEffect(() => {
    // Only renumber if we have questions (not just start/end)
    const questionNodes = nodes.filter(n => n.id !== 'start' && n.id !== 'end');
    if (questionNodes.length === 0) return;
    
    // Find the main path from start to end
  const adjacencyList = new Map<string, string[]>();
    edges.forEach(edge => {
    if (!adjacencyList.has(edge.source)) {
      adjacencyList.set(edge.source, []);
    }
    adjacencyList.get(edge.source)!.push(edge.target);
  });

  const findMainPath = (): string[] => {
    const path: string[] = ['start'];
    const visited = new Set<string>(['start']);
    let current = 'start';
    
    while (current !== 'end') {
        const neighbors = adjacencyList.get(current) || [];
        const nonConditional = neighbors.filter(targetId => {
          const edge = edges.find(e => e.source === current && e.target === targetId);
          return !isConditionalEdge(edge);
        });
        const nextId = nonConditional.length > 0 ? nonConditional[0] : neighbors[0];
        
        if (!nextId || visited.has(nextId)) {
        break;
      }
      
        current = nextId;
      path.push(current);
      visited.add(current);
    }
    
    return path;
  };
  
  const mainPath = findMainPath();
    if (mainPath.length <= 2) return; // Only start and end, no questions
    
    // Check if renumbering is needed by comparing current labels with expected order
    let needsRenumbering = false;
    let expectedNumber = 1;
    
    for (let i = 1; i < mainPath.length - 1; i++) {
      const nodeId = mainPath[i];
      const node = nodes.find(n => n.id === nodeId);
    if (!node) continue;
    
    const label = node.data?.label || '';
      const match = label.match(/^Q(\d+(?:\.\d+)?):/);
      const currentNumber = match ? parseFloat(match[1]) : null;
      
      if (currentNumber !== expectedNumber) {
        needsRenumbering = true;
        break;
      }
      expectedNumber++;
    }
    
    if (needsRenumbering) {
      handleFlowStructureChange();
    }
  }, [nodes, edges, setNodes, handleFlowStructureChange]);
  
  // Track viewport state for offset logic
  const lastViewportRef = useRef<{ x: number; y: number; zoom: number } | null>(null);
  const questionCountAtLastViewportRef = useRef<number>(0);
  
  // Feature 9: Track auto-trigger attempts to prevent infinite loops
  const feature9AutoTriggerCountRef = useRef<number>(0);
  const feature9LastTriggerTimeRef = useRef<number>(0);
  const MAX_AUTO_TRIGGER_ATTEMPTS = 3; // Maximum attempts within a time window
  const AUTO_TRIGGER_COOLDOWN_MS = 2000; // 2 seconds cooldown between auto-triggers
  
  // Feature 9: Auto-trigger if no path from start to done exists
  useEffect(() => {
    // Only check if we have questions (not just start/end)
    const questionNodes = nodes.filter(n => n.id !== 'start' && n.id !== 'end');
    if (questionNodes.length === 0) return;
    
    // Check if start can reach end
    const hasPath = canStartReachEnd(nodes, edges);
    
    if (!hasPath) {
      const now = Date.now();
      const timeSinceLastTrigger = now - feature9LastTriggerTimeRef.current;
      
      // Reset counter if enough time has passed
      if (timeSinceLastTrigger > AUTO_TRIGGER_COOLDOWN_MS) {
        feature9AutoTriggerCountRef.current = 0;
      }
      
      // Prevent infinite loops: only trigger if under max attempts
      if (feature9AutoTriggerCountRef.current < MAX_AUTO_TRIGGER_ATTEMPTS) {
        feature9AutoTriggerCountRef.current += 1;
        feature9LastTriggerTimeRef.current = now;
        
        // Trigger Feature 9 to fix the connection
        setTimeout(() => {
          setEdges((currentEdges) => {
            const reconnectedEdges = ensureStartToDoneConnection(nodes, currentEdges);
            
            // Check if we successfully created a path
            const pathCreated = canStartReachEnd(nodes, reconnectedEdges);
            
            if (pathCreated) {
              // Reset counter on success
              feature9AutoTriggerCountRef.current = 0;
              
              // Renumber questions
              setTimeout(() => {
                setNodes((currentNodes) => {
                  const { nodes: renumberedNodes, edges: renumberedEdges } = renumberQuestionsInFlow(currentNodes, reconnectedEdges);
                  setEdges(renumberedEdges);
                  return renumberedNodes;
                });
              }, 0);
            } else {
              // Path still can't be created - this is an exceptional case
              // Log warning but don't trigger again to prevent infinite loop
              console.warn('Feature 9: Unable to establish start-to-done connection. This may indicate a corrupted flow state.');
            }
            
            return reconnectedEdges;
          });
        }, 100); // Small delay to avoid race conditions
      } else {
        // Max attempts reached - log error and stop trying
        console.error('Feature 9: Maximum auto-trigger attempts reached. Start-to-done connection cannot be established. This may indicate a corrupted flow state.');
      }
  } else {
      // Path exists - reset counter
      feature9AutoTriggerCountRef.current = 0;
    }
  }, [nodes, edges, setNodes, setEdges]);
  
  const isConnectingRef = useRef(false); // Track if user is connecting/dragging an edge
  const onEdgeUpdateRef = useRef<((oldEdge: Edge, newConnection: Connection) => void) | null>(null);
  const onEdgeUpdateEndRef = useRef<((event: MouseEvent | TouchEvent, edge: Edge) => void) | null>(null);
  const disconnectedEdgeLabelsRef = useRef<Map<string, string>>(new Map()); // Store labels of disconnected edges
  
  // Track dragging state for cursor changes
  const [isDraggingNode, setIsDraggingNode] = useState(false);
  const [isPanning, setIsPanning] = useState(false); // Track if user is panning with middle mouse button
  const draggedNodeRef = useRef<string | null>(null); // Track which node is being dragged
  const edgeUnderDragRef = useRef<string | null>(null); // Track which edge the node is over during drag
  
  // Handle mouse move and mouse up globally to track edge dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      // Track edge dragging - set connecting state when dragging
      if (edgeDragStateRef.edgeId) {
        isConnectingRef.current = true;
        isEdgeDraggingRef.current = true;
        
        // Prevent infinite loop: Check if this is a synthetic event we dispatched
        // Synthetic events will have isTrusted = false, but we need a better check
        // Check if the event target is the pane/viewport (meaning it's already been forwarded)
        // Fix: Check if target is an Element before calling closest (SVG elements don't have closest)
        const target = e.target;
        const isElement = target instanceof Element;
        const isSyntheticEvent = isElement && (target.closest('.react-flow__pane') || target.closest('.react-flow__viewport'));
        
        // Only forward if this is a real user mouse event, not one we dispatched
        if (!isSyntheticEvent && e.isTrusted !== false) {
          // If we're dragging from a line (not a handle), we need to forward
          // mousemove events to ReactFlow's pane to maintain visual feedback
          // Find the edge and handle
          const edge = edges.find(ed => ed.id === edgeDragStateRef.edgeId);
          if (edge && edgeDragStateRef.updateMode) {
            // Forward the mousemove event to ReactFlow's pane so it tracks the connection line
            // ReactFlow needs these events to update the connection line position
            const paneElement = document.querySelector('.react-flow__pane') as HTMLElement;
            const viewportElement = document.querySelector('.react-flow__viewport') as HTMLElement;
            
            // Mark this event as synthetic to prevent re-dispatching
            const moveEvent = new MouseEvent('mousemove', {
              bubbles: false, // Don't bubble to prevent loop
              cancelable: true,
              button: 0,
              buttons: 1, // Indicate button is still pressed
              clientX: e.clientX,
              clientY: e.clientY,
              view: window,
              movementX: e.movementX || 0,
              movementY: e.movementY || 0,
            });
            
            // Dispatch directly to targets without bubbling
            // ReactFlow listens on the pane for mousemove during edge updates
            if (paneElement) {
              paneElement.dispatchEvent(moveEvent);
            }
            if (viewportElement) {
              viewportElement.dispatchEvent(moveEvent);
            }
          }
        }
      }
    };

    const handleGlobalMouseUp = (e: MouseEvent) => {
      // Reset panning state when middle mouse button is released
      if (e.button === 1) {
        setIsPanning(false);
      }
      
      // Helper function to clear drag state and restore handle visibility
      const clearDragState = () => {
        // Restore handle visibility if it was temporarily made visible
        if (edgeDragStateRef.handleElement) {
          console.log('👁️ Restoring handle visibility');
          if (edgeDragStateRef.originalHandleVisibility) {
            edgeDragStateRef.handleElement.style.visibility = edgeDragStateRef.originalHandleVisibility;
          } else {
            edgeDragStateRef.handleElement.style.visibility = '';
          }
          if (edgeDragStateRef.originalHandleOpacity) {
            edgeDragStateRef.handleElement.style.opacity = edgeDragStateRef.originalHandleOpacity;
          } else {
            edgeDragStateRef.handleElement.style.opacity = '';
          }
          edgeDragStateRef.handleElement = null;
          edgeDragStateRef.originalHandleVisibility = '';
          edgeDragStateRef.originalHandleOpacity = '';
        }
        
        // Clear drag state
        edgeDragStateRef.edgeId = null;
        edgeDragStateRef.updateMode = null;
        isConnectingRef.current = false;
        isEdgeDraggingRef.current = false;
        console.log('🧹 Drag state cleared');
      };
      
      // Handle edge drag from anywhere on the edge - follow the usual dragging procedure
      if (edgeDragStateRef.edgeId) {
        console.log('🔴 MOUSE UP - Edge drag ended', {
          edgeId: edgeDragStateRef.edgeId,
          updateMode: edgeDragStateRef.updateMode,
          target: e.target
        });
        
        // Try to find edge in current edges state first, then fallback to globalEdgesRef
        let edge = edges.find(e => e.id === edgeDragStateRef.edgeId);
        if (!edge && globalEdgesRef.current) {
          edge = globalEdgesRef.current.find(e => e.id === edgeDragStateRef.edgeId);
          if (edge) {
            console.log('⚠️ Edge found in globalEdgesRef but not in current edges state - using global ref');
          }
        }
        if (!edge) {
          console.log('❌ Edge not found in edges array or globalEdgesRef', {
            edgeId: edgeDragStateRef.edgeId,
            edgesCount: edges.length,
            globalEdgesCount: globalEdgesRef.current?.length || 0,
            edgeIds: edges.map(e => e.id).slice(0, 10)
          });
          clearDragState();
          return;
        }
        
        if (!edgeDragStateRef.updateMode) {
          console.log('❌ No update mode set');
          clearDragState();
          return;
        }
        
        console.log('✅ Edge found:', edge);
        
        // Find the handle/node under the mouse
        // Fix: Check if target is an Element before calling closest (SVG elements don't have closest)
        const target = e.target;
        const isElement = target instanceof Element;
        let handleElement: HTMLElement | null = null;
        let nodeElement: HTMLElement | null = null;
        
        if (isElement) {
          handleElement = target.closest('.react-flow__handle') as HTMLElement;
          nodeElement = target.closest('.react-flow__node') as HTMLElement;
        }
        
        console.log('🔍 Handle/Node detection:', {
          handleElement: handleElement ? 'FOUND' : 'NOT FOUND',
          nodeElement: nodeElement ? 'FOUND' : 'NOT FOUND',
          targetTag: isElement ? target.tagName : 'N/A',
          targetClass: isElement ? target.className : 'N/A'
        });
        
        // If we didn't find a node at all, or found a node but not a handle, search for the closest handle
        // This is especially important for Question-to-Question edge dragging where the mouse might
        // not be directly over a node, or might be over the wrong node (e.g., Done node)
        if ((!nodeElement || (nodeElement && !handleElement)) && reactFlowInstanceRef.current) {
          // If no node was found, we'll search all nodes
          // If a node was found but no handle, we'll first check that node, then fall back to all nodes
          const shouldSearchAllNodes = !nodeElement;
          
          // Get mouse position in flow coordinates (needed for both cases)
          const paneElement = reactFlowContainerRef.current?.querySelector('.react-flow__pane') as HTMLElement;
          if (paneElement) {
            const paneRect = paneElement.getBoundingClientRect();
            const relativeX = e.clientX - paneRect.left;
            const relativeY = e.clientY - paneRect.top;
            
            const reactFlowInstance = reactFlowInstanceRef.current;
            const flowPos = reactFlowInstance.screenToFlowPosition({
              x: relativeX,
              y: relativeY,
            });
            
            // Determine which type of handle we need based on updateMode
            const updateSource = edgeDragStateRef.updateMode === 'source';
            const handleType = updateSource ? 'source' : 'target';
            
            let closestHandle: HTMLElement | null = null;
            let minDistance = Infinity;
            let foundNodeElement: HTMLElement | null = null;
            
            // Helper function to search handles on a node
            const searchHandlesOnNode = (nodeEl: Element, skipNodeId?: string) => {
              const candidateNodeId = nodeEl.getAttribute('data-id');
              if (!candidateNodeId || candidateNodeId === skipNodeId) {
                return;
              }
              
              const candidateNode = nodes.find(n => n.id === candidateNodeId);
              if (!candidateNode) {
                return;
              }
              
              // Find all handles on this node in the DOM (even if hidden)
              const allHandles = nodeEl.querySelectorAll('.react-flow__handle');
              
              allHandles.forEach((handle) => {
                const handleElement = handle as HTMLElement;
                const handleId = handleElement.getAttribute('data-handleid');
                const handlePos = handleElement.getAttribute('data-handlepos');
                
                // Check if handle is in layout
                const computedStyle = window.getComputedStyle(handleElement);
                if (computedStyle.display === 'none') {
                  return;
                }
                
                // Check handle type (same logic as before)
                const isSourceByClass = handleElement.classList.contains('react-flow__handle-source');
                const isTargetByClass = handleElement.classList.contains('react-flow__handle-target');
                const handleTypeAttr = handleElement.getAttribute('data-handletype');
                const hasOutputInId = handleId?.includes('output');
                const hasInputInId = handleId?.includes('input');
                
                let isSourceByPos = false;
                let isTargetByPos = false;
                if (!handleId && !isSourceByClass && !isTargetByClass) {
                  if (handlePos === 'bottom') {
                    isSourceByPos = true;
                  } else if (handlePos === 'top') {
                    isTargetByPos = true;
                  }
                }
                
                const isSourceHandle = isSourceByClass || 
                                      handleTypeAttr === 'source' || 
                                      hasOutputInId ||
                                      isSourceByPos;
                const isTargetHandle = isTargetByClass || 
                                      handleTypeAttr === 'target' || 
                                      hasInputInId ||
                                      isTargetByPos;
                
                if ((updateSource && isSourceHandle) || (!updateSource && isTargetHandle)) {
                  // Get handle position
                  const handleRect = handleElement.getBoundingClientRect();
                  const handleCenterX = handleRect.left + handleRect.width / 2;
                  const handleCenterY = handleRect.top + handleRect.height / 2;
                  
                  // Convert to flow coordinates
                  const handleFlowPos = reactFlowInstance.screenToFlowPosition({
                    x: handleCenterX - paneRect.left,
                    y: handleCenterY - paneRect.top,
                  });
                  
                  // Calculate distance
                  const distance = Math.sqrt(
                    Math.pow(flowPos.x - handleFlowPos.x, 2) + 
                    Math.pow(flowPos.y - handleFlowPos.y, 2)
                  );
                  
                  // Type match bonus
                  let typeMatchBonus = 0;
                  if (updateSource && isSourceHandle) {
                    if (isSourceByClass) {
                      typeMatchBonus = -50;
                    } else if (hasOutputInId) {
                      typeMatchBonus = -30;
                    } else if (handleTypeAttr === 'source') {
                      typeMatchBonus = -20;
                    }
                  } else if (!updateSource && isTargetHandle) {
                    if (isTargetByClass) {
                      typeMatchBonus = -50;
                    } else if (hasInputInId) {
                      typeMatchBonus = -30;
                    } else if (handleTypeAttr === 'target') {
                      typeMatchBonus = -20;
                    }
                  }
                  
                  const adjustedDistance = distance + typeMatchBonus;
                  
                  if (adjustedDistance < minDistance) {
                    minDistance = adjustedDistance;
                    closestHandle = handleElement;
                    foundNodeElement = nodeEl as HTMLElement;
                  }
                }
              });
            };
            
            // First, if we found a node, search its handles
            if (nodeElement && !shouldSearchAllNodes) {
              const nodeId = nodeElement.getAttribute('data-id');
              if (nodeId) {
                console.log('🔍 Node found but handle not found, searching for closest handle on node', nodeId);
                searchHandlesOnNode(nodeElement);
                
                if (closestHandle) {
                  handleElement = closestHandle;
                  const handleEl = closestHandle as HTMLElement;
                  const handleIdAttr = handleEl.getAttribute('data-handleid');
                  const handlePosAttr = handleEl.getAttribute('data-handlepos');
                  const handleTypeAttr = handleEl.getAttribute('data-handletype');
                  const hasOutputInId = handleIdAttr?.includes('output');
                  const hasInputInId = handleIdAttr?.includes('input');
                  const isSourceByClass = handleEl.classList.contains('react-flow__handle-source');
                  const isTargetByClass = handleEl.classList.contains('react-flow__handle-target');
                  console.log('✅ Found closest handle:', {
                    handleId: handleIdAttr,
                    handlePos: handlePosAttr,
                    handleType: handleTypeAttr,
                    isSourceByClass,
                    isTargetByClass,
                    hasOutputInId,
                    hasInputInId,
                    distance: minDistance.toFixed(2),
                    updateMode: edgeDragStateRef.updateMode,
                    typeMatchStrength: updateSource 
                      ? (isSourceByClass ? 'STRONG (CSS)' : hasOutputInId ? 'STRONG (ID)' : handleTypeAttr === 'source' ? 'MEDIUM (ATTR)' : 'WEAK (POS)')
                      : (isTargetByClass ? 'STRONG (CSS)' : hasInputInId ? 'STRONG (ID)' : handleTypeAttr === 'target' ? 'MEDIUM (ATTR)' : 'WEAK (POS)')
                  });
                } else {
                  console.log('⚠️ No matching handle found for', handleType, 'on node', nodeId);
                }
              }
            }
            
            // If no handle found on the initial node, or no node was found, search ALL nodes
            if (!closestHandle) {
              if (shouldSearchAllNodes) {
                console.log('🔍 No node found under mouse, searching all nodes for closest matching handle...');
              } else {
                console.log('🔍 No matching handle on found node, searching all nodes...');
              }
              
              const allNodeElements = document.querySelectorAll('.react-flow__node');
              const skipNodeId = nodeElement?.getAttribute('data-id') ?? undefined;
              
              allNodeElements.forEach((nodeEl) => {
                searchHandlesOnNode(nodeEl, skipNodeId);
              });
              
              if (closestHandle && foundNodeElement) {
                const handleEl = closestHandle as HTMLElement;
                const nodeEl = foundNodeElement as HTMLElement;
                console.log('✅ Found matching handle on different node:', {
                  nodeId: nodeEl.getAttribute('data-id'),
                  handleId: handleEl.getAttribute('data-handleid'),
                  distance: minDistance.toFixed(2)
                });
                handleElement = closestHandle;
                nodeElement = foundNodeElement;
              }
            } else if (foundNodeElement) {
              // Update nodeElement if we found a handle on a different node
              nodeElement = foundNodeElement;
            }
          }
        }
        
        // Original code continues here - check if we have handle and node
        if (handleElement && nodeElement) {
          const nodeId = nodeElement.getAttribute('data-id');
          if (nodeId) {
            // Get the node from ReactFlow
            const node = nodes.find(n => n.id === nodeId);
            if (node) {
              // Get mouse position in flow coordinates
              const paneElement = reactFlowContainerRef.current?.querySelector('.react-flow__pane') as HTMLElement;
              if (paneElement) {
                const paneRect = paneElement.getBoundingClientRect();
                const relativeX = e.clientX - paneRect.left;
                const relativeY = e.clientY - paneRect.top;
                
                const reactFlowInstance = reactFlowInstanceRef.current;
                const flowPos = reactFlowInstance.screenToFlowPosition({
                  x: relativeX,
                  y: relativeY,
                });
                
                // Determine which type of handle we need based on updateMode
                const updateSource = edgeDragStateRef.updateMode === 'source';
                const handleType = updateSource ? 'source' : 'target';
                
                // Find all handles on this node in the DOM (even if hidden)
                const allHandles = nodeElement.querySelectorAll('.react-flow__handle');
                console.log(`🔍 Found ${allHandles.length} handles on node`);
                
                let closestHandle: HTMLElement | null = null;
                let minDistance = Infinity;
                
                allHandles.forEach((handle) => {
                  const handleElement = handle as HTMLElement;
                  const handleId = handleElement.getAttribute('data-handleid');
                  const handlePos = handleElement.getAttribute('data-handlepos'); // React Flow sets: "top", "bottom", "left", "right"
                  
                  // Skip handles that are completely hidden (left/right handles are hidden when node not selected)
                  // But still check them if they're in the DOM - they might be visible during drag
                  const computedStyle = window.getComputedStyle(handleElement);
                  const isVisible = computedStyle.visibility !== 'hidden' && computedStyle.opacity !== '0';
                  
                  // For left/right handles, we still want to consider them even if hidden
                  // because they might become visible during the drag operation
                  // Only skip if they're completely removed from layout
                  const isInLayout = computedStyle.display !== 'none';
                  
                  if (!isInLayout) {
                    return; // Skip handles that are not in the layout
                  }
                  
                  // Check handle type using multiple methods (same as DraggableEdge.tsx):
                  // Priority order: CSS classes > handle type attr > handle ID > position (least reliable)
                  
                  // 1. Check CSS classes (most reliable - React Flow sets these)
                  const isSourceByClass = handleElement.classList.contains('react-flow__handle-source');
                  const isTargetByClass = handleElement.classList.contains('react-flow__handle-target');
                  const handleTypeAttr = handleElement.getAttribute('data-handletype');
                  
                  // 2. Check handle ID (more reliable than position)
                  // For IDs, check for explicit 'output' or 'input' keywords first
                  const hasOutputInId = handleId?.includes('output');
                  const hasInputInId = handleId?.includes('input');
                  
                  // Position-based detection (least reliable - only use if no other indicators)
                  // Note: Position alone is ambiguous (left/right can be either type)
                  let isSourceByPos = false;
                  let isTargetByPos = false;
                  // Only use position if we don't have ID or class info
                  if (!handleId && !isSourceByClass && !isTargetByClass) {
                    // For default nodes without IDs, use position as fallback
                    if (handlePos === 'bottom') {
                      isSourceByPos = true; // Bottom is typically output
                    } else if (handlePos === 'top') {
                      isTargetByPos = true; // Top is typically input
                    }
                    // Don't use left/right as they're ambiguous
                  }
                  
                  // Determine handle type with priority:
                  // 1. CSS class (most reliable)
                  // 2. Handle type attribute
                  // 3. Handle ID keywords (output/input)
                  // 4. Position (only as last resort for default nodes)
                  const isSourceHandle = isSourceByClass || 
                                        handleTypeAttr === 'source' || 
                                        hasOutputInId ||
                                        isSourceByPos;
                  const isTargetHandle = isTargetByClass || 
                                        handleTypeAttr === 'target' || 
                                        hasInputInId ||
                                        isTargetByPos;
                  
                  if ((updateSource && isSourceHandle) || (!updateSource && isTargetHandle)) {
                    // Get handle position
                    const handleRect = handleElement.getBoundingClientRect();
                    const handleCenterX = handleRect.left + handleRect.width / 2;
                    const handleCenterY = handleRect.top + handleRect.height / 2;
                    
                    // Convert to flow coordinates
                    const handleFlowPos = reactFlowInstance.screenToFlowPosition({
                      x: handleCenterX - paneRect.left,
                      y: handleCenterY - paneRect.top,
                    });
                    
                    // Calculate distance
                    const distance = Math.sqrt(
                      Math.pow(flowPos.x - handleFlowPos.x, 2) + 
                      Math.pow(flowPos.y - handleFlowPos.y, 2)
                    );
                    
                    // For left/right handles at the same position, prioritize the correct type
                    // Add a significant bonus for handles that match the required type more strongly
                    // This ensures that when left-input and left-output are at the same position,
                    // we always pick the correct one based on type
                    let typeMatchBonus = 0;
                    if (updateSource && isSourceHandle) {
                      // Prefer handles with explicit 'output' in ID or source class
                      if (isSourceByClass) {
                        typeMatchBonus = -50; // Strong preference for CSS class match
                      } else if (hasOutputInId) {
                        typeMatchBonus = -30; // Strong preference for ID match
                      } else if (handleTypeAttr === 'source') {
                        typeMatchBonus = -20; // Preference for type attribute
                      }
                    } else if (!updateSource && isTargetHandle) {
                      // Prefer handles with explicit 'input' in ID or target class
                      if (isTargetByClass) {
                        typeMatchBonus = -50; // Strong preference for CSS class match
                      } else if (hasInputInId) {
                        typeMatchBonus = -30; // Strong preference for ID match
                      } else if (handleTypeAttr === 'target') {
                        typeMatchBonus = -20; // Preference for type attribute
                      }
                    }
                    
                    const adjustedDistance = distance + typeMatchBonus;
                    
                    if (adjustedDistance < minDistance) {
                      minDistance = adjustedDistance;
                      closestHandle = handleElement;
                    }
                  }
                });
                
                if (closestHandle) {
                  handleElement = closestHandle;
                  const handleEl = closestHandle as HTMLElement;
                  const handleIdAttr = handleEl.getAttribute('data-handleid');
                  const handlePosAttr = handleEl.getAttribute('data-handlepos');
                  const handleTypeAttr = handleEl.getAttribute('data-handletype');
                  const isSourceByClass = handleEl.classList.contains('react-flow__handle-source');
                  const isTargetByClass = handleEl.classList.contains('react-flow__handle-target');
                  const hasOutputInId = handleIdAttr?.includes('output');
                  const hasInputInId = handleIdAttr?.includes('input');
                  console.log('✅ Found closest handle:', {
                    handleId: handleIdAttr,
                    handlePos: handlePosAttr,
                    handleType: handleTypeAttr,
                    isSourceByClass,
                    isTargetByClass,
                    hasOutputInId,
                    hasInputInId,
                    distance: minDistance.toFixed(2),
                    updateMode: edgeDragStateRef.updateMode,
                    typeMatchStrength: updateSource 
                      ? (isSourceByClass ? 'STRONG (CSS)' : hasOutputInId ? 'STRONG (ID)' : handleTypeAttr === 'source' ? 'MEDIUM (ATTR)' : 'WEAK (POS)')
                      : (isTargetByClass ? 'STRONG (CSS)' : hasInputInId ? 'STRONG (ID)' : handleTypeAttr === 'target' ? 'MEDIUM (ATTR)' : 'WEAK (POS)')
                  });
                } else {
                  console.log('⚠️ No matching handle found for', handleType, 'on node', nodeId);
                  
                  // Fallback: If the found node doesn't have matching handles, search ALL nodes
                  // This fixes the issue where dragging from Question-to-Question edges might
                  // find the Done node (which only has 1 handle) instead of the Question node
                  console.log('🔍 Searching all nodes for closest matching handle...');
                  
                  let bestHandle: HTMLElement | null = null;
                  let bestNodeElement: HTMLElement | null = null;
                  let bestDistance = Infinity;
                  
                  // Search all nodes in the DOM
                  const allNodeElements = document.querySelectorAll('.react-flow__node');
                  allNodeElements.forEach((nodeEl) => {
                    const candidateNodeId = nodeEl.getAttribute('data-id');
                    if (!candidateNodeId || candidateNodeId === nodeId) {
                      return; // Skip the node we already checked
                    }
                    
                    const candidateNode = nodes.find(n => n.id === candidateNodeId);
                    if (!candidateNode) {
                      return;
                    }
                    
                    // Find all handles on this node
                    const candidateHandles = nodeEl.querySelectorAll('.react-flow__handle');
                    
                    candidateHandles.forEach((handle) => {
                      const handleEl = handle as HTMLElement;
                      const handleId = handleEl.getAttribute('data-handleid');
                      const handlePos = handleEl.getAttribute('data-handlepos');
                      
                      // Check if handle is in layout
                      const computedStyle = window.getComputedStyle(handleEl);
                      if (computedStyle.display === 'none') {
                        return;
                      }
                      
                      // Check handle type (same logic as above)
                      const isSourceByClass = handleEl.classList.contains('react-flow__handle-source');
                      const isTargetByClass = handleEl.classList.contains('react-flow__handle-target');
                      const handleTypeAttr = handleEl.getAttribute('data-handletype');
                      const hasOutputInId = handleId?.includes('output');
                      const hasInputInId = handleId?.includes('input');
                      
                      let isSourceByPos = false;
                      let isTargetByPos = false;
                      if (!handleId && !isSourceByClass && !isTargetByClass) {
                        if (handlePos === 'bottom') {
                          isSourceByPos = true;
                        } else if (handlePos === 'top') {
                          isTargetByPos = true;
                        }
                      }
                      
                      const isSourceHandle = isSourceByClass || 
                                            handleTypeAttr === 'source' || 
                                            hasOutputInId ||
                                            isSourceByPos;
                      const isTargetHandle = isTargetByClass || 
                                            handleTypeAttr === 'target' || 
                                            hasInputInId ||
                                            isTargetByPos;
                      
                      if ((updateSource && isSourceHandle) || (!updateSource && isTargetHandle)) {
                        // Calculate distance
                        const handleRect = handleEl.getBoundingClientRect();
                        const handleCenterX = handleRect.left + handleRect.width / 2;
                        const handleCenterY = handleRect.top + handleRect.height / 2;
                        
                        const handleFlowPos = reactFlowInstance.screenToFlowPosition({
                          x: handleCenterX - paneRect.left,
                          y: handleCenterY - paneRect.top,
                        });
                        
                        const distance = Math.sqrt(
                          Math.pow(flowPos.x - handleFlowPos.x, 2) + 
                          Math.pow(flowPos.y - handleFlowPos.y, 2)
                        );
                        
                        // Type match bonus (same as above)
                        let typeMatchBonus = 0;
                        if (updateSource && isSourceHandle) {
                          if (isSourceByClass) {
                            typeMatchBonus = -50;
                          } else if (hasOutputInId) {
                            typeMatchBonus = -30;
                          } else if (handleTypeAttr === 'source') {
                            typeMatchBonus = -20;
                          }
                        } else if (!updateSource && isTargetHandle) {
                          if (isTargetByClass) {
                            typeMatchBonus = -50;
                          } else if (hasInputInId) {
                            typeMatchBonus = -30;
                          } else if (handleTypeAttr === 'target') {
                            typeMatchBonus = -20;
                          }
                        }
                        
                        const adjustedDistance = distance + typeMatchBonus;
                        
                        if (adjustedDistance < bestDistance) {
                          bestDistance = adjustedDistance;
                          bestHandle = handleEl;
                          bestNodeElement = nodeEl as HTMLElement;
                        }
                      }
                    });
                  });
                  
                  if (bestHandle && bestNodeElement) {
                    const bestHandleEl = bestHandle as HTMLElement;
                    const bestNodeEl = bestNodeElement as HTMLElement;
                    console.log('✅ Found matching handle on different node:', {
                      nodeId: bestNodeEl.getAttribute('data-id'),
                      handleId: bestHandleEl.getAttribute('data-handleid'),
                      distance: bestDistance.toFixed(2)
                    });
                    handleElement = bestHandle;
                    nodeElement = bestNodeElement;
                  }
                }
              }
            }
          }
        }
        
        if (handleElement && nodeElement) {
          // Get the node ID and handle ID
          const nodeId = nodeElement.getAttribute('data-id');
          const handleId = handleElement.getAttribute('data-handleid');
          
          console.log('✅ Handle and node found:', { nodeId, handleId });
          
          if (nodeId) {
            // Determine which endpoint to update based on updateMode
            const updateSource = edgeDragStateRef.updateMode === 'source';
            
            // Fix: Prevent using Done/end node as source (it only has input handles)
            // If we're updating source and target is Done, or if we're updating target to Done,
            // we need to ensure we're not trying to use Done's input handle as a source
            if (nodeId === 'end' && updateSource) {
              console.log('❌ Cannot use Done node as source - it only accepts incoming connections');
              clearDragState();
              return;
            }
            
            // Fix: Validate handle type matches the connection direction
            // For source updates, we need an output handle; for target updates, we need an input handle
            // Use the same improved detection logic as the closest handle search
            const isSourceByClass = handleElement.classList.contains('react-flow__handle-source');
            const isTargetByClass = handleElement.classList.contains('react-flow__handle-target');
            const handleTypeAttr = handleElement.getAttribute('data-handletype');
            const handlePos = handleElement.getAttribute('data-handlepos');
            const hasOutputInId = handleId?.includes('output');
            const hasInputInId = handleId?.includes('input');
            
            // Position-based detection (fallback for handles with null IDs, like Done node)
            let isSourceByPos = false;
            let isTargetByPos = false;
            // Only use position if we don't have ID or class info
            if (!handleId && !isSourceByClass && !isTargetByClass) {
              // For default nodes without IDs, use position as fallback
              if (handlePos === 'bottom') {
                isSourceByPos = true; // Bottom is typically output
              } else if (handlePos === 'top') {
                isTargetByPos = true; // Top is typically input
              }
              // Don't use left/right as they're ambiguous
            }
            
            const isSourceHandle = isSourceByClass || 
                                  handleTypeAttr === 'source' || 
                                  hasOutputInId ||
                                  isSourceByPos;
            const isTargetHandle = isTargetByClass || 
                                  handleTypeAttr === 'target' || 
                                  hasInputInId ||
                                  isTargetByPos;
            
            // Enhanced validation: Check handle type with detailed logging
            if (updateSource && !isSourceHandle) {
              console.error('❌ Invalid handle type for source update', {
                handleId,
                handlePos,
                handleTypeAttr,
                isSourceByClass,
                isTargetByClass,
                hasOutputInId,
                hasInputInId,
                isSourceByPos,
                isTargetByPos,
                nodeId,
                updateMode: 'source'
              });
              clearDragState();
              return;
            }
            
            if (!updateSource && !isTargetHandle) {
              console.error('❌ Invalid handle type for target update', {
                handleId,
                handlePos,
                handleTypeAttr,
                isSourceByClass,
                isTargetByClass,
                hasOutputInId,
                hasInputInId,
                isSourceByPos,
                isTargetByPos,
                nodeId,
                updateMode: 'target'
              });
              clearDragState();
              return;
            }
            
            // Additional validation: For sub-question nodes, ensure we're using the correct handle type
            // Sub-question nodes should have the same handle configuration as regular question nodes
            if (nodeId.includes('.')) {
              // This is a sub-question node
              if (updateSource && hasInputInId) {
                console.error('❌ Sub-question node: Attempting to use input handle as source', {
                  handleId,
                  nodeId,
                  suggestion: 'Should use output handle (bottom-output, right-output, or left-output)'
                });
                clearDragState();
                return;
              }
            }
            
            // Create the new connection
            const newConnection: Connection = updateSource
              ? {
                  source: nodeId,
                  sourceHandle: handleId || null,
                  target: edge.target,
                  targetHandle: edge.targetHandle || null,
                }
              : {
                  source: edge.source,
                  sourceHandle: edge.sourceHandle || null,
                  target: nodeId,
                  targetHandle: handleId || null,
                };
            
            console.log('🔗 Creating new connection via onEdgeUpdate:', newConnection);
            
            // Use onEdgeUpdate to ensure autocomplete logic runs (same path as dragging from handle)
            // This will handle all the logic including autocomplete to 'done' node
            if (onEdgeUpdateRef.current) {
              onEdgeUpdateRef.current(edge, newConnection);
              console.log('✅ Edge update called (includes autocomplete logic)');
              
              // Also call onEdgeUpdateEnd to ensure cleanup (same as ReactFlow would do)
              // Find the updated edge to pass to onEdgeUpdateEnd
              setTimeout(() => {
                const updatedEdge = edges.find(e => e.id === edge.id);
                if (onEdgeUpdateEndRef.current && updatedEdge) {
                  onEdgeUpdateEndRef.current(e, updatedEdge);
                  console.log('✅ Edge update end called (cleanup)');
                }
              }, 0);
            } else {
              console.log('⚠️ onEdgeUpdate not available yet');
            }
          } else {
            console.log('❌ No node ID found');
          }
        } else {
          console.log('⚠️ Mouse released but not over a handle. Drag cancelled.');
        }
        
        // Restore handle visibility if it was temporarily made visible
        if (edgeDragStateRef.handleElement) {
          console.log('👁️ Restoring handle visibility');
          if (edgeDragStateRef.originalHandleVisibility) {
            edgeDragStateRef.handleElement.style.visibility = edgeDragStateRef.originalHandleVisibility;
          } else {
            edgeDragStateRef.handleElement.style.visibility = '';
          }
          if (edgeDragStateRef.originalHandleOpacity) {
            edgeDragStateRef.handleElement.style.opacity = edgeDragStateRef.originalHandleOpacity;
          } else {
            edgeDragStateRef.handleElement.style.opacity = '';
          }
          edgeDragStateRef.handleElement = null;
          edgeDragStateRef.originalHandleVisibility = '';
          edgeDragStateRef.originalHandleOpacity = '';
        }
        
        // Clear drag state
        edgeDragStateRef.edgeId = null;
        edgeDragStateRef.updateMode = null;
        isConnectingRef.current = false;
        isEdgeDraggingRef.current = false;
        console.log('🧹 Drag state cleared');
      }
      
    };
    
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [nodes, setNodes, edges, setEdges]);
  
  // Add CSS for cursor styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .react-flow__pane {
        cursor: pointer !important;
      }
      .react-flow__node {
        cursor: pointer !important;
      }
      .react-flow__node:hover {
        cursor: pointer !important;
      }
      .react-flow__node.dragging,
      .react-flow__node.dragging:hover {
        cursor: grabbing !important;
      }
      .react-flow__node textarea {
        cursor: text !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Handle middle mouse button up globally to reset panning state
  useEffect(() => {
    const handleGlobalMouseUp = (e: MouseEvent) => {
      if (e.button === 1) {
        setIsPanning(false);
      }
    };
    
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, []);
  const brandOptions = useMemo(() => {
    const values = new Set<string>();
    surveys.forEach((survey) => {
      if (survey.brand_id !== null && survey.brand_id !== undefined) {
        const value = String(survey.brand_id).trim();
        if (value) {
          values.add(value);
        }
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [surveys]);

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let response = await api.get('/surveys');
      const normalized = response.data.map((survey: Survey) => {
        const rawBrand =
          (survey as any).brand_id !== undefined
            ? (survey as any).brand_id
            : (survey as any).brandId;
        const normalizedBrand =
          rawBrand === null || rawBrand === undefined || rawBrand === ''
            ? null
            : rawBrand;
        return {
          ...survey,
          is_active:
            typeof survey.is_active === 'number'
              ? Boolean(survey.is_active)
              : survey.is_active ?? true,
          brand_id: normalizedBrand,
          data_reward:
            survey.data_reward ?? (survey as any).dataReward ?? '',
          link: buildSurveyLink(survey.id),
        };
      });
      const sorted = sortSurveysById(normalized);
      setSurveys(sorted);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch surveys');
      toast.error('Failed to fetch surveys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const handleOpenDialog = (survey?: Survey) => {
    if (survey) {
      navigate(`/surveys/${survey.id}/edit`);
    } else {
      navigate('/surveys/new');
    }
  };

  const handleCloseDialog = () => {
    navigate('/surveys');
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setSurveyStartDate(newStartDate);
    
    // Clear validation error when user starts typing
    if (validationErrors.startDate) {
      setValidationErrors(prev => ({ ...prev, startDate: undefined }));
    }
    
    // Validate end date if it exists
    if (surveyEndDate && newStartDate) {
      const start = new Date(newStartDate);
      const end = new Date(surveyEndDate);
      if (end < start) {
        setValidationErrors(prev => ({ ...prev, endDate: 'End date must be after start date' }));
      } else if (validationErrors.endDate) {
        setValidationErrors(prev => ({ ...prev, endDate: undefined }));
      }
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    setSurveyEndDate(newEndDate);
    
    // Clear validation error when user starts typing
    if (validationErrors.endDate) {
      setValidationErrors(prev => ({ ...prev, endDate: undefined }));
    }
    
    // Validate against start date
    if (surveyStartDate && newEndDate) {
      const start = new Date(surveyStartDate);
      const end = new Date(newEndDate);
      if (end < start) {
        setValidationErrors(prev => ({ ...prev, endDate: 'End date must be after start date' }));
      }
    }
  };

  // Function to renumber questions sequentially based on flow position
  const renumberQuestionsSequentially = useCallback((nodesToRenumber: Node[], edgesToUse: Edge[]): Node[] => {
    // Find the main path from start to end
    const nodeMap = new Map(nodesToRenumber.map((n) => [n.id, n]));
    const adjacencyList = new Map<string, string[]>();
    edgesToUse.forEach((edge) => {
      if (!adjacencyList.has(edge.source)) {
        adjacencyList.set(edge.source, []);
      }
      adjacencyList.get(edge.source)!.push(edge.target);
    });

    // Find main path (prefer non-conditional edges)
    const findMainPath = (): string[] => {
      const path: string[] = ['start'];
      const visited = new Set<string>(['start']);
      let current = 'start';
      
      while (current !== 'end') {
        const targetIds = adjacencyList.get(current) || [];
        if (targetIds.length === 0) break;
        
        // Prefer non-conditional edges for main path
        const nonConditionalTargets = targetIds.filter((targetId) => {
          const edge = edgesToUse.find((e) => e.source === current && e.target === targetId);
          return edge && !edge.data?.conditionalOption;
        });
        
        const nextTarget = nonConditionalTargets.length > 0 ? nonConditionalTargets[0] : targetIds[0];
        
        if (!nextTarget || visited.has(nextTarget)) {
          break;
        }
        
        current = nextTarget;
        path.push(current);
        visited.add(current);
      }
      
      return path;
    };

    const mainPath = findMainPath();
    let questionNumber = 1;
    const nodeIdToQuestionNumber = new Map<string, number>();

    // Assign sequential numbers to questions in the main path
    for (let i = 1; i < mainPath.length - 1; i++) { // Skip 'start' and 'end'
      const nodeId = mainPath[i];
      const node = nodeMap.get(nodeId);
      if (!node) continue;

      // Skip 'done' type questions (they should only be at the end)
      const questionType = determineQuestionType(node);
      if (questionType === 'done') {
        continue;
      }

      // Assign sequential number
      nodeIdToQuestionNumber.set(nodeId, questionNumber);
      questionNumber++;
    }

    // Update node labels with correct sequential numbers
    return nodesToRenumber.map((node) => {
      const correctNumber = nodeIdToQuestionNumber.get(node.id);
      if (correctNumber && node.id !== 'start' && node.id !== 'end') {
        const currentLabel = node.data?.label || '';
        const questionText = parseQuestionText(currentLabel);
        if (questionText) {
          // Rebuild label with correct Q number
          const lines = currentLabel.split('\n');
          const newLines = [`Q${correctNumber}: ${questionText}`];
          // Preserve other lines (Options, Choices, Scale, etc.)
          lines.slice(1).forEach((line: string) => {
            if (line.trim() && 
                !line.trim().toLowerCase().startsWith('q') &&
                (line.trim().toLowerCase().startsWith('options:') ||
                 line.trim().toLowerCase().startsWith('choices:') ||
                 line.trim().toLowerCase().startsWith('scale:') ||
                 line.trim().toLowerCase().startsWith('type:') ||
                 line.trim().toLowerCase().includes('[optional]') ||
                 line.trim().toLowerCase().includes('[required]'))) {
              newLines.push(line);
            }
          });
          return {
            ...node,
            data: {
              ...node.data,
              label: newLines.join('\n'),
            },
          };
        }
      }
      return node;
    });
  }, []);

  const handleSave = async () => {
    if (!surveyName.trim()) {
      toast.error('Survey name is required');
      return;
    }

    setSaving(true);
    try {
      // First, renumber questions sequentially based on flow position
      const renumberedNodes = renumberQuestionsSequentially(nodes, edges);
      
      // Update the nodes state with renumbered nodes
      setNodes(renumberedNodes);
      
      // Get connected nodes and edges after renumbering
      const { connectedNodes, connectedEdges } = findConnectedPathFromStartToEnd(renumberedNodes, edges);
      
      if (connectedNodes.length === 0) {
        toast.error('No connected questions found. Please ensure questions are connected from Start to Done.');
        setSaving(false);
        return;
      }
      
      const nextId =
        surveys.length > 0 ? Math.max(...surveys.map((s) => s.id)) + 1 : 1;
      
      // Convert React Flow format to survey JSON format
      // Questions are already renumbered sequentially, so convertFlowToSurveyFormat will use those numbers
      const surveyArray = convertFlowToSurveyFormat(connectedNodes, connectedEdges);
      
      // Build survey data - only include fields that the DTOs accept
      const surveyData: any = {
        name: surveyName.trim(),
        description: surveyDescription.trim(), // Required by DTO
      };
      
      console.log('[CREATE SURVEY] Building survey data:', {
        name: surveyData.name,
        description: surveyData.description,
        hasDescription: !!surveyData.description,
      });
      
      // Include data_reward if provided (allow 0 as a valid value)
      if (surveyDataReward !== null && surveyDataReward !== undefined) {
        const dataRewardStr = String(surveyDataReward).trim();
        // Include if not empty (including '0')
        if (dataRewardStr !== '') {
          surveyData.data_reward = dataRewardStr;
        }
      }
      
      // Include brand_id if provided
      if (surveyBrandId !== null && surveyBrandId !== undefined && surveyBrandId !== '') {
        const brandIdStr = String(surveyBrandId).trim();
        surveyData.brand_id = brandIdStr || null;
      }
      
      // Include typeform_id if provided
      if (surveyTypeformId !== null && surveyTypeformId !== undefined && surveyTypeformId !== '') {
        surveyData.typeform_id = surveyTypeformId.trim() || null;
      }
      
      // Include icon_url if provided (always include if it has a value, including default)
      if (surveyIconUrl !== null && surveyIconUrl !== undefined && surveyIconUrl.trim() !== '') {
        surveyData.icon_url = surveyIconUrl.trim();
      }
      
      // Include is_active (explicitly convert to boolean)
      surveyData.is_active = Boolean(surveyIsActive);
      
      // Include dates - DTO requires ISO 8601 format
      // Validate dates
      const errors: { startDate?: string; endDate?: string } = {};
      
      if (surveyStartDate && surveyStartDate.trim()) {
        try {
          const date = new Date(surveyStartDate);
          if (!isNaN(date.getTime())) {
            surveyData.start_date = date.toISOString();
            console.log('[CREATE SURVEY] Start date formatted:', surveyData.start_date);
          } else {
            errors.startDate = 'Invalid start date format';
          }
        } catch (e) {
          errors.startDate = 'Invalid start date format';
        }
      } else {
        errors.startDate = 'Start date is required';
      }
      
      if (surveyEndDate && surveyEndDate.trim()) {
        try {
          const date = new Date(surveyEndDate);
          if (!isNaN(date.getTime())) {
            surveyData.end_date = date.toISOString();
            console.log('[CREATE SURVEY] End date formatted:', surveyData.end_date);
            
            // Validate that end date is after start date
            if (surveyData.start_date && new Date(surveyData.end_date) < new Date(surveyData.start_date)) {
              errors.endDate = 'End date must be after start date';
            }
          } else {
            errors.endDate = 'Invalid end date format';
          }
        } catch (e) {
          errors.endDate = 'Invalid end date format';
        }
      } else {
        errors.endDate = 'End date is required';
      }
      
      // If there are validation errors, show them and stop
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        const errorMessages = Object.values(errors).filter(Boolean);
        toast.error(errorMessages.join(', '));
        setSaving(false);
        return;
      }
      
      setValidationErrors({});
      
      // For new surveys, we'll update the link after creation with the actual ID
      // For existing surveys, use the provided link
      if (editingSurvey) {
        // Only include link if it's not empty
        if (surveyLink && surveyLink.trim()) {
          surveyData.link = surveyLink.trim();
        }
      } else {
        // For new surveys, link is already pre-populated with expected ID in handleOpenDialog
        // DTO requires link, so it must be included
        if (surveyLink && surveyLink.trim()) {
          surveyData.link = surveyLink.trim();
          console.log('[CREATE SURVEY] Link included:', surveyData.link);
        } else {
          console.error('[CREATE SURVEY] Link is required but missing');
          toast.error('Link is required');
          setSaving(false);
          return;
        }
      }
      
      console.log('[CREATE SURVEY] Final survey data before sending:', {
        name: surveyData.name,
        description: surveyData.description,
        link: surveyData.link,
        start_date: surveyData.start_date,
        end_date: surveyData.end_date,
        hasQuestions: !!surveyData.questions,
        keys: Object.keys(surveyData),
      });

      // Add React Flow format AND simplified survey format
      // Store both formats: React Flow for editing, simplified format for API/display
      // Use connectedNodes which have correct Q numbers in labels
      if (connectedNodes.length > 0) {
        surveyData.questions = {
          nodes: connectedNodes,
          edges: connectedEdges,
          survey: surveyArray, // Add the simplified survey format
        };
      }

      if (editingSurvey) {
        // Validate that questions can be stringified before sending
        if (surveyData.questions) {
          try {
            JSON.stringify(surveyData.questions);
          } catch (e) {
            console.error('❌ Failed to stringify questions:', e);
            toast.error('Invalid questions data - cannot save');
            setSaving(false);
            return;
          }
        }
        
        const response = await api.put(`/surveys/${editingSurvey.id}`, surveyData);
        
        // Get the final ID (might be different if ID was changed)
        const finalId = response.data?.id || editingSurvey.id;
        
        toast.success('Survey updated successfully');
        
        // Refetch the specific survey to get the updated data from the database
        // Use the final ID in case it was changed
        try {
          const updatedSurveyResponse = await api.get(`/surveys/${finalId}`);
          let updatedSurvey = updatedSurveyResponse.data;
          
          // Normalize the survey data to match the format used in fetchSurveys
          const rawBrand =
            (updatedSurvey as any).brand_id !== undefined
              ? (updatedSurvey as any).brand_id
              : (updatedSurvey as any).brandId;
          const normalizedBrand =
            rawBrand === null || rawBrand === undefined || rawBrand === ''
              ? null
              : rawBrand;
          
          updatedSurvey = {
            ...updatedSurvey,
            is_active:
              typeof updatedSurvey.is_active === 'number'
                ? Boolean(updatedSurvey.is_active)
                : updatedSurvey.is_active ?? true,
            brand_id: normalizedBrand,
            data_reward:
              updatedSurvey.data_reward ?? (updatedSurvey as any).dataReward ?? '',
            link: buildSurveyLink(updatedSurvey.id),
          };
          
          // Update the local surveys state with the updated survey
          // If ID changed, remove old entry and add new one; otherwise just update
          setSurveys((prev) => {
            if (finalId !== editingSurvey.id) {
              // ID was changed - remove old entry and add new one
              const filtered = prev.filter((s) => s.id !== editingSurvey.id);
              return sortSurveysById([...filtered, updatedSurvey]);
            } else {
              // ID unchanged - just update the existing entry
            const updated = prev.map((s) => 
              s.id === editingSurvey.id ? updatedSurvey : s
            );
            return sortSurveysById(updated);
            }
          });
          
        } catch (refetchErr: any) {
          // Silently fall back to fetchSurveys if refetch fails
          // Fallback to fetching all surveys if refetch fails
          await fetchSurveys();
        }
      } else {
        // For new surveys, create with pre-populated link and explicit ID
        // Include the calculated nextId to ensure we get the expected ID
        surveyData.id = nextId;
        console.log('[CREATE SURVEY] Creating survey with ID:', nextId, 'and link:', surveyData.link);
        const createResponse = await api.post('/surveys', surveyData);
        const newSurveyId = createResponse.data.id;
        console.log('[CREATE SURVEY] Survey created with ID:', newSurveyId);
        console.log('[CREATE SURVEY] Link in payload was:', surveyData.link);
        
        // Link is already set correctly in surveyData, but verify it matches the actual ID
        const actualLink = buildSurveyLink(newSurveyId);
        if (surveyData.link !== actualLink) {
          console.log('[CREATE SURVEY] Link mismatch, updating:', { expected: surveyData.link, actual: actualLink });
          await api.put(`/surveys/${newSurveyId}`, {
            link: actualLink,
          });
        } else {
          console.log('[CREATE SURVEY] Link matches, no update needed');
        }
        
        toast.success('Survey created successfully');
        // For new surveys, fetch all to get the new ID
        await fetchSurveys();
      }

      handleCloseDialog();
    } catch (err: any) {
      console.error('❌ Error in handleSave:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        stack: err.stack,
      });
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          (Array.isArray(err.response?.data?.message) ? err.response?.data?.message.join(', ') : '') ||
                          err.message || 
                          'Failed to save survey';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (survey: Survey, newValue: boolean) => {
    setStatusUpdatingId(survey.id);
    try {
      await api.put(`/surveys/${survey.id}`, { is_active: newValue });
      setSurveys((prev) =>
        sortSurveysById(
          prev.map((item) =>
            item.id === survey.id ? { ...item, is_active: newValue } : item
          )
        )
      );
      toast.success(`${survey.name} ${newValue ? 'activated' : 'deactivated'}`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update survey status');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const removeConditionalBranch = useCallback(
    (sourceId: string, option: string) => {
      console.log('🔴 [PATH: removeConditionalBranch] Entry:', { 
        sourceId, 
        option,
        timestamp: new Date().toISOString(),
        totalEdges: edges.length,
        edgesFromSource: edges.filter(e => e.source === sourceId).map(e => ({
          id: e.id,
          target: e.target,
          isConditional: isConditionalEdge(e),
          option: e.data?.conditionalOption,
          label: e.label
        }))
      });
      
      if (!sourceId || !option) {
        console.warn('⚠️ [PATH: removeConditionalBranch] Missing sourceId or option:', { sourceId, option });
        return;
      }

      // Find the conditional edge with this option to get the sub-question ID and branchId
      console.log('🔴 [PATH: removeConditionalBranch] Searching for conditional edge:', {
        sourceId,
        option,
        searchingBy: 'source + option match',
        normalizedOption: option.trim().toLowerCase()
      });
      
      const conditionalEdge = edges.find(
        (e) => {
          if (e.source !== sourceId || !isConditionalEdge(e)) return false;
          const edgeOption = (e.data?.conditionalOption as string)?.trim() || '';
          const edgeLabel = (e.label as string)?.trim() || '';
          const normalizedOption = option.trim().toLowerCase();
          const matches = edgeOption.toLowerCase() === normalizedOption || edgeLabel.toLowerCase() === normalizedOption;
          if (matches) {
            console.log('🔴 [PATH: removeConditionalBranch] Found matching edge:', {
              edgeId: e.id,
              source: e.source,
              target: e.target,
              edgeOption,
              edgeLabel,
              normalizedOption,
              optionMatches: edgeOption.toLowerCase() === normalizedOption,
              labelMatches: edgeLabel.toLowerCase() === normalizedOption
            });
          }
          return matches;
        }
      );

      if (!conditionalEdge) {
        console.error('❌ [PATH: removeConditionalBranch] NO CONDITIONAL EDGE FOUND!', {
          sourceId,
          option,
          availableEdgesFromSource: edges.filter(e => e.source === sourceId && isConditionalEdge(e)).map(e => ({
            id: e.id,
            target: e.target,
            option: e.data?.conditionalOption,
            label: e.label
          }))
        });
        return;
      }

      const subQuestionId = conditionalEdge?.target;
      const branchId = conditionalEdge?.data?.conditionalBranchId;
      
      console.log('🔴 [PATH: removeConditionalBranch] Edge found, extracted info:', {
        conditionalEdgeId: conditionalEdge.id,
        subQuestionId,
        branchId,
        edgeSource: conditionalEdge.source,
        edgeTarget: conditionalEdge.target,
        edgeOption: conditionalEdge.data?.conditionalOption,
        edgeLabel: conditionalEdge.label
      });

      // Step 1: Remove the option from the parent question's label
      console.log('🔴 [PATH: removeConditionalBranch] STEP 1: Removing option from parent label');
      setNodes((nds) => {
        const sourceNode = nds.find(n => n.id === sourceId);
        console.log('🔴 [PATH: removeConditionalBranch] STEP 1: Source node found:', {
          nodeId: sourceNode?.id,
          currentLabel: sourceNode?.data?.label,
          optionToRemove: option
        });
        
        return nds.map((node) => {
          if (node.id === sourceId) {
            const currentLabel = typeof node.data?.label === 'string' ? node.data.label : '';
            const newLabel = removeOptionFromLabelText(currentLabel, option);
            console.log('🔴 [PATH: removeConditionalBranch] STEP 1: Label update:', {
              nodeId: node.id,
              currentLabel,
              newLabel,
              changed: newLabel !== currentLabel
            });
            
            if (newLabel !== currentLabel) {
              const remainingOptions = getNodeOptions({ ...node, data: { ...node.data, label: newLabel } });
              const shouldClearConditionalMode = remainingOptions.length === 0;
              console.log('🔴 [PATH: removeConditionalBranch] STEP 1: Remaining options:', {
                remainingOptions,
                shouldClearConditionalMode
              });
              
              return {
                ...node,
                data: {
                  ...node.data,
                  label: newLabel,
                  ...(shouldClearConditionalMode ? { 
                    createConditionalQuestions: undefined,
                    questionType: undefined
                  } : {}),
                },
              };
            }
          }
          return node;
        });
      });

      // Step 2: Remove the conditional edge and check if all remaining options are conditional
      // Use branchId to reliably find and delete the conditional edge
      console.log('🔴 [PATH: removeConditionalBranch] STEP 2: Removing conditional edge and edges from sub-question', {
        subQuestionId,
        conditionalEdgeId: conditionalEdge?.id,
        branchId,
        hasConditionalEdge: !!conditionalEdge
      });
      
      if (subQuestionId || conditionalEdge) {
        setEdges((eds) => {
          console.log('🔴 [PATH: removeConditionalBranch] STEP 2: Current edges before filtering:', {
            totalEdges: eds.length,
            edgesToSubQuestion: eds.filter(e => e.target === subQuestionId).map(e => ({
              id: e.id,
              source: e.source,
              target: e.target,
              isConditional: isConditionalEdge(e),
              branchId: e.data?.conditionalBranchId
            })),
            edgesFromSubQuestion: eds.filter(e => e.source === subQuestionId).map(e => ({
              id: e.id,
              source: e.source,
              target: e.target
            }))
          });
          
          // Remove the conditional edge
          let deletedEdgeIds: string[] = [];
          let updatedEdges = eds.filter((edge) => {
            let shouldDelete = false;
            let deleteReason = '';
            
            // Delete the conditional edge
            if (conditionalEdge && edge.id === conditionalEdge.id) {
              shouldDelete = true;
              deleteReason = 'matches conditionalEdge.id';
            }
            // Delete edges FROM sub-question (outgoing - disconnects attached questions)
            else if (subQuestionId && edge.source === subQuestionId) {
              shouldDelete = true;
              deleteReason = 'source matches subQuestionId';
            }
            // Delete conditional edge using branchId (most reliable method)
            else if (branchId && edge.data?.conditionalBranchId === branchId) {
              shouldDelete = true;
              deleteReason = 'branchId matches';
            }
            // Fallback: Delete conditional edge TO the sub-question (for backward compatibility)
            else if (edge.id === conditionalEdge?.id || (edge.target === subQuestionId && isConditionalEdge(edge))) {
              shouldDelete = true;
              deleteReason = 'target matches subQuestionId and is conditional';
            }
            
            if (shouldDelete) {
              deletedEdgeIds.push(edge.id);
              console.log('🔴 [PATH: removeConditionalBranch] STEP 2: Deleting edge:', {
                edgeId: edge.id,
                source: edge.source,
                target: edge.target,
                reason: deleteReason
              });
            }
            
            return !shouldDelete;
          });
          
          console.log('🔴 [PATH: removeConditionalBranch] STEP 2: Edges after filtering:', {
            totalEdgesBefore: eds.length,
            totalEdgesAfter: updatedEdges.length,
            deletedEdgeIds,
            deletedCount: deletedEdgeIds.length
          });
          
          // After removing the conditional edge, check if all remaining options are conditional
          // If so, remove any main path edges
          setTimeout(() => {
            setNodes((nds) => {
              const sourceNode = nds.find(n => n.id === sourceId);
              if (sourceNode) {
                const remainingOptions = getNodeOptions(sourceNode);
                if (remainingOptions.length > 0) {
                  setEdges((finalEdges) => {
                    if (allOptionsAreConditional(sourceId, nds, finalEdges)) {
                      const nonConditionalEdges = finalEdges.filter(
                        e => e.source === sourceId && !isConditionalEdge(e)
                      );
                      if (nonConditionalEdges.length > 0) {
                        console.log(`🗑️ After removing option "${option}", all remaining options are conditional for ${sourceId}, removing ${nonConditionalEdges.length} main path edge(s)`);
                        return finalEdges.filter(e => !nonConditionalEdges.some(edge => edge.id === e.id));
                      }
                    }
                    return finalEdges;
                  });
                }
              }
              return nds;
            });
          }, 0);
          
          return updatedEdges;
        });

        // Step 3: Delete the sub-question node
        console.log('🔴 [PATH: removeConditionalBranch] STEP 3: Scheduling sub-question node deletion', {
          subQuestionId,
          willDeleteIn: '0ms (setTimeout)'
        });
        
        setTimeout(() => {
          setNodes((nds) => {
            const nodeBefore = nds.find(n => n.id === subQuestionId);
            console.log('🔴 [PATH: removeConditionalBranch] STEP 3: Deleting sub-question node:', {
              subQuestionId,
              nodeExists: !!nodeBefore,
              nodeData: nodeBefore ? {
                id: nodeBefore.id,
                label: nodeBefore.data?.label,
                parentConditionalSourceId: (nodeBefore.data as any)?.parentConditionalSourceId,
                parentConditionalOption: (nodeBefore.data as any)?.parentConditionalOption
              } : null,
              totalNodesBefore: nds.length
            });
            
            const filtered = nds.filter((node) => node.id !== subQuestionId);
            console.log('🔴 [PATH: removeConditionalBranch] STEP 3: Nodes after deletion:', {
              totalNodesAfter: filtered.length,
              nodeDeleted: nds.length > filtered.length,
              subQuestionStillExists: filtered.some(n => n.id === subQuestionId)
            });
            
            return filtered;
          });
        }, 0);
      } else {
        console.error('❌ [PATH: removeConditionalBranch] STEP 2/3 SKIPPED: No subQuestionId or conditionalEdge!', {
          subQuestionId,
          hasConditionalEdge: !!conditionalEdge
        });
      }
      
      console.log('🔴 [PATH: removeConditionalBranch] COMPLETED:', {
        sourceId,
        option,
        subQuestionId,
        branchId
      });
    },
    [edges, setNodes, setEdges]
  );

  // Helper function to check if a node is reachable from "start"
  const isNodeReachableFromStart = useCallback((nodeId: string, currentEdges: Edge[]): boolean => {
    if (nodeId === 'start') return true;
    
    // Build adjacency list
    const adjacencyList = new Map<string, string[]>();
    currentEdges.forEach(edge => {
      if (!adjacencyList.has(edge.source)) {
        adjacencyList.set(edge.source, []);
      }
      adjacencyList.get(edge.source)!.push(edge.target);
    });
    
    // DFS from start to check if node is reachable
    const visited = new Set<string>();
    const dfs = (id: string): boolean => {
      if (id === nodeId) return true;
      if (visited.has(id)) return false;
      visited.add(id);
      
      const neighbors = adjacencyList.get(id) || [];
      return neighbors.some(neighbor => dfs(neighbor));
    };
    
    return dfs('start');
  }, []);

  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      console.log('🔴 [KEYBOARD DELETE EDGE PATH] handleEdgesChange entry:', {
        changesCount: changes.length,
        changes: changes.map(c => ({
          type: c.type,
          id: c.type === 'remove' ? c.id : 'N/A',
          isRemove: c.type === 'remove'
        })),
        timestamp: new Date().toISOString()
      });
      
      const removals = new Map<string, Set<string>>();
      const removedEdgeLabels = new Map<string, string>(); // Capture labels before filtering
      const conditionalEdgeDeletions: Array<{ parentId: string; option: string; edgeId: string; branchId?: string }> = []; // Collect conditional edges to delete
      
      // FIRST: Capture labels of edges being removed (before filtering)
      changes.forEach((change) => {
        if (change.type === 'remove') {
          const edge = edges.find((e) => e.id === change.id);
          if (edge) {
            const edgeKey = `${edge.source}-${edge.target}`;
            // Check disconnectedEdgeLabelsRef first (captured in onEdgeUpdate), then fall back to edge.label
            const refLabel = disconnectedEdgeLabelsRef.current.get(edgeKey);
            const edgeLabel = edge.label ? String(edge.label) : null;
            const label = refLabel || edgeLabel;
            if (label) {
              removedEdgeLabels.set(edgeKey, label);
              console.log('📝 [KEYBOARD DELETE EDGE PATH] Captured label in handleEdgesChange:', { edgeKey, label, edgeId: edge.id, source: refLabel ? 'ref' : 'edge' });
            }
          }
        }
      });
      
      // SECOND: Filter out deletions of conditional edges - extract info and handle via handleRemoveOptionFromQuestion
      // Also prevent deletion of edges connecting to "Done" (end node) ONLY if the source node is reachable from start
      const filteredChanges = changes.filter((change) => {
        if (change.type === 'remove') {
          const edge = edges.find((e) => e.id === change.id);
          if (!edge) {
            console.log('⚠️ handleEdgesChange: Edge not found for removal:', change.id);
            return true; // Allow removal if edge not found (might have been removed already)
          }
          
          console.log('🔍 handleEdgesChange: Checking edge for removal:', {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            isConditional: isConditionalEdge(edge),
            targetIsDone: edge.target === 'end' || edge.target === 'done'
          });
          
          // Handle deletion of conditional edges by extracting info and calling handleRemoveOptionFromQuestion
          if (isConditionalEdge(edge)) {
            console.log('🔴 [KEYBOARD DELETE EDGE PATH] Conditional edge detected for deletion:', {
              edgeId: edge.id,
              source: edge.source,
              target: edge.target,
              edgeOption: edge.data?.conditionalOption,
              edgeLabel: edge.label,
              branchId: edge.data?.conditionalBranchId
            });
            
            const option = edge.data?.conditionalOption || edge.label || '';
            const parentId = edge.source;
            const branchId = edge.data?.conditionalBranchId;
            
            console.log('🔴 [KEYBOARD DELETE EDGE PATH] Extracted info from conditional edge:', {
              parentId,
              option,
              branchId,
              hasBoth: !!(parentId && option)
            });
            
            if (parentId && option) {
              console.log('✅ [KEYBOARD DELETE EDGE PATH] Adding to conditionalEdgeDeletions:', {
                parentId,
                option,
                edgeId: edge.id,
                branchId
              });
              conditionalEdgeDeletions.push({ parentId, option, edgeId: edge.id, branchId });
              console.log('🔴 [KEYBOARD DELETE EDGE PATH] Blocking edge deletion change - will handle via removeConditionalBranch');
              return false; // Block the edge deletion change itself - we'll handle it via handleRemoveOptionFromQuestion
            } else {
              console.error('❌ [KEYBOARD DELETE EDGE PATH] BLOCKING: Cannot delete conditional edge - missing parent or option:', {
                parentId,
                option,
                edgeId: edge.id
              });
              toast.error('Cannot delete conditional edges. Remove the option instead.');
              return false; // Block this change
            }
          }
          
          // Prevent deletion of edges connecting to "Done" (end node) ONLY if source is reachable from start
          if (edge.target === 'end' || edge.target === 'done') {
            const isReachable = isNodeReachableFromStart(edge.source, edges);
            if (isReachable) {
              console.log('❌ BLOCKING: Cannot disconnect from Done (node is part of main flow)', {
                edgeId: edge.id,
                source: edge.source,
                target: edge.target
              });
              toast.error('Cannot disconnect from Done. All questions and sub-questions must connect to Done.');
              // Clear the captured label since we're blocking the deletion
              const edgeKey = `${edge.source}-${edge.target}`;
              removedEdgeLabels.delete(edgeKey);
              disconnectedEdgeLabelsRef.current.delete(edgeKey);
              return false; // Block this change
            } else {
              console.log('✅ ALLOWING: Orphaned node can be disconnected from Done', {
                edgeId: edge.id,
                source: edge.source,
                target: edge.target
              });
              // Allow orphaned nodes to be disconnected
            }
          }
          
          if (edge?.data?.conditionalOption && edge.source) {
            if (!removals.has(edge.source)) {
              removals.set(edge.source, new Set<string>());
            }
            removals.get(edge.source)!.add(edge.data.conditionalOption as string);
          }
        }
        return true; // Allow all other changes
      });
      
      console.log('📊 [KEYBOARD DELETE EDGE PATH] handleEdgesChange: Filtered changes', {
        originalCount: changes.length,
        filteredCount: filteredChanges.length,
        blockedCount: changes.length - filteredChanges.length,
        removals: changes.filter(c => c.type === 'remove').length,
        blockedRemovals: changes.filter(c => c.type === 'remove').length - filteredChanges.filter(c => c.type === 'remove').length,
        conditionalEdgeDeletions: conditionalEdgeDeletions.length,
        conditionalEdgeDeletionsDetails: conditionalEdgeDeletions
      });

      console.log('🔴 [KEYBOARD DELETE EDGE PATH] Calling onEdgesChange with filtered changes');
      onEdgesChange(filteredChanges);

      // Handle conditional edge deletions via removeConditionalBranch (same method as X button)
      // This ensures consistent behavior - deleting a conditional edge should delete the sub-question
      if (conditionalEdgeDeletions.length > 0) {
        console.log('🔴 [KEYBOARD DELETE EDGE PATH] Processing conditional edge deletions:', {
          count: conditionalEdgeDeletions.length,
          deletions: conditionalEdgeDeletions
        });
        
        // Use setTimeout to ensure this happens after the edge change is processed
        setTimeout(() => {
          console.log('🔴 [KEYBOARD DELETE EDGE PATH] setTimeout callback executing, calling removeConditionalBranch for each deletion');
          conditionalEdgeDeletions.forEach(({ parentId, option, edgeId, branchId }, index) => {
            console.log('🔴 [KEYBOARD DELETE EDGE PATH] Calling removeConditionalBranch:', {
              index: index + 1,
              total: conditionalEdgeDeletions.length,
              parentId,
              option,
              edgeId,
              branchId
            });
            // Call removeConditionalBranch directly (same logic as handleRemoveOptionFromQuestion)
            // This ensures we use the same logic as the X button
            removeConditionalBranch(parentId, option);
            console.log('🔴 [KEYBOARD DELETE EDGE PATH] removeConditionalBranch call completed for:', { parentId, option });
            toast.success(`Removed option "${option}"`);
          });
          console.log('🔴 [KEYBOARD DELETE EDGE PATH] All removeConditionalBranch calls completed');
        }, 0);
      } else {
        console.log('🔴 [KEYBOARD DELETE EDGE PATH] No conditional edge deletions to process');
      }

      // Cleanup: Ensure only ONE non-conditional edge per question (consolidate duplicates)
      // Also remove main path edges if ALL options are conditional
      // This handles cases where multiple non-conditional edges might exist from previous connections
      // NOTE: Exclude sub-questions (nodes with decimal IDs) from this cleanup - they should only have one connection edge
      setEdges((currentEdges) => {
        const questionNodes = nodes.filter(n => 
          n.type === 'question' && 
          n.id !== 'start' && 
          n.id !== 'end' &&
          !n.id.includes('.') // Exclude sub-questions (they have IDs like "1.1", "1.2", etc.)
        );
        let hasChanges = false;
        const edgesToRemove = new Set<string>();
        
        questionNodes.forEach(node => {
          // Check if all options are conditional - if so, remove all main path edges
          if (allOptionsAreConditional(node.id, nodes, currentEdges)) {
            const nonConditionalEdges = currentEdges.filter(
              e => e.source === node.id && !isConditionalEdge(e)
            );
            if (nonConditionalEdges.length > 0) {
              console.log(`🗑️ All options are conditional for ${node.id}, removing ${nonConditionalEdges.length} main path edge(s)`);
              hasChanges = true;
              nonConditionalEdges.forEach(edge => {
                edgesToRemove.add(edge.id);
                console.log('🗑️ Removing main path edge (all options conditional):', edge.id, 'from', edge.source, 'to', edge.target);
              });
            }
            return; // Skip consolidation for this node
          }
          
          // Find all non-conditional edges from this question
          const nonConditionalEdges = currentEdges.filter(
            e => e.source === node.id && !isConditionalEdge(e)
          );
          
          if (nonConditionalEdges.length > 1) {
            // Multiple non-conditional edges found - keep only one
            console.log('🔍 Found multiple non-conditional edges from question', node.id, '- consolidating to one main path');
            hasChanges = true;
            
            // Prefer edge going to a question (not 'end') if possible, otherwise keep the first one
            const preferredEdge = nonConditionalEdges.find(e => {
              const targetNode = nodes.find(n => n.id === e.target);
              return targetNode && targetNode.type === 'question';
            }) || nonConditionalEdges[0];
            
            // Mark all other non-conditional edges for removal
            nonConditionalEdges.forEach(edge => {
              if (edge.id !== preferredEdge.id) {
                edgesToRemove.add(edge.id);
                console.log('🗑️ Removing duplicate non-conditional edge:', edge.id, 'from', edge.source, 'to', edge.target);
              }
            });
          }
        });
        
        if (hasChanges) {
          const filteredEdges = currentEdges.filter(e => !edgesToRemove.has(e.id));
          console.log('✅ Consolidated duplicate non-conditional edges: removed', edgesToRemove.size, 'edge(s)');
          // Clean up orphaned edges after consolidation
          return removeOrphanedEdges(nodes, filteredEdges);
        }
        
        // Always clean up orphaned edges, even if no consolidation was needed
        return removeOrphanedEdges(nodes, currentEdges);
      });

      removals.forEach((options, sourceId) => {
        options.forEach((option) => {
          const edge = edges.find(
            (e) =>
              e.source === sourceId &&
              e.data?.conditionalOption === option
          );
          const childIds =
            edge && edge.target && edge.target !== 'end' && edge.target !== 'start'
              ? [edge.target]
              : [];
          removeConditionalBranch(sourceId, option);
        });
      });
      
      // Feature 9: Trigger on edge changes (connections, disconnections)
      const hasStructuralChange = changes.some(c => c.type === 'remove' || c.type === 'add');
      if (hasStructuralChange) {
        // Labels will be rebuilt from question options (non-conditional options)
        // No need to capture labels - they're regenerated from the source question
        
        setTimeout(() => {
          setEdges((currentEdges) => {
            // First, ensure all branch nodes (sub-questions) have a path to Done
            // ONLY for branch nodes that are reachable from "start" (orphaned branches are allowed)
            const ensureBranchPathsToDone = (edges: Edge[]): Edge[] => {
              const updatedEdges = [...edges];
              
              // Find all branch nodes (sub-questions with decimal IDs like "1.1", "1.2", etc.)
              // But only those reachable from "start"
              const allBranchNodes = nodes.filter(n => 
                n.id.includes('.') && 
                n.id.match(/^\d+\.\d+$/) &&
                n.type === 'question'
              );
              
              // Filter to only include branch nodes reachable from start
              const branchNodes = allBranchNodes.filter(branchNode => 
                isNodeReachableFromStart(branchNode.id, edges)
              );
              
              // Helper to check if a node can reach "end"
              const canReachEnd = (nodeId: string, edgeList: Edge[], visited: Set<string> = new Set()): boolean => {
                if (nodeId === 'end' || nodeId === 'done') return true;
                if (visited.has(nodeId)) return false;
                visited.add(nodeId);
                
                const outgoingEdges = edgeList.filter(e => e.source === nodeId);
                return outgoingEdges.some(edge => canReachEnd(edge.target, edgeList, new Set(visited)));
              };
              
              // Helper to find where a branch should connect (same logic as when creating sub-questions)
              // This finds where the PARENT question connects to (via non-conditional edges)
              // Sub-questions should connect to the same target as their parent's main path
              const findNextTargetForBranch = (branchNodeId: string, edgeList: Edge[]): string => {
                // Find the parent question (the number before the dot)
                const match = branchNodeId.match(/^(\d+)\.\d+$/);
                if (!match) return 'end';
                
                const parentId = match[1];
                const parentNode = nodes.find(n => n.id === parentId);
                if (!parentNode) return 'end';
                
                // Find non-conditional edges from the parent (main flow path)
                // This is the key: we want to connect to where the PARENT connects, not traverse from the branch
                const parentOutgoingEdges = edgeList.filter(
                  e => e.source === parentId && !isConditionalEdge(e)
                );
                
                if (parentOutgoingEdges.length > 0) {
                  let nextTarget = parentOutgoingEdges[0].target;
                  
                  // If it's a sub-question, traverse through it to find the next main question or end
                  const findNextMainQuestion = (targetId: string, visited: Set<string> = new Set()): string => {
                    if (visited.has(targetId) || targetId === 'end' || targetId === 'done') {
                      return targetId;
                    }
                    visited.add(targetId);
                    
                    // If it's a sub-question, find where it connects to
                    if (targetId.includes('.') && targetId.match(/^\d+\.\d+$/)) {
                      const subQuestionOutgoing = edgeList.filter(
                        e => e.source === targetId && !isConditionalEdge(e)
                      );
                      if (subQuestionOutgoing.length > 0) {
                        return findNextMainQuestion(subQuestionOutgoing[0].target, visited);
                      }
                      return 'end';
                    }
                    
                    // It's a main question or end, return it
                    return targetId;
                  };
                  
                  return findNextMainQuestion(nextTarget);
                }
                
                // If parent has no main path edge (all options are conditional), default to 'end'
                return 'end';
              };
              
              // Check each branch node
              branchNodes.forEach(branchNode => {
                // Check if this branch has a path to end
                if (!canReachEnd(branchNode.id, updatedEdges)) {
                  // Find where it should connect
                  const nextTarget = findNextTargetForBranch(branchNode.id, updatedEdges);
                  
                  // Check if edge already exists
                  const existingEdge = updatedEdges.find(
                    e => e.source === branchNode.id && e.target === nextTarget
                  );
                  
                  if (!existingEdge) {
                    // Create edge from branch to next question or Done
                    // Rebuild label from branch node's non-conditional options
                    const branchLabel = rebuildEdgeLabelFromQuestion(branchNode.id, nodes, updatedEdges);
                    const branchToNextEdge: Edge = {
                      id: `edge-${branchNode.id}-${nextTarget}`,
                      source: branchNode.id,
                      sourceHandle: 'bottom-output',
                      target: nextTarget,
                      targetHandle: nextTarget === 'end' || nextTarget === 'done' ? 'top-input' : 'top-input',
                      type: 'smoothstep',
                      reconnectable: true,
                      data: { linear: true, autoReconnected: true },
                      ...(branchLabel ? { label: branchLabel } : {}),
                    };
                    
                    updatedEdges.push(branchToNextEdge);
                    console.log(`🔗 Auto-reconnected branch ${branchNode.id} to ${nextTarget} to maintain path to Done`);
                  }
                }
              });
              
              return updatedEdges;
            };
            
            // Ensure branch paths first
            const edgesWithBranchPaths = ensureBranchPathsToDone(currentEdges);
            
            // Then ensure start to done connection (preserve labels from removedEdgeLabels)
            const reconnectedEdges = ensureStartToDoneConnection(nodes, edgesWithBranchPaths, removedEdgeLabels);
            
            // Clear the disconnectedEdgeLabelsRef after use
            disconnectedEdgeLabelsRef.current.clear();
            
            setNodes((currentNodes) => {
              const { nodes: renumberedNodes, edges: renumberedEdges } = renumberQuestionsInFlow(currentNodes, reconnectedEdges);
              setEdges(renumberedEdges);
              return renumberedNodes;
            });
            return reconnectedEdges;
          });
        }, 0);
      }
    },
    [edges, onEdgesChange, removeConditionalBranch, removeOrphanedEdges, setNodes, setEdges, nodes, disconnectedEdgeLabelsRef, isNodeReachableFromStart]
  );

  // Safety net: Monitor edges and restore any edges connected to "Done" that were incorrectly removed
  // ONLY for nodes that are reachable from "start" (part of the main flow)
  useEffect(() => {
    // Check if any nodes that should connect to "Done" are missing their connection
    // But only for nodes reachable from "start" (orphaned nodes are allowed to be disconnected)
    const nodesThatShouldConnectToDone = nodes.filter(n => 
      n.id !== 'start' && 
      n.id !== 'end' && 
      n.type === 'question' &&
      isNodeReachableFromStart(n.id, edges) // Only check nodes reachable from start
    );
    
    nodesThatShouldConnectToDone.forEach(node => {
      // Check if this node has a path to "end" (either direct or through other nodes)
      const hasPathToEnd = (nodeId: string, edgeList: Edge[], visited: Set<string> = new Set()): boolean => {
        if (nodeId === 'end' || nodeId === 'done') return true;
        if (visited.has(nodeId)) return false;
        visited.add(nodeId);
        
        const outgoingEdges = edgeList.filter(e => e.source === nodeId);
        return outgoingEdges.some(edge => hasPathToEnd(edge.target, edgeList, new Set(visited)));
      };
      
      if (!hasPathToEnd(node.id, edges)) {
        console.log('⚠️ Safety net: Node missing path to Done, will restore connection:', node.id);
        // Find the last question in the main path and connect it to end
        // This is a fallback - the main reconnection logic should handle this
        // But we'll restore it here as a safety net
        setEdges((currentEdges) => {
          // Check again with current edges (avoid race conditions)
          if (hasPathToEnd(node.id, currentEdges)) {
            return currentEdges; // Already connected
          }
          
          // Double-check that node is still reachable from start (might have been orphaned)
          if (!isNodeReachableFromStart(node.id, currentEdges)) {
            console.log('⚠️ Safety net: Node is now orphaned, skipping restoration:', node.id);
            return currentEdges; // Node is orphaned, don't restore
          }
          
          // Find if there's a preserved label for this connection
          const edgeKey = `${node.id}-end`;
          const preservedLabel = disconnectedEdgeLabelsRef.current.get(edgeKey);
          
          // Check if edge already exists
          const existingEdge = currentEdges.find(e => e.source === node.id && e.target === 'end');
          if (existingEdge) {
            return currentEdges; // Already exists
          }
          
          // Restore the edge
          const restoredEdge: Edge = {
            id: `edge-${node.id}-end`,
            source: node.id,
            target: 'end',
            type: 'smoothstep',
            data: { linear: true, autoReconnected: true },
            reconnectable: true,
            sourceHandle: 'bottom-output',
            targetHandle: 'top-input',
            ...(preservedLabel ? { label: preservedLabel } : {}),
          };
          
          console.log('🔗 Safety net: Restored edge to Done:', {
            source: node.id,
            target: 'end',
            label: preservedLabel || 'N/A'
          });
          
          return [...currentEdges, restoredEdge];
        });
      }
    });
  }, [edges, nodes, setEdges, disconnectedEdgeLabelsRef, isNodeReachableFromStart]);

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const removals = new Map<
        string,
        { sourceId: string; option: string; childIds: Set<string> }
      >();
      const deletedNodes: Array<{ id: string; node: Node | undefined }> = [];
      let hasRemoval = false;

      changes.forEach((change) => {
        if (change.type === 'remove') {
          hasRemoval = true;
          const node = nodes.find((n) => n.id === change.id);
          deletedNodes.push({ id: change.id, node });
          
          // For sub-questions, use the conditional edge's option instead of node's parentConditionalOption
          // The edge is the source of truth for which option this sub-question is associated with
          if (node && node.id.includes('.') && node.id.match(/^\d+\.\d+$/)) {
            // This is a sub-question - find the conditional edge that connects to it
            const conditionalEdge = edges.find(e => 
              e.target === node.id && isConditionalEdge(e)
            );
            
            if (conditionalEdge) {
              // Use the edge's conditionalOption or label as the source of truth
              const option = conditionalEdge.data?.conditionalOption || conditionalEdge.label || '';
              const parentSource = conditionalEdge.source;
              
              if (parentSource && option) {
                const key = `${parentSource}::${option}`;
                if (!removals.has(key)) {
                  removals.set(key, {
                    sourceId: parentSource,
                    option: option,
                    childIds: new Set<string>(),
                  });
                }
                if (node.id) {
                  removals.get(key)!.childIds.add(node.id);
                }
              }
            }
          } else {
            // For non-sub-questions, use the node's parentConditionalOption (backward compatibility)
            const parentSource = (node?.data as any)?.parentConditionalSourceId;
            const parentOption = (node?.data as any)?.parentConditionalOption;
            if (parentSource && parentOption) {
              const key = `${parentSource}::${parentOption}`;
              if (!removals.has(key)) {
                removals.set(key, {
                  sourceId: parentSource,
                  option: parentOption,
                  childIds: new Set<string>(),
                });
              }
              if (node?.id) {
                removals.get(key)!.childIds.add(node.id);
              }
            }
          }
        }
      });

      // When deleting a sub-question via keyboard: Use the same method as the "x" button
      // This ensures consistent behavior across all deletion methods
      // CRITICAL: Find the conditional edge first (like removeConditionalBranch does) - this is more reliable than node data
      console.log('🔴 [KEYBOARD DELETE NODE PATH] handleNodesChange - Processing sub-question deletions:', {
        deletedNodesCount: deletedNodes.length,
        deletedNodeIds: deletedNodes.map(d => d.id),
        timestamp: new Date().toISOString()
      });
      
      const subQuestionDeletions: Array<{ parentId: string; option: string; deletedId: string; branchId?: string }> = [];
      
      deletedNodes.forEach(({ id: deletedId, node: deletedNode }) => {
        console.log('🔴 [KEYBOARD DELETE NODE PATH] Processing deleted node:', {
          deletedId,
          isSubQuestion: deletedNode && deletedId.includes('.') && deletedId.match(/^\d+\.\d+$/),
          nodeData: deletedNode ? {
            label: deletedNode.data?.label,
            parentConditionalSourceId: (deletedNode.data as any)?.parentConditionalSourceId,
            parentConditionalOption: (deletedNode.data as any)?.parentConditionalOption,
            conditionalBranchId: (deletedNode.data as any)?.conditionalBranchId
          } : null
        });
        
        if (deletedNode && deletedId.includes('.') && deletedId.match(/^\d+\.\d+$/)) {
          console.log('🔴 [KEYBOARD DELETE NODE PATH] This is a sub-question, searching for conditional edge by TARGET:', {
            deletedId,
            searchingFor: 'edge.target === deletedId && isConditionalEdge(e)',
            totalEdges: edges.length,
            edgesToDeletedId: edges.filter(e => e.target === deletedId).map(e => ({
              id: e.id,
              source: e.source,
              target: e.target,
              isConditional: isConditionalEdge(e),
              option: e.data?.conditionalOption,
              label: e.label
            }))
          });
          
          // Find the conditional edge that targets this sub-question (same approach as removeConditionalBranch)
          // This is more reliable than extracting from node data, which may be missing or incorrect
          const conditionalEdge = edges.find(e => 
            e.target === deletedId && isConditionalEdge(e)
          );
          
          if (conditionalEdge) {
            // Extract option and parent ID from the edge (source of truth)
            const option = conditionalEdge.data?.conditionalOption || conditionalEdge.label || '';
            const parentId = conditionalEdge.source;
            const branchId = conditionalEdge.data?.conditionalBranchId;
            
            console.log('🔴 [KEYBOARD DELETE NODE PATH] Conditional edge found:', {
              edgeId: conditionalEdge.id,
              source: conditionalEdge.source,
              target: conditionalEdge.target,
              extractedOption: option,
              extractedParentId: parentId,
              extractedBranchId: branchId,
              edgeOption: conditionalEdge.data?.conditionalOption,
              edgeLabel: conditionalEdge.label
            });
            
            if (parentId && option) {
              subQuestionDeletions.push({ parentId, option, deletedId, branchId });
              console.log('🔴 [KEYBOARD DELETE NODE PATH] Added to subQuestionDeletions:', {
                parentId,
                option,
                deletedId,
                branchId
              });
            } else {
              console.error('❌ [KEYBOARD DELETE NODE PATH] Missing parentId or option:', { parentId, option });
            }
          } else {
            console.warn('⚠️ [KEYBOARD DELETE NODE PATH] No conditional edge found, trying node data fallback');
            // Fallback: Try node data if edge not found (for backward compatibility)
            const nodeData = deletedNode.data as any;
            const option = nodeData?.parentConditionalOption || '';
            const parentId = nodeData?.parentConditionalSourceId || '';
            
            console.log('🔴 [KEYBOARD DELETE NODE PATH] Fallback - node data:', {
              parentId,
              option,
              hasBoth: !!(parentId && option)
            });
            
            if (parentId && option) {
              subQuestionDeletions.push({ parentId, option, deletedId });
              console.log('🔴 [KEYBOARD DELETE NODE PATH] Added to subQuestionDeletions (fallback):', {
                parentId,
                option,
                deletedId
              });
            } else {
              console.error('❌ [KEYBOARD DELETE NODE PATH] Fallback also failed - missing data:', { parentId, option });
            }
          }
        }
      });
      
      console.log('🔴 [KEYBOARD DELETE NODE PATH] Final subQuestionDeletions array:', {
        count: subQuestionDeletions.length,
        deletions: subQuestionDeletions
      });

      // Process sub-question deletions: Use the same method as the "x" button
      // This ensures all deletion paths (x button, keyboard delete) work the same way
      if (subQuestionDeletions.length > 0) {
        console.log('🔴 [KEYBOARD DELETE NODE PATH] Processing sub-question deletions:', {
          count: subQuestionDeletions.length,
          deletions: subQuestionDeletions
        });
        
        // Filter out sub-question deletions from the changes - we handle them via handleRemoveOptionFromQuestion
        const filteredChanges = changes.filter(change => {
          if (change.type === 'remove') {
            const deletedNode = nodes.find(n => n.id === change.id);
            if (deletedNode && change.id.includes('.') && change.id.match(/^\d+\.\d+$/)) {
              console.log('🔴 [KEYBOARD DELETE NODE PATH] Filtering out sub-question from changes:', change.id);
              return false; // Skip - we handle sub-question deletion via handleRemoveOptionFromQuestion
            }
          }
          return true;
        });

        console.log('🔴 [KEYBOARD DELETE NODE PATH] Filtered changes:', {
          originalCount: changes.length,
          filteredCount: filteredChanges.length,
          removedSubQuestions: changes.length - filteredChanges.length
        });

        // Apply the filtered node changes first (non-sub-question deletions)
        console.log('🔴 [KEYBOARD DELETE NODE PATH] Calling onNodesChange with filtered changes');
        onNodesChange(filteredChanges);

        // Then call handleRemoveOptionFromQuestion for each deleted sub-question
        // This uses the same working logic as the "x" button
        console.log('🔴 [KEYBOARD DELETE NODE PATH] Calling handleRemoveOptionFromQuestion for each deletion');
        subQuestionDeletions.forEach(({ parentId, option, deletedId, branchId }, index) => {
          console.log('🔴 [KEYBOARD DELETE NODE PATH] Calling handleRemoveOptionFromQuestion:', {
            index: index + 1,
            total: subQuestionDeletions.length,
            parentId,
            option,
            deletedId,
            branchId
          });
          handleRemoveOptionFromQuestion(parentId, option);
        });

        console.log('🔴 [KEYBOARD DELETE NODE PATH] All handleRemoveOptionFromQuestion calls completed, returning early');
        // Early return to skip the old reconnection logic below
        return;
      } else {
        console.log('🔴 [KEYBOARD DELETE NODE PATH] No sub-question deletions to process');
      }

      // Filter out sub-question deletions from the changes - we handle them manually above
      const filteredChanges = changes.filter(change => {
        if (change.type === 'remove') {
          const deletedNode = nodes.find(n => n.id === change.id);
          if (deletedNode && change.id.includes('.') && change.id.match(/^\d+\.\d+$/)) {
            return false; // Skip - we handle sub-question deletion via handleRemoveOptionFromQuestion
          }
        }
        return true;
      });

      // Apply the filtered node changes (sub-question nodes are handled separately)
      onNodesChange(filteredChanges);

      // Handle removals for non-sub-question deletions (backward compatibility)
      removals.forEach(({ sourceId, option }) => {
        removeConditionalBranch(sourceId, option);
      });

      // Feature 9: Reconnect after deletion (before general ensureStartToDoneConnection)
      // Skip reconnection for sub-questions - they should just be disconnected
      if (hasRemoval && deletedNodes.length > 0) {
        // Create a Set of deleted sub-question IDs for quick lookup
        const deletedSubQuestionIds = new Set(
          deletedNodes
            .filter(({ id, node }) => node && id.includes('.') && id.match(/^\d+\.\d+$/))
            .map(({ id }) => id)
        );
        
        // Store edges snapshot BEFORE deletion for reconnection logic
        const edgesSnapshot = edges;
        
        setTimeout(() => {
          // Get current nodes after deletion
          setNodes((currentNodes) => {
            // Update edges with reconnection logic
            setEdges((currentEdges) => {
              let updatedEdges = currentEdges;
              
              // Reconnect each deleted node using edges snapshot (before deletion)
              // Pass remaining nodes (after deletion) and edges before deletion
              // SKIP sub-questions - they should remain disconnected
              deletedNodes.forEach(({ id: deletedId, node: deletedNode }) => {
                // Skip reconnection for sub-questions (they're handled via handleRemoveOptionFromQuestion)
                if (deletedSubQuestionIds.has(deletedId)) {
                  return; // Don't reconnect sub-questions
                }
                updatedEdges = reconnectAfterDeletion(
                  deletedId, 
                  deletedNode, 
                  currentNodes, // Remaining nodes after deletion
                  edgesSnapshot // Edges before deletion
                );
              });
              
              // Then ensure start-to-done connection
              const reconnectedEdges = ensureStartToDoneConnection(currentNodes, updatedEdges);
              
              // Clean up orphaned edges after reconnection
              const cleanedEdges = removeOrphanedEdges(currentNodes, reconnectedEdges);
              
              // Finally, renumber questions and update nodes
              // CRITICAL: Renumbering must receive edges that include conditional edges to remaining sub-questions
              // (e.g., Q1 → Q1.4) so they can be updated when Q1.4 becomes Q1.3 after Q1.3 is deleted
              // Use a longer timeout to ensure edges from Step 2 are fully updated before renumbering
              setTimeout(() => {
                // Get the latest edges state to ensure we have all conditional edges to remaining sub-questions
                setEdges((latestEdges) => {
                  // Ensure all conditional edges from latest state are included
                  const edgesMap = new Map(cleanedEdges.map(e => [e.id, e]));
                  latestEdges.forEach(e => {
                    // Preserve conditional edges from latest state - these are the edges to remaining sub-questions
                    if (isConditionalEdge(e)) {
                      edgesMap.set(e.id, e);
                    }
                  });
                  const mergedEdges = Array.from(edgesMap.values());
                  
                  setNodes((nodesAfterReconnect) => {
                    const { nodes: renumberedNodes, edges: renumberedEdges } = renumberQuestionsInFlow(nodesAfterReconnect, mergedEdges);
                    // Clean up orphaned edges after renumbering
                    const cleanedRenumberedEdges = removeOrphanedEdges(nodesAfterReconnect, renumberedEdges);
                    setEdges(cleanedRenumberedEdges);
                    return renumberedNodes;
                  });
                  // Clean up orphaned edges in latest edges
                  return removeOrphanedEdges(nodes, latestEdges);
                });
              }, 50); // Longer timeout to ensure edge deletion from Step 2 is complete
              
              return cleanedEdges;
            });
            
            return currentNodes;
          });
        }, 0);
        return; // Early return to skip the old renumbering logic below
      }

    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleRemoveOptionFromQuestion and removeOrphanedEdges are declared later; adding them causes TDZ error
    [nodes, edges, onNodesChange, removeConditionalBranch, setNodes, setEdges]
  );

  // Optimized: Only update selected edges when connectionLineType changes
  useEffect(() => {
    setEdges((eds) => {
      const hasSelectedEdges = eds.some((e) => e.selected);
      if (!hasSelectedEdges) return eds; // Early return if no selected edges
      
      return eds.map((edge) => (edge.selected ? { ...edge, type: connectionLineType } : edge));
    });
  }, [connectionLineType, setEdges]);

  // Connection validation rules:
  // 1. Prevent self-connections (box cannot link to itself)
  // 2. Only 1 distinct link from a box to another box (one source box → one target box = one connection max)
  // 3. Each output handle can only have 1 connection
  // 4. Input handles can accept multiple connections from different boxes
  // Note: This is used for NEW connections and edge updates. For edge updates, we're more lenient
  // to allow moving edges between handles (the onEdgeUpdate handler will clean up conflicts).
  const isValidConnection = useCallback(
    (connection: Connection | null) => {
      if (!connection) return false;
      
      // Rule 1: Prevent self-connections
      if (connection.source === connection.target) {
        return false;
      }
      
      // Allow connections to sub-question nodes (nodes with decimal IDs like "1.1", "2.1")
      // The conditional edge itself is non-reconnectable, but other questions can connect to sub-questions
      const isConnectingToSubQuestion = connection.target?.includes('.') && connection.target?.match(/^\d+\.\d+$/);
      if (isConnectingToSubQuestion) {
        console.log('✅ Valid connection to sub-question node:', connection.target);
        return true; // Allow connections to sub-question nodes
      }
      
      // For edge updates, we're more lenient - allow the connection and let onEdgeUpdate handle cleanup
      // This allows moving edges from one handle to another
      // Rule 2 & 3 are enforced in onEdgeUpdate, not here, to allow edge reconnection
      
      return true;
    },
    []
  );

  const onConnectStart = useCallback((event: React.MouseEvent | React.TouchEvent, params: { nodeId: string | null; handleType: string | null }) => {
    // Connection started - user is dragging from a handle
    console.log('🟢 CONNECT START - Visual connection line should appear', params);
    isConnectingRef.current = true;
  }, []);
  
  // Update global ref so it can be accessed from DraggableEdge
  useEffect(() => {
    onConnectStartRef.current = onConnectStart;
  }, [onConnectStart]);

  const onConnectEnd = useCallback(() => {
    // Connection ended - user finished dragging
    isConnectingRef.current = false;
  }, []);

  const onConnect = useCallback(
    (params: Connection) => {
      // Rule 1: Prevent self-connections
      if (params.source === params.target) {
        return;
      }
      
      // Allow connections to sub-question nodes (nodes with decimal IDs like "1.1", "2.1")
      // BUT: Prevent creating new links TO sub-questions from their parent (they should only have the conditional edge)
      const isConnectingToSubQuestion = params.target?.includes('.') && params.target?.match(/^\d+\.\d+$/);
      
      if (isConnectingToSubQuestion) {
        // Check if this sub-question already has a conditional edge from its parent
        const subQuestionNode = nodes.find(n => n.id === params.target);
        const parentSourceId = subQuestionNode?.data ? (subQuestionNode.data as any).parentConditionalSourceId : null;
        
        // If connecting from the parent to the sub-question, check if conditional edge already exists
        if (parentSourceId && params.source === parentSourceId) {
          const existingConditionalEdge = edges.find(
            e => e.source === parentSourceId && 
                 e.target === params.target &&
                 isConditionalEdge(e)
          );
          
          if (existingConditionalEdge) {
            toast.error('Sub-question already has a conditional edge from its parent. Remove the option to delete it.');
            return; // Block the connection
          }
        }
      }
      
      // Rule 2: Only 1 distinct link from source box to target box
      // If source already connects to target, remove the old connection
      // BUT: Don't remove conditional edges - they're managed automatically
      const existingEdgeToTarget = edges.find(
        (e) => e.source === params.source && e.target === params.target
      );
      if (existingEdgeToTarget && !isConditionalEdge(existingEdgeToTarget)) {
        setEdges((eds) => eds.filter((e) => e.id !== existingEdgeToTarget.id));
      }
      
      // Rule 3: Check if this output handle already has a connection
      // BUT: Don't remove conditional edges - they're managed automatically
      const existingEdgeFromHandle = edges.find(
        (e) => e.source === params.source && e.sourceHandle === (params.sourceHandle || null)
      );
      if (existingEdgeFromHandle && !isConditionalEdge(existingEdgeFromHandle)) {
        // Remove the old edge from this handle
        setEdges((eds) => eds.filter((e) => e.id !== existingEdgeFromHandle.id));
      }
      
      // Rule 4: Only ONE main path edge per question (non-conditional edge)
      // If there are multiple non-conditional edges from the same source, remove all except the new one
      // This ensures there's only one main path, even if the question has multiple non-conditional options
      const sourceNode = nodes.find(n => n.id === params.source);
      if (sourceNode && sourceNode.type === 'question') {
        // Find all existing non-conditional edges from this source
        const existingNonConditionalEdges = edges.filter(
          (e) => e.source === params.source && !isConditionalEdge(e)
        );
        
        // If there are multiple non-conditional edges, remove all except the one we're about to create
        // We'll remove them in the setEdges call below
        if (existingNonConditionalEdges.length > 0) {
          console.log('🔍 Found multiple non-conditional edges from source, will consolidate to one main path', {
            source: params.source,
            existingCount: existingNonConditionalEdges.length,
            newTarget: params.target
          });
        }
      }
      
      // Log connection to sub-question for debugging
      if (isConnectingToSubQuestion) {
        console.log('✅ Connecting to sub-question node:', params.target);
      }
      
      // Rebuild label from source question's non-conditional options
      const sourceLabel = rebuildEdgeLabelFromQuestion(params.source || '', nodes, edges);
      
      const newEdge = {
        ...params,
        type: connectionLineType,
        id: `edge-${params.source}-${params.target}-${params.sourceHandle || 'default'}`,
        reconnectable: true,
        ...(sourceLabel ? { label: sourceLabel } : {}),
      };
      
      const targetId = params.target ?? '';
      if (!targetId) {
      setEdges((eds) => addEdge(newEdge, eds));
        return;
      }
      
      // Add the new edge first, then check for auto-linking using the updated edges
      setEdges((eds) => {
        // Rule 4: Remove all other non-conditional edges from the same source before adding the new one
        // This ensures only ONE main path edge per question
        // BUT: This does NOT apply to sub-questions - a question can have many sub-questions
        const sourceNode = nodes.find(n => n.id === params.source);
        
        // Helper to check if a node ID represents a sub-question (has decimal format like "1.1", "2.1")
        const isSubQuestion = (nodeId: string | null | undefined): boolean => {
          if (!nodeId) return false;
          return nodeId.includes('.') && nodeId.match(/^\d+\.\d+$/) !== null;
        };
        
        const sourceIsSubQuestion = isSubQuestion(params.source);
        const targetIsSubQuestion = isSubQuestion(params.target);
        
        let updatedEdges: Edge[];
        
        // Only apply the "one output" rule if:
        // 1. Source is a question (not start/end)
        // 2. Source is NOT a sub-question (sub-questions can have multiple outputs)
        // 3. Target is NOT a sub-question (connecting TO a sub-question is allowed - they can have multiple inputs)
        if (sourceNode && sourceNode.type === 'question' && !sourceIsSubQuestion && !targetIsSubQuestion) {
          // Remove all existing non-conditional edges from this source (except conditional edges)
          const filteredEdges = eds.filter(
            (e) => !(e.source === params.source && !isConditionalEdge(e))
          );
          // Add the new edge
          updatedEdges = addEdge(newEdge, filteredEdges);
          
          console.log('✅ Consolidated non-conditional edges: removed', eds.length - filteredEdges.length, 'duplicate(s), keeping new edge to', params.target);
        } else {
          // For non-question nodes, sub-questions, or when connecting to sub-questions, just add the edge normally
          updatedEdges = addEdge(newEdge, eds);
        }
        
        const targetNode = nodes.find((n) => n.id === targetId);
      const endNode = nodes.find((n) => n.id === 'end');
      
      // Auto-link to 'end' using bottom output handle (default) if target node doesn't have a path to 'end'
      // Check all output handles: bottom-output, left-output, right-output
        if (targetNode && endNode && targetId !== 'end' && !isEndNode(targetNode)) {
        // Check if there's already a direct edge to 'end' from this node
          const existingEdgeToEnd = updatedEdges.find((e) => e.source === targetId && e.target === 'end');
        if (!existingEdgeToEnd) {
          // Check if there's any path to 'end' through other nodes
            // Use a more robust DFS that properly checks all paths
            // IMPORTANT: Use updatedEdges (which includes the newly added edge) for accurate path checking
          const hasPathToEnd = (() => {
              const visited = new Set<string>();
              const dfs = (nodeId: string): boolean => {
                // If we've reached 'end', we found a path
                if (nodeId === 'end') {
                  return true;
                }
                // If we've already visited this node, skip (avoid cycles)
                if (visited.has(nodeId)) {
                  return false;
                }
                visited.add(nodeId);
                
                // Check all outgoing edges from this node using updatedEdges
                const outgoingEdges = updatedEdges.filter((e) => e.source === nodeId);
                
                // If no outgoing edges, this node can't reach 'end'
                if (outgoingEdges.length === 0) {
                  return false;
                }
                
                // Recursively check all paths from this node
                for (const edge of outgoingEdges) {
                  // Recursively check if the target node can reach 'end'
                  if (dfs(edge.target)) {
                    return true;
                  }
                }
                return false;
              };
              return dfs(targetId);
          })();
          
            // Only create auto-link if there's NO path to 'end' at all
            // This prevents creating duplicate links when the target already has a path through other questions
          if (!hasPathToEnd) {
            // Auto-link using bottom output handle (default)
            // Rebuild label from target question's non-conditional options
            const autoLinkLabel = rebuildEdgeLabelFromQuestion(targetId, nodes, updatedEdges);
            const autoLinkEdge: Edge = {
                id: `edge-${targetId}-end`,
                source: targetId,
              sourceHandle: 'bottom-output', // Use bottom handle as default
              target: 'end',
              type: connectionLineType,
              data: { linear: true, autoLinked: true },
                reconnectable: true,
                ...(autoLinkLabel ? { label: autoLinkLabel } : {}),
            };
              return [...updatedEdges, autoLinkEdge];
          }
        }
      }
        
        // Feature 9: Trigger on connection
        setTimeout(() => {
          setEdges((currentEdges) => {
            const reconnectedEdges = ensureStartToDoneConnection(nodes, currentEdges);
            setNodes((currentNodes) => {
              const { nodes: renumberedNodes, edges: renumberedEdges } = renumberQuestionsInFlow(currentNodes, reconnectedEdges);
              setEdges(renumberedEdges);
              return renumberedNodes;
            });
            return reconnectedEdges;
          });
        }, 0);
        
        return updatedEdges;
      });
    },
    [setEdges, setNodes, nodes, edges, connectionLineType]
  );

  const onEdgeUpdateStart = useCallback(() => {
    // Edge update started - user is dragging an edge endpoint (from a handle)
    console.log('🟡 EDGE UPDATE START - Dragging from handle (ReactFlow built-in)');
    isConnectingRef.current = true;
  }, []);
  
  // Update global ref so it can be accessed from DraggableEdge
  useEffect(() => {
    onEdgeUpdateStartRef.current = onEdgeUpdateStart;
  }, [onEdgeUpdateStart]);

  const onEdgeUpdate = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      // For conditional edges: Allow moving connection points (handles) on same nodes
      // but prevent changing source/target nodes
      if (isConditionalEdge(oldEdge)) {
        // Check if source and target are the same (only handles are changing)
        const sameSource = newConnection.source === oldEdge.source || newConnection.source === null || newConnection.source === undefined;
        const sameTarget = newConnection.target === oldEdge.target || newConnection.target === null || newConnection.target === undefined;
        
        if (sameSource && sameTarget) {
          // Allow handle updates (moving connection points on same nodes)
          console.log('✅ Allowing handle update for conditional edge:', {
            edgeId: oldEdge.id,
            oldSourceHandle: oldEdge.sourceHandle,
            newSourceHandle: newConnection.sourceHandle,
            oldTargetHandle: oldEdge.targetHandle,
            newTargetHandle: newConnection.targetHandle
          });
          // Continue with the update - this will just change handles
        } else {
          // Prevent changing source/target nodes
          toast.error('Cannot reconnect conditional edges to different nodes. You can only move the connection points on the same nodes.');
          return; // Block the update
        }
      }
      
      // Update ref so it can be accessed from useEffect
      onEdgeUpdateRef.current = onEdgeUpdate;
      // Check which endpoint should be moved based on where the user clicked
      const updateMode = edgeUpdateModeRef[oldEdge.id] || 'target';
      
      // If we need to move the source endpoint, we need to reverse the edge
      // ReactFlow's edge update always moves the target, so we swap source/target
      let actualConnection = newConnection;
      let actualOldEdge = oldEdge;
      
      if (updateMode === 'source') {
        // Reverse the edge: swap source and target, and swap handles
        actualOldEdge = {
          ...oldEdge,
          source: oldEdge.target,
          target: oldEdge.source,
          sourceHandle: oldEdge.targetHandle,
          targetHandle: oldEdge.sourceHandle,
        };
        actualConnection = {
          source: newConnection.target || oldEdge.target || '',
          target: newConnection.source || oldEdge.source || '',
          sourceHandle: newConnection.targetHandle || oldEdge.targetHandle || null,
          targetHandle: newConnection.sourceHandle || oldEdge.sourceHandle || null,
        };
      }
      
      // Rule 1: Prevent self-connections
      if (actualConnection.source === actualConnection.target) {
        return;
      }
      
      // Check if source and target are valid
      // If edge is being disconnected, check if it's connected to "Done" and prevent it
      // ONLY if the source node is reachable from "start" (orphaned nodes can be disconnected)
      if (!actualConnection.source || !actualConnection.target) {
        // Prevent disconnection of edges connected to "Done" (end node) ONLY if source is reachable from start
        if (oldEdge.target === 'end' || oldEdge.target === 'done') {
          const isReachable = isNodeReachableFromStart(oldEdge.source, edges);
          if (isReachable) {
            console.log('❌ BLOCKING: Cannot disconnect edge connected to Done during drag (node is part of main flow)', {
              edgeId: oldEdge.id,
              source: oldEdge.source,
              target: oldEdge.target
            });
            toast.error('Cannot disconnect from Done. All questions and sub-questions must connect to Done.');
            return; // Block the disconnection
          } else {
            console.log('✅ ALLOWING: Orphaned node can be disconnected from Done during drag', {
              edgeId: oldEdge.id,
              source: oldEdge.source,
              target: oldEdge.target
            });
            // Allow orphaned nodes to be disconnected - don't block, just return
            return;
          }
        }
        
        // Capture the label before the edge is removed (for other edges that can be disconnected)
        const edgeLabel = oldEdge.label ? String(oldEdge.label) : null;
        if (edgeLabel) {
          const edgeKey = `${oldEdge.source}-${oldEdge.target}`;
          disconnectedEdgeLabelsRef.current.set(edgeKey, edgeLabel);
          console.log('📝 Captured label for disconnected edge:', { edgeKey, label: edgeLabel });
        }
        console.log('⚠️ Edge disconnected during drag - label captured, handleEdgesChange will handle removal');
        return; // Let handleEdgesChange handle the actual removal
      }
      
      setEdges((eds) => {
        // Rule 2: Only 1 distinct link from source box to target box
        // If source already connects to new target (and it's not the edge we're updating), remove it
        const existingEdgeToTarget = eds.find(
          (e) => e.source === actualConnection.source && 
                 e.target === actualConnection.target && 
                 e.id !== actualOldEdge.id
        );
        
        // Rule 3: Check if the new source handle already has a connection
        const existingEdgeFromHandle = eds.find(
          (e) => e.source === actualConnection.source && 
                 e.sourceHandle === (actualConnection.sourceHandle || null) && 
                 e.id !== actualOldEdge.id
        );
        
        // If moving to a handle that already has a connection, remove the old one
        let updated = eds;
        const movingConditional = isConditionalEdge(actualOldEdge);

        if (existingEdgeToTarget && !(movingConditional && isConditionalEdge(existingEdgeToTarget))) {
          updated = updated.filter((e) => e.id !== existingEdgeToTarget.id);
        }
        if (existingEdgeFromHandle && !(movingConditional && isConditionalEdge(existingEdgeFromHandle))) {
          updated = updated.filter((e) => e.id !== existingEdgeFromHandle.id);
        }
        
        // Use reconnectEdge which properly handles handle snapping
        // reconnectEdge automatically preserves sourceHandle and targetHandle from actualConnection
        updated = reconnectEdge(actualOldEdge, actualConnection, updated);
        
        // Rule: A question can only have ONE output link (non-conditional edge)
        // This applies when reconnecting an edge to a NEW target (not just repositioning)
        // BUT: This does NOT apply to sub-questions - a question can have many sub-questions
        const sourceId = actualConnection.source || oldEdge.source;
        const targetId = actualConnection.target || oldEdge.target;
        const oldTargetId = oldEdge.target;
        
        // Only apply the rule if the target actually changed
        const targetChanged = targetId && targetId !== oldTargetId;
        
        // Helper to check if a node ID represents a sub-question (has decimal format like "1.1", "2.1")
        const isSubQuestion = (nodeId: string | null | undefined): boolean => {
          if (!nodeId) return false;
          return nodeId.includes('.') && nodeId.match(/^\d+\.\d+$/) !== null;
        };
        
        const sourceNode = nodes.find(n => n.id === sourceId);
        const sourceIsQuestion = sourceNode && sourceNode.type === 'question';
        const sourceIsSubQuestion = isSubQuestion(sourceId);
        const targetIsSubQuestion = isSubQuestion(targetId);
        
        // Only apply the "one output" rule if:
        // 1. The target actually changed (not just repositioning)
        // 2. Source is a question (not start/end)
        // 3. Source is NOT a sub-question (sub-questions can have multiple outputs)
        // 4. Target is NOT a sub-question (connecting TO a sub-question is allowed - they can have multiple inputs)
        // 5. The edge being updated is NOT a conditional edge (conditional edges are managed separately)
        if (targetChanged && sourceIsQuestion && !sourceIsSubQuestion && !targetIsSubQuestion && !isConditionalEdge(oldEdge)) {
          // Find all existing non-conditional output edges from this source (excluding the one we just updated)
          const existingOutputEdges = updated.filter(
            (e) => e.source === sourceId && 
                   !isConditionalEdge(e) && 
                   e.id !== actualOldEdge.id // Exclude the edge we're updating
          );
          
          // If there are other output edges, remove them to enforce single output rule
          if (existingOutputEdges.length > 0) {
            console.log('🔍 Removing existing output edges after edge update to enforce single output rule', {
              source: sourceId,
              existingCount: existingOutputEdges.length,
              oldTarget: oldTargetId,
              newTarget: targetId
            });
            
            updated = updated.filter(
              (e) => !(e.source === sourceId && !isConditionalEdge(e) && e.id !== actualOldEdge.id)
            );
          }
        }
        
        // If we reversed the edge, we need to reverse it back
        let finalEdge = updated.find((e) => e.id === actualOldEdge.id);
        if (updateMode === 'source' && finalEdge && actualConnection.target && actualConnection.source) {
          // Reverse the edge back: swap source and target, and swap handles
          finalEdge.source = actualConnection.target;
          finalEdge.target = actualConnection.source;
          finalEdge.sourceHandle = actualConnection.targetHandle;
          finalEdge.targetHandle = actualConnection.sourceHandle;
        }
        
        // Ensure the edge has the correct handles and is reconnectable
        if (finalEdge) {
          // Preserve the handles from actualConnection to ensure proper snapping
          if (actualConnection.sourceHandle !== undefined) {
            finalEdge.sourceHandle = actualConnection.sourceHandle;
          }
          if (actualConnection.targetHandle !== undefined) {
            finalEdge.targetHandle = actualConnection.targetHandle;
          }
          // Preserve reconnectable status - conditional edges should remain non-reconnectable
          if (isConditionalEdge(finalEdge)) {
            finalEdge.reconnectable = false; // Keep conditional edges non-reconnectable
          } else {
            finalEdge.reconnectable = true;
          }
          
          // Rebuild label from source question's non-conditional options (for non-conditional edges only)
          if (!isConditionalEdge(finalEdge) && finalEdge.source) {
            const rebuiltLabel = rebuildEdgeLabelFromQuestion(finalEdge.source, nodes, updated);
            if (rebuiltLabel) {
              finalEdge.label = rebuiltLabel;
            } else {
              // Remove label if no non-conditional options
              delete finalEdge.label;
            }
          }
        }
        
        // For auto-linking logic, use the original edge direction
        const oldTarget = oldEdge.target;
        const newTarget = updateMode === 'source' ? actualConnection.source : actualConnection.target;
        if (!newTarget) {
          return eds;
        }
        const endNode = nodes.find((n) => n.id === 'end');
        
        if (!endNode) return updated;
        
        const oldTargetNode = nodes.find((n) => n.id === oldTarget);
        const newTargetNode = nodes.find((n) => n.id === newTarget);
        
        // Only auto-link old target if the edge being moved wasn't the auto-linked one
        const isAutoLinkedEdge = oldEdge.data?.autoLinked === true;
        if (oldTargetNode && oldTarget !== 'end' && !isEndNode(oldTargetNode) && !isAutoLinkedEdge) {
          const hasPathToEnd = updated.some((e) => {
            if (e.source === oldTarget && e.target === 'end') return true;
            const reachable = new Set<string>();
            const dfs = (nodeId: string) => {
              if (reachable.has(nodeId)) return;
              reachable.add(nodeId);
              updated.forEach((edge) => {
                if (edge.source === nodeId && !reachable.has(edge.target)) {
                  dfs(edge.target);
                }
              });
            };
            dfs(oldTarget);
            return reachable.has('end');
          });
          
          if (!hasPathToEnd) {
            // Rebuild label from old target question's non-conditional options
            const autoLinkLabel = rebuildEdgeLabelFromQuestion(oldTarget, nodes, updated);
            const autoLinkEdge: Edge = {
              id: `edge-${oldTarget}-end`,
              source: oldTarget,
              sourceHandle: 'bottom-output', // Use bottom handle as default
              target: 'end',
              type: oldEdge.type || connectionLineType,
              data: { linear: true, autoLinked: true },
              reconnectable: true,
              ...(autoLinkLabel ? { label: autoLinkLabel } : {}),
            };
            updated.push(autoLinkEdge);
          }
        }
        
        // Auto-link new target to 'end' using bottom output handle (default)
        // Only if it doesn't already have a path to 'end'
        if (newTargetNode && newTarget !== 'end' && !isEndNode(newTargetNode)) {
          const existingEdgeToEnd = updated.find((e) => e.source === newTarget && e.target === 'end');
          if (!existingEdgeToEnd) {
            // Check if there's any path to 'end' through other nodes
            // Use a more robust DFS that checks all possible paths
            const hasPathToEnd = (() => {
              const visited = new Set<string>();
              const dfs = (nodeId: string): boolean => {
                // If we've reached 'end', we found a path
                if (nodeId === 'end') {
                  return true;
                }
                // If we've already visited this node, skip (avoid cycles)
                if (visited.has(nodeId)) {
                  return false;
                }
                visited.add(nodeId);
                
                // Check all outgoing edges from this node
                const outgoingEdges = updated.filter((e) => e.source === nodeId);
                for (const edge of outgoingEdges) {
                  // Recursively check if the target node can reach 'end'
                  if (dfs(edge.target)) {
                    return true;
                  }
                }
                return false;
              };
              return dfs(newTarget);
            })();
            
            // Only create auto-link if there's NO path to 'end' at all
            if (!hasPathToEnd && newTarget) {
              // Auto-link using bottom output handle (default)
            // Rebuild label from new target question's non-conditional options
            const autoLinkLabel = rebuildEdgeLabelFromQuestion(newTarget, nodes, updated);
            const autoLinkEdge: Edge = {
              id: `edge-${newTarget}-end`,
              source: newTarget,
              sourceHandle: 'bottom-output', // Use bottom handle as default
              target: 'end',
              type: oldEdge.type || connectionLineType,
              data: { linear: true, autoLinked: true },
              reconnectable: true,
              ...(autoLinkLabel ? { label: autoLinkLabel } : {}),
            };
            updated.push(autoLinkEdge);
          }
        }
        }
        
        // Feature 9: Trigger on edge update
        setTimeout(() => {
          setEdges((currentEdges) => {
            const reconnectedEdges = ensureStartToDoneConnection(nodes, currentEdges);
            setNodes((currentNodes) => {
              const { nodes: renumberedNodes, edges: renumberedEdges } = renumberQuestionsInFlow(currentNodes, reconnectedEdges);
              setEdges(renumberedEdges);
              return renumberedNodes;
            });
            return reconnectedEdges;
          });
        }, 0);
        
        return updated;
      });
    },
    [setEdges, setNodes, nodes, connectionLineType, edges, isNodeReachableFromStart]
  );
  
  const onEdgeUpdateEnd = useCallback((_event: MouseEvent | TouchEvent, _edge: Edge) => {
    // Edge update ended - user finished dragging an edge endpoint
    console.log('🟣 EDGE UPDATE END - ReactFlow onEdgeUpdateEnd called', {
      edgeId: _edge?.id
    });
    isConnectingRef.current = false;
    // Clear the update mode for this edge
    if (_edge?.id) {
      delete edgeUpdateModeRef[_edge.id];
    }
  }, []);
  
  // Update refs when callbacks change (must be after callbacks are defined)
  useEffect(() => {
    onEdgeUpdateRef.current = onEdgeUpdate;
    onEdgeUpdateEndRef.current = onEdgeUpdateEnd;
  }, [onEdgeUpdate, onEdgeUpdateEnd, isNodeReachableFromStart]);

  // Helper to extract Q number from label or generate one
  const extractOrGenerateQNumber = (nodeId: string, currentLabel?: string): string => {
    if (currentLabel) {
      const match = currentLabel.match(/^Q(\d+(?:\.\d+)?):/);
      if (match) {
        return `Q${match[1]}`;
      }
    }
    // Generate from nodeId if it's numeric, otherwise use a default
    const numericId = nodeId.match(/^\d+$/);
    return numericId ? `Q${nodeId}` : `Q${nodeId}`;
  };

  // Handler to update node label when edited inline - preserves options and metadata
  const handleNodeLabelUpdate = useCallback((nodeId: string, newLabel: string) => {
    setNodes((nds) => {
      const node = nds.find((n) => n.id === nodeId);
      if (!node) return nds;

      const currentLabel = node.data?.label || '';
      const trimmedNewLabel = newLabel.trim();
      
      // If the new label already includes a Q prefix (e.g., "Q3: Question 3"), use it as-is
      // This preserves the full label when the user doesn't change it
      if (trimmedNewLabel.match(/^Q\d+(?:\.\d+)?:/)) {
        // Extract preserved metadata from current label (options, scale, etc.)
        const currentLines = currentLabel.split('\n');
        const optionLine = currentLines.find((line: string) => 
          line.trim().toLowerCase().startsWith('options:') || 
          line.trim().toLowerCase().startsWith('choices:')
        );
        const scaleLine = currentLines.find((line: string) => 
          line.trim().toLowerCase().startsWith('scale:')
        );
        
        // Check if the new label already includes metadata lines
        const newLines = trimmedNewLabel.split('\n');
        const hasOptionsInNew = newLines.some(line => 
          line.trim().toLowerCase().startsWith('options:') || 
          line.trim().toLowerCase().startsWith('choices:')
        );
        const hasScaleInNew = newLines.some(line => 
          line.trim().toLowerCase().startsWith('scale:')
        );
        
        // Build the final label, preserving metadata from current if not in new
        const labelParts = [trimmedNewLabel.split('\n')[0]]; // First line with Q prefix
        if (hasScaleInNew) {
          labelParts.push(...newLines.filter(line => line.trim().toLowerCase().startsWith('scale:')));
        } else if (scaleLine) {
          labelParts.push(scaleLine);
        }
        if (hasOptionsInNew) {
          labelParts.push(...newLines.filter(line => 
            line.trim().toLowerCase().startsWith('options:') || 
            line.trim().toLowerCase().startsWith('choices:')
          ));
        } else if (optionLine) {
          labelParts.push(optionLine);
        }
        
        const updatedLabel = labelParts.join('\n');
        return nds.map((n) =>
          n.id === nodeId
            ? { ...n, data: { ...n.data, label: updatedLabel } }
            : n
        );
      }
      
      // If the new label doesn't have a Q prefix, preserve the existing Q prefix and append the new text
      // Extract preserved metadata from current label
      const lines = currentLabel.split('\n');
      const optionLine = lines.find((line: string) => 
        line.trim().toLowerCase().startsWith('options:') || 
        line.trim().toLowerCase().startsWith('choices:')
      );
      const scaleLine = lines.find((line: string) => 
        line.trim().toLowerCase().startsWith('scale:')
      );
      
      // Parse the new label to extract question text
      const newLines = trimmedNewLabel.split('\n');
      let questionText = '';
      
      // Check if first line is just a number (like "3") - if so, preserve original question text
      const firstLine = newLines[0]?.trim() || '';
      const isJustNumber = /^\d+$/.test(firstLine);
      
      // Extract Q number prefix - use the number the user typed if it's just a number,
      // otherwise extract from current label
      let qNumberPrefix: string;
      if (isJustNumber) {
        // User typed just a number - use it for the Q prefix (autocomplete feature)
        qNumberPrefix = `Q${firstLine}`;
        // Preserve the original question text
        const originalQuestion = parseQuestionText(currentLabel);
        questionText = originalQuestion || `Question ${nodeId}`;
      } else {
        // Extract Q number prefix from current label
        qNumberPrefix = extractOrGenerateQNumber(nodeId, currentLabel);
        // Try to find question text in the new label
        // Check if first line is question text (no metadata)
        if (firstLine && 
            !firstLine.toLowerCase().startsWith('options:') && 
            !firstLine.toLowerCase().startsWith('choices:') && 
            !firstLine.toLowerCase().startsWith('scale:')) {
          questionText = firstLine;
        } else {
          // Fallback: use the first non-metadata line
          questionText = newLines.find((line: string) => {
            const trimmed = line.trim().toLowerCase();
            return trimmed && 
                   !trimmed.startsWith('options:') && 
                   !trimmed.startsWith('choices:') && 
                   !trimmed.startsWith('scale:');
          }) || '';
        }
      }
      
      // If no question text found, preserve the original
      if (!questionText) {
        const originalQuestion = parseQuestionText(currentLabel);
        questionText = originalQuestion || `Question ${nodeId}`;
      }
      
      // Rebuild label with preserved metadata
      const labelParts: string[] = [`${qNumberPrefix}: ${questionText}`];
      if (scaleLine) {
        labelParts.push(scaleLine);
      }
      if (optionLine) {
        labelParts.push(optionLine);
      }
      
      const updatedLabel = labelParts.join('\n');
      
      return nds.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, label: updatedLabel } }
          : n
      );
    });
  }, [setNodes]);

  // Handler to remove an option from a question (manual removal)
  const handleRemoveOptionFromQuestion = useCallback(
    (nodeId: string, option: string) => {
      console.log('🔴 [PATH: handleRemoveOptionFromQuestion] Entry:', { 
        nodeId, 
        option,
        timestamp: new Date().toISOString(),
        stackTrace: new Error().stack
      });
      console.log('🔴 [PATH: handleRemoveOptionFromQuestion] Calling removeConditionalBranch:', { nodeId, option });
      removeConditionalBranch(nodeId, option);
      console.log('🔴 [PATH: handleRemoveOptionFromQuestion] removeConditionalBranch call completed');
      toast.success(`Removed option "${option}"`);
    },
    [removeConditionalBranch]
  );

  // Reconnect a conditional edge for an orphaned sub-question
  const handleReconnectConditionalEdge = useCallback(
    (parentNodeId: string, option: string) => {
      // Find the orphaned sub-question for this parent and option
      const orphanedSubQuestion = nodes.find(n => {
        if (n.id === 'start' || n.id === 'end') return false;
        if (n.type !== 'question') return false;
        if (!n.id.includes('.')) return false; // Must be a sub-question
        const parentSourceId = (n.data as any)?.parentConditionalSourceId;
        const parentOption = (n.data as any)?.parentConditionalOption;
        return parentSourceId === parentNodeId && parentOption === option;
      });

      if (!orphanedSubQuestion) {
        toast.error(`No orphaned sub-question found for option "${option}"`);
        return;
      }

      // Check if edge already exists
      const existingEdge = edges.find(
        e => e.source === parentNodeId && 
             e.target === orphanedSubQuestion.id &&
             isConditionalEdge(e)
      );

      if (existingEdge) {
        toast(`Conditional edge already exists for option "${option}"`, { icon: 'ℹ️' });
        return;
      }

      // Create the conditional edge (non-reconnectable - managed automatically)
      const conditionalEdge: Edge = {
        id: `edge-${parentNodeId}-${orphanedSubQuestion.id}`,
        source: parentNodeId,
        sourceHandle: 'right-output',
        target: orphanedSubQuestion.id,
        targetHandle: 'left-input',
        type: 'smoothstep',
        animated: true,
        label: option,
        style: CONDITIONAL_EDGE_STYLE,
        data: { 
          conditionalOption: option,
          linkedFromOption: { sourceId: parentNodeId, option },
        },
        reconnectable: false, // Prevent manual reconnection - managed automatically
      };

      setEdges((eds) => [...eds, conditionalEdge]);
      toast.success(`Reconnected conditional edge for option "${option}"`);
    },
    [nodes, edges, setEdges]
  );

  // Reconnect all orphaned sub-questions for a parent node
  const handleReconnectAllOrphanedSubQuestions = useCallback(
    (parentNodeId: string) => {
      const parentNode = nodes.find(n => n.id === parentNodeId);
      if (!parentNode) {
        toast.error('Parent node not found');
        return;
      }

      // Get all options for this parent
      const options = getNodeOptions(parentNode);
      let reconnectedCount = 0;

      // For each option, check if there's an orphaned sub-question
      options.forEach(option => {
        const orphanedSubQuestion = nodes.find(n => {
          if (n.id === 'start' || n.id === 'end') return false;
          if (n.type !== 'question') return false;
          if (!n.id.includes('.')) return false;
          const parentSourceId = (n.data as any)?.parentConditionalSourceId;
          const parentOption = (n.data as any)?.parentConditionalOption;
          return parentSourceId === parentNodeId && parentOption === option;
        });

        if (!orphanedSubQuestion) return;

        // Check if edge already exists
        const existingEdge = edges.find(
          e => e.source === parentNodeId && 
               e.target === orphanedSubQuestion.id &&
               isConditionalEdge(e)
        );

        if (existingEdge) return; // Already connected

        // Create the conditional edge (non-reconnectable - managed automatically)
        const conditionalEdge: Edge = {
          id: `edge-${parentNodeId}-${orphanedSubQuestion.id}`,
          source: parentNodeId,
          sourceHandle: 'right-output',
          target: orphanedSubQuestion.id,
          targetHandle: 'left-input',
          type: 'smoothstep',
          animated: true,
          label: option,
          style: CONDITIONAL_EDGE_STYLE,
          data: { 
            conditionalOption: option,
            linkedFromOption: { sourceId: parentNodeId, option },
          },
          reconnectable: false, // Prevent manual reconnection - managed automatically
        };

        setEdges((eds) => [...eds, conditionalEdge]);
        reconnectedCount++;
      });

      if (reconnectedCount > 0) {
        toast.success(`Reconnected ${reconnectedCount} conditional edge${reconnectedCount > 1 ? 's' : ''}`);
      } else {
        toast('No orphaned sub-questions found to reconnect', { icon: 'ℹ️' });
      }
    },
    [nodes, edges, setEdges]
  );

  // Handler to update rating configuration
  const handleUpdateRatingConfig = useCallback((nodeId: string, format: 'numerical' | 'satisfaction', rateAmount: number) => {
    setNodes((nds) => {
      const node = nds.find((n) => n.id === nodeId);
      if (!node) return nds;

      const currentLabel = node.data?.label || '';
      const qNumberPrefix = extractOrGenerateQNumber(nodeId, currentLabel);
      const questionText = parseQuestionText(currentLabel) || `Question ${nodeId}`;
      
      // Rebuild label with updated rating config
      const newLabel = `${qNumberPrefix}: ${questionText}\nScale: ${format} (${rateAmount})`;
      
      return nds.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, label: newLabel } }
          : n
      );
    });
    toast.success(`Updated rating configuration: ${format} (${rateAmount})`);
  }, [setNodes]);

  // Handler to add rating scale to a question
  const handleAddRatingScale = useCallback((nodeId: string) => {
    setNodes((nds) => {
      const node = nds.find((n) => n.id === nodeId);
      if (!node) return nds;

      const currentLabel = node.data?.label || '';
      const qNumberPrefix = extractOrGenerateQNumber(nodeId, currentLabel);
      const questionText = parseQuestionText(currentLabel) || `Question ${nodeId}`;
      
      // Check if rating scale already exists
      if (currentLabel.toLowerCase().includes('scale:')) {
        toast.success('Rating scale already exists. Use the settings icon to modify it.');
        return nds;
      }
      
      // Add rating scale with default values
      const newLabel = `${qNumberPrefix}: ${questionText}\nScale: numerical (5)`;
      
      return nds.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, label: newLabel } }
          : n
      );
    });
    toast.success('Added rating scale configuration');
  }, [setNodes]);

  // Handler to unlink a node (remove all edges connected to it)
  const handleUnlinkNode = useCallback((nodeId: string) => {
    // Store edges snapshot BEFORE unlinking for reconnection logic
    const edgesSnapshot = edges;
    
    // Remove all edges connected to the unlinked node
    setEdges((currentEdges) => {
      const updatedEdges = currentEdges.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId
      );
      
      // Use similar logic to reconnectAfterDeletion to reconnect edges that went through the unlinked node
      // Find all edges that were connected to the unlinked node
      const incomingEdges = edgesSnapshot.filter(e => e.target === nodeId);
      const outgoingEdges = edgesSnapshot.filter(e => e.source === nodeId);
      
      const nodeMap = new Map(nodes.map(n => [n.id, n]));
      let reconnectedEdges = [...updatedEdges];
      
      // For each incoming edge, reconnect to the unlinked node's outgoing targets (same as deletion logic)
      incomingEdges.forEach(incomingEdge => {
        const sourceId = incomingEdge.source;
        
        // Find the best target to reconnect to from the unlinked node's outgoing edges
        const nonConditionalOutgoing = outgoingEdges.filter(e => !isConditionalEdge(e));
        const conditionalOutgoing = outgoingEdges.filter(e => isConditionalEdge(e));
        
        let bestTarget: string | null = null;
        let bestEdge: Edge | null = null;
        
        // First, try non-conditional edges to non-radio/checkbox questions
        for (const edge of nonConditionalOutgoing) {
          const targetNode = nodeMap.get(edge.target);
          if (targetNode && targetNode.id !== 'start' && targetNode.id !== 'end') {
            const questionType = determineQuestionType(targetNode);
            if (questionType !== 'radio' && questionType !== 'checkbox') {
              bestTarget = edge.target;
              bestEdge = edge;
              break;
            }
          } else if (targetNode && targetNode.id === 'end') {
            bestTarget = 'end';
            bestEdge = edge;
            break;
          }
        }
        
        // If no good target found, try any non-conditional edge
        if (!bestTarget && nonConditionalOutgoing.length > 0) {
          bestEdge = nonConditionalOutgoing[0];
          bestTarget = bestEdge.target;
        }
        
        // If still no target, try conditional edges (but skip radio/checkbox)
        if (!bestTarget) {
          for (const edge of conditionalOutgoing) {
            const targetNode = nodeMap.get(edge.target);
            if (targetNode && targetNode.id !== 'start' && targetNode.id !== 'end') {
              const questionType = determineQuestionType(targetNode);
              if (questionType !== 'radio' && questionType !== 'checkbox') {
                bestTarget = edge.target;
                bestEdge = edge;
                break;
              }
            }
          }
        }
        
        // If we found a target, create a new edge
        if (bestTarget && bestEdge) {
          const existingEdge = reconnectedEdges.find(
            e => e.source === sourceId && e.target === bestTarget
          );
          
          if (!existingEdge) {
            reconnectedEdges.push({
              id: `edge-${sourceId}-${bestTarget}`,
              source: sourceId,
              target: bestTarget!,
              type: bestEdge.type || 'smoothstep',
              data: { ...bestEdge.data, reconnected: true },
              reconnectable: true,
              sourceHandle: incomingEdge.sourceHandle || 'bottom-output',
              targetHandle: bestEdge.targetHandle || 'top-input',
            });
          }
        } else if (outgoingEdges.length > 0) {
          // No good target found, but we have outgoing edges - connect to the first one
          const firstOutgoing = outgoingEdges[0];
          const existingEdge = reconnectedEdges.find(
            e => e.source === sourceId && e.target === firstOutgoing.target
          );
          
          if (!existingEdge) {
            reconnectedEdges.push({
              id: `edge-${sourceId}-${firstOutgoing.target}`,
              source: sourceId,
              target: firstOutgoing.target,
              type: firstOutgoing.type || 'smoothstep',
              data: { ...firstOutgoing.data, reconnected: true },
              reconnectable: true,
              sourceHandle: incomingEdge.sourceHandle || 'bottom-output',
              targetHandle: firstOutgoing.targetHandle || 'top-input',
            });
          }
        } else {
          // No outgoing edges from unlinked node - connect directly to end
          const existingEdge = reconnectedEdges.find(
            e => e.source === sourceId && e.target === 'end'
          );
          
          if (!existingEdge) {
            reconnectedEdges.push({
              id: `edge-${sourceId}-end`,
              source: sourceId,
              target: 'end',
              type: 'smoothstep',
              data: { linear: true, reconnected: true },
              reconnectable: true,
              sourceHandle: incomingEdge.sourceHandle || 'bottom-output',
              targetHandle: 'top-input',
            });
          }
        }
      });
      
      // Then ensure start-to-done connection (same as deletion logic)
      const finalEdges = ensureStartToDoneConnection(nodes, reconnectedEdges);
      
      return finalEdges;
    });
    
    // Renumber questions (same as deletion
    setTimeout(() => {
      setNodes((currentNodes) => {
        setEdges((currentEdges) => {
          const { nodes: renumberedNodes, edges: renumberedEdges } = renumberQuestionsInFlow(currentNodes, currentEdges);
          setNodes(renumberedNodes);
          return renumberedEdges;
        });
        return currentNodes;
      });
    }, 0);
    
    toast.success('Question unlinked from all connections');
  }, [setEdges, setNodes, nodes, edges]);

  // Handler to toggle optional/required status
  const handleToggleConditionalMode = useCallback((nodeId: string, enabled: boolean) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, createConditionalQuestions: enabled } }
          : n
      )
    );
    toast.success(`Conditional questions ${enabled ? 'enabled' : 'disabled'} for all options`);
  }, [setNodes]);

  const handleToggleOptional = useCallback((nodeId: string, isOptional: boolean) => {
    setNodes((nds) => {
      const node = nds.find((n) => n.id === nodeId);
      if (!node) return nds;

      const currentLabel = node.data?.label || '';
      const qNumberPrefix = extractOrGenerateQNumber(nodeId, currentLabel);
      let questionText = parseQuestionText(currentLabel) || `Question ${nodeId}`;
      
      // Remove existing [Optional] tag if present
      questionText = questionText.replace(/\s*\[Optional\]/gi, '').trim();
      
      // Add [Optional] tag if making it optional
      if (isOptional) {
        questionText = `${questionText} [Optional]`;
      }
      
      // Rebuild label preserving other metadata
      const lines = currentLabel.split('\n');
      const scaleLine = lines.find((line: string) => 
        line.trim().toLowerCase().startsWith('scale:')
      );
      const optionLine = lines.find((line: string) => 
        line.trim().toLowerCase().startsWith('options:') || 
        line.trim().toLowerCase().startsWith('choices:')
      );
      
      const labelParts: string[] = [`${qNumberPrefix}: ${questionText}`];
      if (scaleLine) {
        labelParts.push(scaleLine);
      }
      if (optionLine) {
        labelParts.push(optionLine);
      }
      
      const newLabel = labelParts.join('\n');
      
      return nds.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, label: newLabel, optional: isOptional } }
          : n
      );
    });
    toast.success(`Question marked as ${isOptional ? 'optional' : 'required'}`);
  }, [setNodes]);

  // Handler to add an option to a question and optionally auto-generate condition nodes
  const handleAddOptionToQuestion = useCallback((nodeId: string, option: string, type: 'options' | 'choices' = 'options', createConditional: boolean = false) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    // Get existing options
    const existingOptions = getNodeOptions(node);
    if (existingOptions.includes(option)) {
      toast.error('Option already exists');
      return;
    }

    // CAPTURE parent's connection target BEFORE any edges are modified
    // This ensures sub-questions connect to the same target as the parent's main path (e.g., Q2)
    // Strategy: 
    // 1. First check if parent node has stored original target (from when first sub-question was created)
    // 2. If not, check current main path edge
    // 3. If still not found, check if any existing sub-questions from this parent have a connection
    // 4. Default to 'end' if nothing found
    const parentMainPathTarget = (() => {
      // First, check if parent has stored the original target (set when first sub-question was created)
      const storedTarget = node.data?.originalMainPathTarget as string | undefined;
      if (storedTarget) {
        console.log(`📌 Using stored original main path target for ${nodeId}: ${storedTarget}`);
        return storedTarget;
      }
      
      // Second, check current main path edge
      const parentNonConditionalEdges = edges.filter(
        e => e.source === nodeId && !isConditionalEdge(e)
      );
      if (parentNonConditionalEdges.length > 0) {
        let target = parentNonConditionalEdges[0].target;
        // If it's a sub-question, traverse through it to find the next main question or end
        const findNextMainQuestion = (targetId: string, visited: Set<string> = new Set()): string => {
          if (visited.has(targetId) || targetId === 'end' || targetId === 'done') {
            return targetId;
          }
          visited.add(targetId);
          
          // If it's a sub-question, find where it connects to
          if (targetId.includes('.') && targetId.match(/^\d+\.\d+$/)) {
            const subQuestionOutgoing = edges.filter(
              e => e.source === targetId && !isConditionalEdge(e)
            );
            if (subQuestionOutgoing.length > 0) {
              return findNextMainQuestion(subQuestionOutgoing[0].target, visited);
            }
            return 'end';
          }
          
          // It's a main question or end, return it
          return targetId;
        };
        const resolvedTarget = findNextMainQuestion(target);
        console.log(`📌 Found current main path target for ${nodeId}: ${resolvedTarget}`);
        return resolvedTarget;
      }
      
      // If no non-conditional edge exists yet, check if there's any edge to a main question
      // This handles the case where the parent question might be connected to another question
      const parentAllEdges = edges.filter(e => e.source === nodeId);
      for (const edge of parentAllEdges) {
        const targetNode = nodes.find(n => n.id === edge.target);
        if (targetNode && edge.target !== 'end' && edge.target !== 'done' && !edge.target.includes('.')) {
          // Found a connection to a main question - use that
          console.log(`📌 Found connection to main question ${edge.target} for ${nodeId}`);
          return edge.target;
        }
      }
      
      // Third, check if any existing sub-questions from this parent have a connection
      // Extract parent question number from nodeId or label
      const parentLabel = node.data?.label || '';
      const parentLabelMatch = parentLabel.match(/^Q(\d+)(?:\.\d+)?:/);
      const parentQuestionNumber = parentLabelMatch ? parentLabelMatch[1] : null;
      
      if (parentQuestionNumber) {
        // Find existing sub-questions for this parent
        const existingSubQuestions = nodes.filter(n => {
          if (n.id === 'start' || n.id === 'end') return false;
          if (n.type !== 'question') return false;
          if (!n.id.includes('.')) return false;
          const subParentMatch = n.id.match(/^(\d+)\.\d+$/);
          if (!subParentMatch) return false;
          return subParentMatch[1] === parentQuestionNumber;
        });
        
        // Check if any existing sub-question has a connection to a main question (not 'end')
        for (const subQuestion of existingSubQuestions) {
          const subQuestionOutgoing = edges.filter(
            e => e.source === subQuestion.id && !isConditionalEdge(e)
          );
          if (subQuestionOutgoing.length > 0) {
            const target = subQuestionOutgoing[0].target;
            // Only use if it's not 'end' (we want to connect to a real question)
            if (target !== 'end' && target !== 'done') {
              console.log(`📌 Found existing sub-question ${subQuestion.id} connected to ${target}, using that for ${nodeId}`);
              return target;
            }
          }
        }
      }
      
      return 'end'; // Default to end if no main path exists
    })();

    // Determine prefix based on type
    const prefix = type === 'choices' ? 'Choices' : 'Options';

    // Update question label to include the new option
    const currentLabel = node.data?.label || '';
    const optionLine = currentLabel.split('\n').find((line: string) => 
      line.trim().toLowerCase().startsWith('options:') || 
      line.trim().toLowerCase().startsWith('choices:')
    );
    
    let newLabel = currentLabel;
    if (optionLine) {
      // Update existing options line - preserve the existing type if it matches, otherwise replace
      const existingPrefix = optionLine.trim().toLowerCase().startsWith('choices:') ? 'Choices' : 'Options';
      const finalPrefix = existingPrefix === prefix ? existingPrefix : prefix; // Use existing if same type, otherwise use new type
      const updatedOptions = [...existingOptions, option];
      newLabel = currentLabel.replace(
        optionLine,
        `${finalPrefix}: ${updatedOptions.join(', ')}`
      );
    } else {
      // Add new options line
      const updatedOptions = [...existingOptions, option];
      newLabel = currentLabel ? `${currentLabel}\n${prefix}: ${updatedOptions.join(', ')}` : `${prefix}: ${updatedOptions.join(', ')}`;
    }

    // If this is the first option, store the conditional mode and question type in node data
    const isFirstOption = existingOptions.length === 0;
    const conditionalMode = isFirstOption ? createConditional : (node.data?.createConditionalQuestions === true);

    // Update the node label and store conditional mode and question type
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId
          ? { 
              ...n, 
              data: { 
                ...n.data, 
                label: newLabel,
                // Store conditional mode and question type when adding first option
                ...(isFirstOption ? { 
                  createConditionalQuestions: createConditional,
                  questionType: type // Store 'options' or 'choices'
                } : {})
              } 
            }
          : n
      )
    );

    // If this is the first option and it's conditional, check if we need to remove existing main path edges
    // This handles the case when a question changes from textfield to radio/checkbox with all conditional options
    if (isFirstOption && createConditional) {
      setEdges((eds) => {
        // Check if there are existing non-conditional edges from this question
        const existingNonConditionalEdges = eds.filter(
          e => e.source === nodeId && !isConditionalEdge(e)
        );
        
        if (existingNonConditionalEdges.length > 0) {
          console.log(`🗑️ Question ${nodeId} changed to radio/checkbox with conditional option, removing ${existingNonConditionalEdges.length} existing main path edge(s)`);
          const filteredEdges = eds.filter(e => !existingNonConditionalEdges.some(edge => edge.id === e.id));
          return filteredEdges;
        }
        
        return eds;
      });
    }

    // Update edge labels for non-conditional options, even if conditional mode is disabled
    if (!conditionalMode) {
      // Update the regular edge label with all non-conditional options
      setEdges((eds) => {
        const allOptions = [...existingOptions, option]; // Include the newly added option
        const conditionalOptions = new Set(
          eds
            .filter(e => e.source === nodeId && isConditionalEdge(e))
            .map(e => e.data?.conditionalOption as string)
            .filter(Boolean)
        );
        const nonConditionalOptions = allOptions.filter(opt => !conditionalOptions.has(opt));
        
        // Find existing non-conditional edge
        const regularEdgeIndex = eds.findIndex(e => 
          e.source === nodeId && 
          !isConditionalEdge(e)
        );
        
        if (regularEdgeIndex >= 0 && nonConditionalOptions.length > 0) {
          // Update existing regular edge label
          const updatedEdges = [...eds];
          updatedEdges[regularEdgeIndex] = {
            ...updatedEdges[regularEdgeIndex],
            label: nonConditionalOptions.join(', '),
          };
          return updatedEdges;
        } else if (regularEdgeIndex < 0 && nonConditionalOptions.length > 0) {
          // Create new regular edge if it doesn't exist
          // Find the next question in the flow or use "end"
          const outgoingEdges = eds.filter(e => e.source === nodeId);
          const targetIds = outgoingEdges.map(e => e.target).filter(id => id !== 'start');
          
          let nextTarget = 'end';
          if (targetIds.length > 0) {
            const nextQuestionNode = nodes.find(n => 
              !n.id.includes('.') && 
              n.id !== nodeId && 
              n.id !== 'start' && 
              n.id !== 'end' &&
              !targetIds.includes(n.id)
            );
            if (nextQuestionNode) {
              nextTarget = nextQuestionNode.id;
            }
          }
          
          const regularEdge: Edge = {
            id: `edge-${nodeId}-${nextTarget}`,
            source: nodeId,
            sourceHandle: 'bottom-output',
            target: nextTarget,
            targetHandle: nextTarget === 'end' ? 'top-input' : 'top-input',
            type: 'smoothstep',
            label: nonConditionalOptions.join(', '),
            reconnectable: true,
          };
          return [...eds, regularEdge];
        }
        
        return eds;
      });
      
      toast.success(`Added option "${option}"`);
      return;
    }

    // Auto-generate linked question for the new option
    const nodePosition = node.position;
    const offsetX = 300;
    const offsetY = 150;
    const optionIndex = existingOptions.length;

    // Extract question number from parent node's label (e.g., "Q1: ..." -> "1")
    // This ensures sub-questions are numbered correctly (Q1.1, Q1.2) even if nodeId doesn't match
    const parentLabel = node.data?.label || '';
    const parentLabelMatch = parentLabel.match(/^Q(\d+)(?:\.\d+)?:/);
    const parentQuestionNumber = parentLabelMatch ? parentLabelMatch[1] : nodeId;
    
    // Count existing sub-questions for this parent to determine the next sub-number
    const existingSubQuestions = nodes.filter(n => {
      if (n.id === 'start' || n.id === 'end') return false;
      if (n.type !== 'question') return false;
      if (!n.id.includes('.')) return false;
      // Check if this sub-question belongs to the same parent
      const subParentMatch = n.id.match(/^(\d+)\.\d+$/);
      if (!subParentMatch) return false;
      // Match by parent's question number from label, not nodeId
      const subParentLabel = n.data?.label || '';
      const subParentLabelMatch = subParentLabel.match(/^Q(\d+)(?:\.\d+)?:/);
      const subParentQNumber = subParentLabelMatch ? subParentLabelMatch[1] : subParentMatch[1];
      return subParentQNumber === parentQuestionNumber;
    });
    
    const nextSubNumber = existingSubQuestions.length + 1;
    const questionNodeId = `${parentQuestionNumber}.${nextSubNumber}`;
    
    // Generate a unique ID that links the option, conditional edge, and sub-question
    const conditionalBranchId = `branch-${nodeId}-${option}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const questionNode: Node = {
      id: questionNodeId,
      type: 'question',
      data: { 
        label: `Q${questionNodeId}: Response for "${option}"`,
        parentConditionalSourceId: nodeId,
        parentConditionalOption: option,
        autoGeneratedConditional: true,
        conditionalBranchId: conditionalBranchId, // Unique ID linking option, edge, and sub-question
      },
      position: {
        x: nodePosition.x + offsetX,
        y: nodePosition.y + (optionIndex * offsetY),
      },
      style: BASE_NODE_STYLE,
    };

    // Create edge directly from source to question (non-reconnectable - managed automatically)
    const sourceToQuestionEdge: Edge = {
      id: `edge-${nodeId}-${questionNodeId}`,
      source: nodeId,
      sourceHandle: 'right-output',
      target: questionNodeId,
      targetHandle: 'left-input',
      type: 'smoothstep',
      animated: true,
      label: option, // Label with the option value
      style: CONDITIONAL_EDGE_STYLE,
      data: { 
        conditionalOption: option,
        linkedFromOption: { sourceId: nodeId, option },
        conditionalBranchId: conditionalBranchId, // Unique ID linking option, edge, and sub-question
      },
      reconnectable: false, // Prevent manual reconnection - managed automatically
    };

    // For non-conditional options, ensure there's a regular edge to the next question or Done
    // This handles cases where some options have conditionals and others don't
    setEdges((eds) => {
      const updatedEdges = [...eds, sourceToQuestionEdge];
      
      // Find existing non-conditional edges from this source node
      const existingNonConditionalEdges = eds.filter(
        (e) => e.source === nodeId && !isConditionalEdge(e)
      );
      
      // Collect all options that don't have conditional questions
      const allOptions = getNodeOptions(node);
      const conditionalOptions = new Set(
        eds
          .filter(e => e.source === nodeId && isConditionalEdge(e))
          .map(e => e.data?.conditionalOption as string)
          .filter(Boolean)
      );
      const nonConditionalOptions = allOptions.filter(opt => !conditionalOptions.has(opt));
      
      // If there's no regular edge to the next question/Done, create one
      // This allows non-conditional options to flow naturally to the next question
      // BUT: Only create if there are non-conditional options (not all options are conditional)
      if (existingNonConditionalEdges.length === 0 && nonConditionalOptions.length > 0 && !allOptionsAreConditional(nodeId, nodes, updatedEdges)) {
        // Find the next question in the flow or use "end"
        const outgoingEdges = eds.filter(e => e.source === nodeId);
        const targetIds = outgoingEdges.map(e => e.target).filter(id => id !== 'start' && id !== questionNodeId);
        
        // Try to find a target that's not a conditional question
        let nextTarget = 'end';
        if (targetIds.length > 0) {
          // Use the first non-conditional target, or find the next question node
          const nextQuestionNode = nodes.find(n => 
            !n.id.includes('.') && 
            n.id !== nodeId && 
            n.id !== 'start' && 
            n.id !== 'end' &&
            !targetIds.includes(n.id)
          );
          if (nextQuestionNode) {
            nextTarget = nextQuestionNode.id;
          }
        }
        
        // Create a regular edge for non-conditional options, labeled with the options
        const regularEdge: Edge = {
          id: `edge-${nodeId}-${nextTarget}`,
          source: nodeId,
          sourceHandle: 'bottom-output',
          target: nextTarget,
          targetHandle: nextTarget === 'end' ? 'top-input' : 'top-input',
          type: 'smoothstep',
          label: nonConditionalOptions.join(', '), // Label with non-conditional options
          reconnectable: true,
        };
        updatedEdges.push(regularEdge);
      } else if (existingNonConditionalEdges.length > 0) {
        // Check if all options are now conditional - if so, remove the main path edge
        if (allOptionsAreConditional(nodeId, nodes, updatedEdges)) {
          console.log(`🗑️ All options are conditional for ${nodeId}, removing main path edge(s)`);
          // Before removing, STORE the target in the parent node's data so future sub-questions can use it
          const edgeToRemove = existingNonConditionalEdges[0];
          if (edgeToRemove && !node.data?.originalMainPathTarget) {
            // Store the original target - we'll update the node after this setEdges call
            const targetToStore = edgeToRemove.target;
            console.log(`💾 Will store original main path target for ${nodeId}: ${targetToStore}`);
            // Use setTimeout to update node after edges are updated
            setTimeout(() => {
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === nodeId
                    ? { ...n, data: { ...n.data, originalMainPathTarget: targetToStore } }
                    : n
                )
              );
            }, 0);
          }
          
          // Remove all non-conditional edges from this question
          const edgesToRemove = updatedEdges.filter(e => 
            e.source === nodeId && !isConditionalEdge(e)
          );
          edgesToRemove.forEach(edge => {
            const index = updatedEdges.findIndex(e => e.id === edge.id);
            if (index >= 0) {
              updatedEdges.splice(index, 1);
              console.log(`🗑️ Removed main path edge: ${edge.id} from ${edge.source} to ${edge.target}`);
            }
          });
        } else if (nonConditionalOptions.length > 0) {
          // Update existing regular edge label with all non-conditional options
          const regularEdgeIndex = updatedEdges.findIndex(e => 
            e.source === nodeId && 
            !isConditionalEdge(e) &&
            e.id !== sourceToQuestionEdge.id
          );
          if (regularEdgeIndex >= 0) {
            updatedEdges[regularEdgeIndex] = {
              ...updatedEdges[regularEdgeIndex],
              label: nonConditionalOptions.join(', '), // Update label with all non-conditional options
            };
          }
        }
      }
      
      // Auto-connect sub-question to the same target as parent's main path (captured above)
      // This ensures sub-questions connect to Q2 (or whatever the parent was connected to) instead of defaulting to Done
      // Do this in the SAME setEdges call to ensure we're working with the updated edges state
      const nextTarget = parentMainPathTarget;
      
      console.log(`🔗 Auto-connecting sub-question ${questionNodeId} to parent's main path target: ${nextTarget}`);
      
      // Check if edge already exists (check in updatedEdges, not eds)
      const existingEdge = updatedEdges.find(
        e => e.source === questionNodeId && e.target === nextTarget
      );
      
      if (!existingEdge) {
        // Create edge from sub-question to next question or Done
        const subQuestionToNextEdge: Edge = {
          id: `edge-${questionNodeId}-${nextTarget}`,
          source: questionNodeId,
          sourceHandle: 'bottom-output',
          target: nextTarget,
          targetHandle: nextTarget === 'end' || nextTarget === 'done' ? 'top-input' : 'top-input',
          type: 'smoothstep',
          reconnectable: true,
          data: { linear: true },
        };
        
        updatedEdges.push(subQuestionToNextEdge);
        console.log(`✅ Added edge from ${questionNodeId} to ${nextTarget}`);
      } else {
        console.log(`⚠️ Edge from ${questionNodeId} to ${nextTarget} already exists, skipping`);
      }
      
      return updatedEdges;
    });

    // Add nodes FIRST, then ensure edges are created
    // Use setTimeout to ensure node is in state before creating edge connections
    setNodes((nds) => [...nds, questionNode]);
    setNodeIdCounter((prev) => prev + 1);
    
    // Note: The edge connection is already handled in the setEdges call above
    // This ensures both the conditional edge and the sub-question connection edge are added together

    toast.success(`Added option "${option}" and generated conditional logic`);
  }, [nodes, edges, setNodes, setEdges, setNodeIdCounter]);

  // Ensure all nodes have the onUpdate callback and addOption/removeOption handlers in their data
  const nodesWithUpdateCallback = useMemo(() => {
    // Calculate if multiple nodes are selected (excluding start/end)
    const selectedNodes = nodes.filter(n => n.selected && n.id !== 'start' && n.id !== 'end');
    const isMultiSelection = selectedNodes.length > 1;
    
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        isMultiSelection: isMultiSelection && node.selected && node.id !== 'start' && node.id !== 'end',
        onUpdate: handleNodeLabelUpdate,
        onAddOption: handleAddOptionToQuestion,
        onRemoveOption: handleRemoveOptionFromQuestion,
        onUpdateRatingConfig: handleUpdateRatingConfig,
        onAddRatingScale: handleAddRatingScale,
        onToggleOptional: handleToggleOptional,
        onUnlinkNode: handleUnlinkNode,
        onReconnectConditionalEdge: handleReconnectConditionalEdge,
        onReconnectAllOrphanedSubQuestions: handleReconnectAllOrphanedSubQuestions,
        onToggleConditionalMode: handleToggleConditionalMode,
      },
    }));
  }, [nodes, handleNodeLabelUpdate, handleAddOptionToQuestion, handleRemoveOptionFromQuestion, handleUpdateRatingConfig, handleAddRatingScale, handleToggleOptional, handleToggleConditionalMode, handleUnlinkNode, handleReconnectConditionalEdge, handleReconnectAllOrphanedSubQuestions]);

  // Compute survey JSON for Menu 2 (expected JSON format)
  // Use state so we can refresh it when accordion opens
  const [surveyArrayJson, setSurveyArrayJson] = useState('[]');
  
  // Refresh JSON when accordion opens or when nodes/edges change
  useEffect(() => {
    // Always refresh JSON when nodes/edges change, regardless of accordion state
    // Use nodes/edges directly to avoid stale closure issues
    try {
      const { connectedNodes, connectedEdges } = findConnectedPathFromStartToEnd(nodes, edges);
      const surveyArray = convertFlowToSurveyFormat(connectedNodes, connectedEdges);
      setSurveyArrayJson(JSON.stringify(surveyArray, null, 2));
    } catch (error) {
      console.error('❌ Error refreshing JSON:', error);
      setSurveyArrayJson('[]');
    }
  }, [expandedAccordion, nodes, edges]);

  // Create refs to store the ReactFlow instance and container
  const reactFlowInstanceRef = useRef<any>(null);
  const reactFlowContainerRef = useRef<HTMLDivElement | null>(null);

  const addQuestionNode = (position?: { x: number; y: number }) => {
    // Determine position: use provided position, or calculate viewport center
    let nodePosition: { x: number; y: number };
    
    if (position) {
      // Use provided position (from drag)
      nodePosition = position;
    } else if (reactFlowInstanceRef.current) {
      // Calculate viewport center using React Flow's viewport
      const reactFlowInstance = reactFlowInstanceRef.current;
      const viewport = reactFlowInstance.getViewport();
      
      // Get the visible area in flow coordinates
      const viewportCenter = reactFlowInstance.screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });
      
      // Ensure the position is reasonable (not too far off)
      // If viewport is at origin, use a default position
      if (Math.abs(viewport.x) > 10000 || Math.abs(viewport.y) > 10000) {
        // Viewport is way off, use a safe default
        nodePosition = { x: BASE_X, y: 200 };
      } else {
        nodePosition = viewportCenter;
      }
    } else {
      // Fallback to default position
      nodePosition = { x: BASE_X, y: 200 };
    }
    
    // Calculate the correct Q number synchronously before adding the node
    // This prevents race conditions where user types before async renumbering completes
    const tempNewNode: Node = {
      id: nodeIdCounter.toString(),
      type: 'question',
      data: { label: `Q999: New Question` },
      position: nodePosition,
      style: BASE_NODE_STYLE,
    };
    
    // Temporarily add the node to calculate its correct Q number
    const tempNodes = [...nodes, tempNewNode];
    const tempEdges = ensureStartToDoneConnection(tempNodes, edges);
    const { nodes: tempRenumberedNodes } = renumberQuestionsInFlow(tempNodes, tempEdges);
    
    // Find the new node in the renumbered result to get its correct Q number
    const renumberedNewNode = tempRenumberedNodes.find(n => n.id === tempNewNode.id);
    const newNode = renumberedNewNode || tempNewNode;
    
    // Now add the node with the correct Q number from the start
    setNodes((nds) => {
      const updatedNodes = [...nds, newNode];
      // Feature 9: Trigger on new question creation (for edge connections)
      setTimeout(() => {
        setEdges((currentEdges) => {
          // Get the latest nodes state (which should include the new node)
          setNodes((currentNodes) => {
            const reconnectedEdges = ensureStartToDoneConnection(currentNodes, currentEdges);
            const { nodes: renumberedNodes, edges: renumberedEdges } = renumberQuestionsInFlow(currentNodes, reconnectedEdges);
            setEdges(renumberedEdges);
            
            // Ensure the new node is visible in the viewport
            if (reactFlowInstanceRef.current) {
              setTimeout(() => {
                reactFlowInstanceRef.current?.fitView({
                  padding: 0.2,
                  includeHiddenNodes: false,
                  minZoom: 0.1,
                  maxZoom: 2,
                });
              }, 100);
            }
            
            return renumberedNodes;
          });
          return currentEdges; // Return current edges for this setEdges call
        });
      }, 0);
      return updatedNodes;
    });
    setNodeIdCounter((prev) => prev + 1);
  };

  // Organize flow from start to done - tree layout algorithm
  const organizeQuestions = useCallback(() => {
    // Calculate node heights based on content (like old code)
    const calculateNodeHeight = (node: Node): number => {
      if (node.type === 'input' || node.type === 'output') {
        return 50; // Standard height for start/end nodes
      }
      
      const label = node.data?.label || '';
      const lines = label.split('\n');
      let height = 120; // Base height
      
      const questionText = parseQuestionText(label);
      if (questionText) {
        const estimatedLines = Math.ceil(questionText.length / 40);
        height += Math.max(0, (estimatedLines - 1) * 20);
      }
      
      const optionLine = lines.find((line: string) => 
        line.trim().toLowerCase().startsWith('options:') || 
        line.trim().toLowerCase().startsWith('choices:')
      );
      if (optionLine) {
        const [, list] = optionLine.split(':');
        if (list) {
          const options = list.split(',').map((item: string) => item.trim()).filter(Boolean);
          const rows = Math.ceil(options.length / 2.5);
          height += rows * 32 + 20;
        }
      }
      
      if (label.toLowerCase().includes('scale:')) {
        height += 30;
      }
      
      if (label.toLowerCase().includes('[optional]') || label.toLowerCase().includes('[required]')) {
        height += 25;
      }
      
      return Math.max(height, 120);
    };
    
    // Separate nodes by type
    const startNode = nodes.find(n => n.id === 'start') ?? createStartNode();
    const endNode = nodes.find(n => n.id === 'end' || n.id === 'done') ?? createEndNode();
    const allNodes = [startNode, endNode, ...nodes.filter(n => n.id !== 'start' && n.id !== 'end' && n.id !== 'done')];
    
    // Calculate node heights for all nodes
    const nodeHeights = new Map<string, number>();
    allNodes.forEach(node => {
      nodeHeights.set(node.id, calculateNodeHeight(node));
    });
    
    // Build edge maps
    const outgoingEdges = new Map<string, Edge[]>();
    const incomingEdges = new Map<string, Edge[]>();
    edges.forEach(edge => {
      if (!outgoingEdges.has(edge.source)) {
        outgoingEdges.set(edge.source, []);
      }
      outgoingEdges.get(edge.source)!.push(edge);
      
      if (!incomingEdges.has(edge.target)) {
        incomingEdges.set(edge.target, []);
      }
      incomingEdges.get(edge.target)!.push(edge);
    });
    
    // Build level-based structure using BFS (including sub-questions)
    const nodeLevels = new Map<string, number>();
    const levels: string[][] = [];
    
    // Level 0: Start node
    nodeLevels.set('start', 0);
    levels[0] = ['start'];
    
    // First pass: Identify all sub-questions and group them by parent
    const subQuestionsByParent = new Map<string, string[]>();
    allNodes.forEach(node => {
      const nodeId = node.id;
      if (nodeId.includes('.') && nodeId.match(/^\d+\.\d+$/)) {
        const parentId = nodeId.split('.')[0];
        if (!subQuestionsByParent.has(parentId)) {
          subQuestionsByParent.set(parentId, []);
        }
        subQuestionsByParent.get(parentId)!.push(nodeId);
      }
    });
    
    // Helper function to place all nodes reachable from sub-questions and calculate max level
    // This ensures nodes connected from sub-questions are placed before calculating next main question
    // visitedSet is the main BFS visited set (passed by reference) to mark nodes as visited
    const placeNodesFromSubQuestions = (parentId: string, parentLevel: number, visitedSet: Set<string>, localVisited: Set<string> = new Set()): number => {
      if (localVisited.has(parentId)) return parentLevel;
      localVisited.add(parentId);
      
      let maxLevel = parentLevel;
      const subQuestions = subQuestionsByParent.get(parentId) || [];
      
      // Place sub-questions at parentLevel + 1
      for (const subQuestionId of subQuestions) {
        const subLevel = parentLevel + 1;
        if (!nodeLevels.has(subQuestionId)) {
          nodeLevels.set(subQuestionId, subLevel);
          if (!levels[subLevel]) {
            levels[subLevel] = [];
          }
          if (!levels[subLevel].includes(subQuestionId)) {
            levels[subLevel].push(subQuestionId);
          }
        }
        maxLevel = Math.max(maxLevel, subLevel);
        
        // Check what's connected from this sub-question
        // Process ALL edges (both conditional and non-conditional) to ensure nodes connected from sub-questions are placed
        const subOutgoing = outgoingEdges.get(subQuestionId) || [];
        for (const edge of subOutgoing) {
          if (edge.target === 'end' || edge.target === 'done' || localVisited.has(edge.target)) continue;
          
          // If target is a main question (not a sub-question), place it directly below the sub-question
          const isTargetSubQuestion = edge.target.includes('.') && edge.target.match(/^\d+\.\d+$/);
          if (!isTargetSubQuestion && edge.target !== 'start' && edge.target !== 'end' && edge.target !== 'done') {
            // This is a main question connected from a sub-question
            // Place it at subLevel + 1 (directly below the sub-question)
            if (!nodeLevels.has(edge.target)) {
              const targetLevel = subLevel + 1;
              nodeLevels.set(edge.target, targetLevel);
              if (!levels[targetLevel]) {
                levels[targetLevel] = [];
              }
              if (!levels[targetLevel].includes(edge.target)) {
                levels[targetLevel].push(edge.target);
              }
              maxLevel = Math.max(maxLevel, targetLevel);
              
              // Mark as visited in the main BFS visited set to prevent it from being treated as orphaned
              visitedSet.add(edge.target);
              
              // Recursively check if this target has sub-questions or connects to other nodes
              const targetMaxLevel = placeNodesFromSubQuestions(edge.target, targetLevel, visitedSet, new Set(localVisited));
              maxLevel = Math.max(maxLevel, targetMaxLevel);
            } else {
              // Already placed, just check its depth
              const existingLevel = nodeLevels.get(edge.target) ?? 0;
              maxLevel = Math.max(maxLevel, existingLevel);
              visitedSet.add(edge.target); // Mark as visited in main BFS
              const targetMaxLevel = placeNodesFromSubQuestions(edge.target, existingLevel, visitedSet, new Set(localVisited));
              maxLevel = Math.max(maxLevel, targetMaxLevel);
            }
          } else if (isTargetSubQuestion) {
            // It's another sub-question, recursively place it
            const targetMaxLevel = placeNodesFromSubQuestions(edge.target, subLevel + 1, visitedSet, new Set(localVisited));
            maxLevel = Math.max(maxLevel, targetMaxLevel);
          }
        }
      }
      
      return maxLevel;
    };
    
    // Second pass: BFS to assign levels for main path, accounting for sub-questions
    const queue: string[] = ['start'];
    const visited = new Set<string>(['start']);
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      const currentLevel = nodeLevels.get(current) || 0;
      
      const outgoing = outgoingEdges.get(current) || [];
      // Follow non-conditional edges for main path
      const mainPathEdges = outgoing.filter(e => !isConditionalEdge(e));
      
      for (const edge of mainPathEdges) {
        // Skip sub-questions in main path (they'll be handled separately)
        if (edge.target.includes('.') && edge.target.match(/^\d+\.\d+$/)) {
          continue;
        }
        
        if (!visited.has(edge.target)) {
          visited.add(edge.target);
          
          // First, place all nodes reachable from current's sub-questions
          // This includes sub-questions themselves and any nodes connected from them
          // Pass the main BFS visited set so nodes are marked as visited
          const maxLevelFromCurrent = placeNodesFromSubQuestions(current, currentLevel, visited, new Set());
          
          // Check if current has both sub-questions AND non-conditional edges to main questions
          // If so, add extra spacing to accommodate the label on the non-conditional edge
          const currentSubQuestions = subQuestionsByParent.get(current) || [];
          const hasSubQuestions = currentSubQuestions.length > 0;
          // Check if there are non-conditional edges to main questions (not sub-questions)
          const nonConditionalToMainQuestions = outgoing.filter(e => 
            !isConditionalEdge(e) && 
            !e.target.includes('.') && 
            !e.target.match(/^\d+\.\d+$/) &&
            e.target !== 'end' &&
            e.target !== 'done'
          );
          const hasNonConditionalEdge = nonConditionalToMainQuestions.length > 0;
          
          // Place next main question after the maximum level used by current and its sub-tree
          // If current uses up to level N, next main question should be at N + 1
          // If current has both sub-questions and non-conditional edges, add extra level for label spacing
          let nextLevel = maxLevelFromCurrent + 1;
          if (hasSubQuestions && hasNonConditionalEdge) {
            // Add an extra level to provide space for the non-conditional edge label
            nextLevel = maxLevelFromCurrent + 2;
          }
          
          // Only place if not already placed (might have been placed by sub-question logic)
          if (!nodeLevels.has(edge.target)) {
            nodeLevels.set(edge.target, nextLevel);
            
            if (!levels[nextLevel]) {
              levels[nextLevel] = [];
            }
            levels[nextLevel].push(edge.target);
          }
          
          queue.push(edge.target);
        }
      }
    }
    
    // Third pass: Ensure all sub-questions are placed (some may have been placed in second pass)
    subQuestionsByParent.forEach((subQuestionIds, parentId) => {
      const parentLevel = nodeLevels.get(parentId);
      
      if (parentLevel !== undefined) {
        // Place all sub-questions at the level immediately after parent
        const subQuestionLevel = parentLevel + 1;
        
        subQuestionIds.forEach(subQuestionId => {
          // Only place if not already placed (might have been placed by placeNodesFromSubQuestions)
          if (!nodeLevels.has(subQuestionId)) {
            nodeLevels.set(subQuestionId, subQuestionLevel);
            
            if (!levels[subQuestionLevel]) {
              levels[subQuestionLevel] = [];
            }
            // Only add if not already in the level
            if (!levels[subQuestionLevel].includes(subQuestionId)) {
              levels[subQuestionLevel].push(subQuestionId);
            }
          }
        });
      }
    });
    
    // Add end node to the last level
    const maxLevel = levels.length > 0 ? Math.max(...Array.from(nodeLevels.values())) : 0;
    const endLevel = maxLevel + 1;
    nodeLevels.set('end', endLevel);
    if (!levels[endLevel]) {
      levels[endLevel] = [];
    }
    levels[endLevel].push('end');
    
    // Sort nodes within each level: main path first, then by ID
    const mainPath = (() => {
      const path: string[] = ['start'];
      const visited = new Set<string>(['start']);
      let current = 'start';
      
      while (current !== 'end' && current !== 'done') {
        const edges = outgoingEdges.get(current) || [];
        const nonConditional = edges.filter(e => !isConditionalEdge(e));
        const nextEdge = nonConditional.length > 0 ? nonConditional[0] : edges[0];
        
        if (!nextEdge || visited.has(nextEdge.target)) {
          break;
        }
        
        current = nextEdge.target;
        path.push(current);
        visited.add(current);
      }
      
      return path;
    })();
    
    const mainPathSet = new Set(mainPath);
    
    // Sort each level: main path nodes first, then others left to right
    levels.forEach((levelNodes, levelIndex) => {
      levelNodes.sort((a, b) => {
        const aInMainPath = mainPathSet.has(a);
        const bInMainPath = mainPathSet.has(b);
        
        if (aInMainPath && !bInMainPath) return -1;
        if (!aInMainPath && bInMainPath) return 1;
        if (aInMainPath && bInMainPath) {
          const aIndex = mainPath.indexOf(a);
          const bIndex = mainPath.indexOf(b);
          return aIndex - bIndex;
        }
        
        // Both not in main path: sort by ID (left to right)
        return extractNumericId(a) - extractNumericId(b);
      });
    });
    
    // Position nodes level by level, aligned below their parent connections
    const NODE_WIDTH = 280;
    const HORIZONTAL_SPACING = 50;
    const MIN_LEVEL_SPACING = 30; // Minimum spacing between levels (in addition to node height)
    const positionedNodes: Node[] = [];
    const nodePositions = new Map<string, { x: number; y: number }>();
    
    let startY = startNode.position.y || 0;
    
    // Track the maximum Y position for each level to calculate next level's start
    const levelMaxY = new Map<number, number>();
    
    levels.forEach((levelNodes, levelIndex) => {
      if (levelNodes.length === 0) return;
      
      // Find max height in this level
      let maxHeightInLevel = 0;
      levelNodes.forEach(nodeId => {
        const height = nodeHeights.get(nodeId) || 120;
        maxHeightInLevel = Math.max(maxHeightInLevel, height);
      });
      
      // Calculate Y position for this level based on previous level's max Y
      let currentLevelY: number;
      if (levelIndex === 0) {
        currentLevelY = startY;
      } else {
        const prevLevelMaxY = levelMaxY.get(levelIndex - 1) || startY;
        currentLevelY = prevLevelMaxY + MIN_LEVEL_SPACING;
      }
      
      // First pass: Calculate desired X positions based on parent connections
      const nodeDesiredX = new Map<string, number>();
      levelNodes.forEach((nodeId) => {
        // Find parent node(s) from previous level that connect to this node
        const parentEdges = incomingEdges.get(nodeId) || [];
        const parentNodes = parentEdges
          .map(edge => {
            const parentLevel = nodeLevels.get(edge.source);
            const currentLevel = nodeLevels.get(nodeId);
            // Only consider parents from the immediately previous level
            if (parentLevel !== undefined && currentLevel !== undefined && parentLevel === currentLevel - 1) {
              return edge.source;
            }
            return null;
          })
          .filter((id): id is string => id !== null);
        
        let desiredX: number;
        
        if (parentNodes.length === 0 || levelIndex === 0) {
          // No parent or first level: center it
          desiredX = BASE_X;
        } else if (parentNodes.length === 1) {
          // Single parent: position directly below it
          const parentPos = nodePositions.get(parentNodes[0]);
          if (parentPos) {
            desiredX = parentPos.x;
          } else {
            // Fallback: center it
            desiredX = BASE_X;
          }
        } else {
          // Multiple parents: position in the middle of all parent nodes
          const parentPositions = parentNodes
            .map(parentId => nodePositions.get(parentId))
            .filter((pos): pos is { x: number; y: number } => pos !== undefined);
          
          if (parentPositions.length > 0) {
            // Calculate the center X of all parent nodes
            const minX = Math.min(...parentPositions.map(p => p.x));
            const maxX = Math.max(...parentPositions.map(p => p.x));
            desiredX = (minX + maxX) / 2;
          } else {
            // Fallback: center it
            desiredX = BASE_X;
          }
        }
        
        nodeDesiredX.set(nodeId, desiredX);
      });
      
      // Second pass: Resolve overlaps by spacing nodes horizontally
      // Group nodes by their desired X position
      const xGroups = new Map<number, string[]>();
      levelNodes.forEach(nodeId => {
        const desiredX = nodeDesiredX.get(nodeId) || BASE_X;
        if (!xGroups.has(desiredX)) {
          xGroups.set(desiredX, []);
        }
        xGroups.get(desiredX)!.push(nodeId);
      });
      
      // Helper to check if a node is a sub-question
      const isSubQuestion = (nodeId: string): boolean => {
        return nodeId.includes('.') && nodeId.match(/^\d+\.\d+$/) !== null;
      };
      
      // Position nodes, handling overlaps
      // First, position all nodes based on desired X
      const initialPositions = new Map<string, { x: number; y: number }>();
      levelNodes.forEach((nodeId) => {
        const node = allNodes.find(n => n.id === nodeId);
        if (!node) return;
        
        const desiredX = nodeDesiredX.get(nodeId) || BASE_X;
        const group = xGroups.get(desiredX) || [nodeId];
        
        let x: number;
        
        if (group.length === 1) {
          // No overlap, use desired position
          x = desiredX;
        } else {
          // Multiple nodes want the same X - space them horizontally
          const indexInGroup = group.indexOf(nodeId);
          const totalWidth = group.length * NODE_WIDTH + (group.length - 1) * HORIZONTAL_SPACING;
          const startX = desiredX - (totalWidth / 2) + (NODE_WIDTH / 2);
          x = startX + indexInGroup * (NODE_WIDTH + HORIZONTAL_SPACING);
        }
        
        const y = currentLevelY;
        initialPositions.set(nodeId, { x, y });
      });
      
      // Second pass: Check for overlaps between questions and sub-questions on the same level
      // If a question and sub-question overlap, find the best position that avoids ALL overlaps
      const OVERLAP_THRESHOLD = 20; // Minimum spacing between nodes
      
      // Helper function to check if a position overlaps with any other node
      const hasOverlap = (testX: number, excludeNodeId: string, currentPositions: Map<string, { x: number; y: number }>): boolean => {
        for (const [otherNodeId, otherPos] of currentPositions.entries()) {
          if (otherNodeId === excludeNodeId) continue;
          const horizontalDistance = Math.abs(testX - otherPos.x);
          if (horizontalDistance < (NODE_WIDTH + OVERLAP_THRESHOLD)) {
            return true; // Overlap found
          }
        }
        return false; // No overlap
      };
      
      // Helper function to find the best position for a question that avoids all overlaps
      const findBestPosition = (questionX: number, questionNodeId: string, currentPositions: Map<string, { x: number; y: number }>): number => {
        // First, try moving to the right
        let testX = questionX;
        let attempts = 0;
        const maxAttempts = 100; // Prevent infinite loops
        
        // Try positions to the right first
        while (hasOverlap(testX, questionNodeId, currentPositions) && attempts < maxAttempts) {
          testX += NODE_WIDTH + OVERLAP_THRESHOLD;
          attempts++;
        }
        
        if (!hasOverlap(testX, questionNodeId, currentPositions)) {
          return testX; // Found a position to the right
        }
        
        // If right doesn't work, try moving to the left
        testX = questionX;
        attempts = 0;
        while (hasOverlap(testX, questionNodeId, currentPositions) && attempts < maxAttempts) {
          testX -= NODE_WIDTH + OVERLAP_THRESHOLD;
          attempts++;
        }
        
        if (!hasOverlap(testX, questionNodeId, currentPositions)) {
          return testX; // Found a position to the left
        }
        
        // If neither direction works, find the rightmost position and place it further right
        let rightmostX = questionX;
        for (const pos of currentPositions.values()) {
          if (pos.x > rightmostX) {
            rightmostX = pos.x;
          }
        }
        return rightmostX + NODE_WIDTH + OVERLAP_THRESHOLD;
      };
      
      // Build current positions map (initially from initialPositions)
      const currentPositions = new Map<string, { x: number; y: number }>();
      levelNodes.forEach(nodeId => {
        const pos = initialPositions.get(nodeId);
        if (pos) {
          currentPositions.set(nodeId, pos);
        }
      });
      
      // Process questions that overlap with sub-questions
      levelNodes.forEach((nodeId) => {
        const node = allNodes.find(n => n.id === nodeId);
        if (!node) return;
        
        const nodeIsSubQuestion = isSubQuestion(nodeId);
        
        // Only process questions (not sub-questions, start, or end)
        if (nodeIsSubQuestion || nodeId === 'start' || nodeId === 'end') {
          return;
        }
        
        let x = currentPositions.get(nodeId)?.x || BASE_X;
        const y = currentLevelY;
        
        // Check if this question overlaps with any sub-question
        let hasSubQuestionOverlap = false;
        for (const otherNodeId of levelNodes) {
          if (otherNodeId === nodeId) continue;
          
          const otherIsSubQuestion = isSubQuestion(otherNodeId);
          if (!otherIsSubQuestion) continue;
          
          const otherPos = currentPositions.get(otherNodeId);
          if (!otherPos) continue;
          
          const horizontalDistance = Math.abs(x - otherPos.x);
          if (horizontalDistance < (NODE_WIDTH + OVERLAP_THRESHOLD)) {
            hasSubQuestionOverlap = true;
            break;
          }
        }
        
        // If overlaps with a sub-question, find the best position that avoids ALL overlaps
        if (hasSubQuestionOverlap) {
          // Temporarily remove this node from positions to check overlaps
          const tempPositions = new Map(currentPositions);
          tempPositions.delete(nodeId);
          
          // Find best position
          x = findBestPosition(x, nodeId, tempPositions);
          
          // Update current positions with the new position
          currentPositions.set(nodeId, { x, y });
        }
      });
      
      // Final pass: Set all positions
      levelNodes.forEach((nodeId) => {
        const node = allNodes.find(n => n.id === nodeId);
        if (!node) return;
        
        const pos = currentPositions.get(nodeId) || initialPositions.get(nodeId) || { x: BASE_X, y: currentLevelY };
        const y = currentLevelY;
        
        nodePositions.set(nodeId, { x: pos.x, y });
        positionedNodes.push({
          ...node,
          position: { x: pos.x, y }
        });
      });
      
      // Calculate max Y for this level (highest node bottom position)
      let maxYForLevel = currentLevelY;
      levelNodes.forEach(nodeId => {
        const height = nodeHeights.get(nodeId) || 120;
        const nodePos = nodePositions.get(nodeId);
        if (nodePos) {
          maxYForLevel = Math.max(maxYForLevel, nodePos.y + height);
        }
      });
      levelMaxY.set(levelIndex, maxYForLevel);
    });
    
    // Build new edges with optimized handles based on level structure
    const newEdges: Edge[] = [];
    
    // Process all existing edges and update handles based on level positions
    edges.forEach(edge => {
      const sourcePos = nodePositions.get(edge.source);
      const targetPos = nodePositions.get(edge.target);
      
      if (!sourcePos || !targetPos) {
        // Keep edge if positions not found
        newEdges.push(edge);
        return;
      }
      
      // RESTRICTION: When organizing questions, ALL edges must use top/bottom handles only (not left/right)
      // This applies when organizing questions - users can still manually change them later
      const sourceHandle = 'bottom-output';
      const targetHandle = 'top-input';
      
      // Add edge with top/bottom handles
      newEdges.push({
        ...edge,
        sourceHandle,
        targetHandle,
      });
    });
    
    // Don't set custom label positions - let DraggableEdge use default getSmoothStepPath positions
    // This ensures labels are positioned correctly along the actual edge path
    // Clear any existing labelPosition and waypoints (waypoints are removed during organization)
    const edgesWithOptimalLabels = newEdges.map(edge => {
      const updatedData: any = { ...edge.data };
      let hasChanges = false;
      
      if (updatedData.labelPosition) {
        delete updatedData.labelPosition;
        hasChanges = true;
      }
      
      if (updatedData.waypoints) {
        delete updatedData.waypoints;
        hasChanges = true;
      }
      
      if (hasChanges) {
        return {
          ...edge,
          data: updatedData,
        };
      }
      return edge;
    });
    
    // Renumber questions based on their level and position
    // Main questions get sequential numbers (1, 2, 3, ...)
    // Sub-questions get decimal numbers based on parent (1.1, 1.2, 2.1, ...)
    const nodeIdToNewId = new Map<string, string>();
    let mainQuestionNumber = 1;
    const parentSubQuestionCount = new Map<string, number>(); // Track sub-question count per parent
    
    // Process levels in order
    levels.forEach((levelNodes) => {
      // Process nodes left to right within each level
      levelNodes.forEach((nodeId) => {
        if (nodeId === 'start' || nodeId === 'end') {
          // Keep start and end as-is
          nodeIdToNewId.set(nodeId, nodeId);
          return;
        }
        
        const node = allNodes.find(n => n.id === nodeId);
        if (!node) return;
        
        // Check if this is a sub-question (has decimal ID)
        if (nodeId.includes('.') && nodeId.match(/^\d+\.\d+$/)) {
          // This is a sub-question - find its parent
          const parentId = nodeId.split('.')[0];
          const parentNewId = nodeIdToNewId.get(parentId);
          
          if (parentNewId) {
            // Get the sub-question number for this parent
            const subCount = parentSubQuestionCount.get(parentNewId) || 0;
            parentSubQuestionCount.set(parentNewId, subCount + 1);
            const newSubId = `${parentNewId}.${subCount + 1}`;
            nodeIdToNewId.set(nodeId, newSubId);
          } else {
            // Parent not found yet, keep original ID for now
            nodeIdToNewId.set(nodeId, nodeId);
          }
        } else {
          // This is a main question - assign next sequential number
          const newId = mainQuestionNumber.toString();
          nodeIdToNewId.set(nodeId, newId);
          mainQuestionNumber++;
        }
      });
    });
    
    // Update node IDs and labels
    const renumberedNodes = positionedNodes.map(node => {
      const newId = nodeIdToNewId.get(node.id) || node.id;
      
      // Update label if this is a question node
      let newLabel = node.data?.label || '';
      if (node.type === 'question' && node.id !== 'start' && node.id !== 'end') {
        if (newId.includes('.')) {
          // Sub-question: extract parent number and sub-question number
          const [parentNum, subNum] = newId.split('.');
          const questionText = parseQuestionText(newLabel) || `Question ${parentNum}.${subNum}`;
          newLabel = `Q${newId}: ${questionText}`;
        } else {
          // Main question
          const questionText = parseQuestionText(newLabel) || `Question ${newId}`;
          newLabel = `Q${newId}: ${questionText}`;
        }
      }
      
      return {
        ...node,
        id: newId,
        data: {
          ...node.data,
          label: newLabel,
        },
      };
    });
    
    // Update edge source and target IDs
    const renumberedEdges = edgesWithOptimalLabels.map(edge => {
      const newSource = nodeIdToNewId.get(edge.source) || edge.source;
      const newTarget = nodeIdToNewId.get(edge.target) || edge.target;
      
      // Update edge label if it contains question numbers
      let newEdgeLabel = edge.label;
      if (edge.label && typeof edge.label === 'string') {
        // Try to update edge label with new question numbers
        const oldSourceMatch = edge.source.match(/^(\d+)/);
        const oldTargetMatch = edge.target.match(/^(\d+)/);
        const newSourceMatch = newSource.match(/^(\d+)/);
        const newTargetMatch = newTarget.match(/^(\d+)/);
        
        if (oldSourceMatch && newSourceMatch && edge.label.includes(`Q${oldSourceMatch[1]}`)) {
          newEdgeLabel = edge.label.replace(new RegExp(`Q${oldSourceMatch[1]}`, 'g'), `Q${newSourceMatch[1]}`);
        }
        if (oldTargetMatch && newTargetMatch && typeof edge.label === 'string' && edge.label.includes(`Q${oldTargetMatch[1]}`)) {
          const labelToUpdate = newEdgeLabel ?? edge.label;
          newEdgeLabel = typeof labelToUpdate === 'string' ? labelToUpdate.replace(new RegExp(`Q${oldTargetMatch[1]}`, 'g'), `Q${newTargetMatch[1]}`) : labelToUpdate;
        }
      }
      
      return {
        ...edge,
        id: `${newSource}-${newTarget}`,
        source: newSource,
        target: newTarget,
        label: newEdgeLabel,
      };
    });
    
    setNodes(renumberedNodes);
    setEdges(renumberedEdges);
    
    // Feature 9: Trigger after organizing to ensure start-to-done connection and renumber
    setTimeout(() => {
      setEdges((currentEdges) => {
        const reconnectedEdges = ensureStartToDoneConnection(positionedNodes, currentEdges);
        setNodes((currentNodes) => {
          const { nodes: renumberedNodes, edges: renumberedEdges } = renumberQuestionsInFlow(currentNodes, reconnectedEdges);
          setEdges(renumberedEdges);
          return renumberedNodes;
        });
        return reconnectedEdges;
      });
    }, 0);
    
    /* TEMPORARY: Comment out the complex logic below - will restore after testing
    const endNode = nodes.find((n) => n.id === 'end');
    if (!startNode || !endNode) {
      toast.error('Start and End nodes are required');
      console.error('Missing start or end node');
      console.error('Start node found:', !!startNode);
      console.error('End node found:', !!endNode);
      return;
    }
    
    try {
      // ... old complex logic ...
    } catch (error) {
      console.error('Error organizing questions:', error);
      toast.error(`Failed to organize: ${(error as Error).message || 'Unknown error'}`);
    }
    */
    
    /* OLD CODE COMMENTED OUT - All the complex organization logic below is commented out
    const nodesWithoutDuplicates = nodes.filter((node) => {
      // Keep all nodes except "done" nodes that aren't 'end'
      if (node.type === 'output' && node.id !== 'end') {
        return false;
      }
      return true;
    });
    
    // Ensure 'end' node exists
    if (!nodesWithoutDuplicates.some((n) => n.id === 'end')) {
      nodesWithoutDuplicates.push(createEndNode());
    }
    
    // ... rest of old code ...
    
    // All the complex organization logic below is commented out:
    */
    
    /* OLD CODE - All complex organization logic from here to line 5308 is commented out
      const calculateNodeHeight = (node: Node): number => {
        // For start/end nodes, use a base height
        if (node.type === 'input' || node.type === 'output') {
          return 50; // Standard height for start/end nodes
        }
        
        // For question nodes, calculate based on content
        const label = node.data?.label || '';
        const lines = label.split('\n');
        
        // Base height for padding and question text
        let height = 120; // Base height
        
        // Add height for question text (estimate ~2-3 lines)
        const questionText = parseQuestionText(label);
        if (questionText) {
          // Estimate text wrapping: ~40 characters per line
          const estimatedLines = Math.ceil(questionText.length / 40);
          height += Math.max(0, (estimatedLines - 1) * 20);
        }
        
        // Add height for options/choices
        const optionLine = lines.find((line: string) => 
          line.trim().toLowerCase().startsWith('options:') || 
          line.trim().toLowerCase().startsWith('choices:')
        );
        if (optionLine) {
          const [, list] = optionLine.split(':');
          if (list) {
            const options = list.split(',').map((item: string) => item.trim()).filter(Boolean);
            // Options are displayed as chips, estimate 2-3 chips per row
            const rows = Math.ceil(options.length / 2.5);
            height += rows * 32 + 20; // 32px per chip row + padding
          }
        }
        
        // Add height for rating scale info
        if (label.toLowerCase().includes('scale:')) {
          height += 30;
        }
        
        // Add height for optional/required tag
        if (label.toLowerCase().includes('[optional]') || label.toLowerCase().includes('[required]')) {
          height += 25;
        }
        
        // Minimum height to ensure visibility
        return Math.max(height, 120);
      };
      
      // Calculate handle positions
      const getHandlePosition = (nodeY: number, nodeHeight: number, handle: string): number => {
        if (handle === 'bottom-output') {
          return nodeY + nodeHeight; // Bottom of node
        }
        if (handle === 'top-input') {
          return nodeY; // Top of node
        }
        return nodeY + nodeHeight / 2; // Default to center
      };
      
      // Build main flow path from start to end (excluding conditional edges)
      const buildMainPath = (): { nodeId: string; edge: Edge | null }[] => {
        const path: { nodeId: string; edge: Edge | null }[] = [];
        const visited = new Set<string>();
        let currentNodeId = 'start';
        
        while (currentNodeId && currentNodeId !== 'end' && currentNodeId !== 'done') {
          if (visited.has(currentNodeId)) {
            console.warn(`⚠️ Circular reference detected at node ${currentNodeId}`);
            break;
          }
          visited.add(currentNodeId);
          
          // Find next node in main flow (non-conditional edge)
          const outgoingEdges = edges.filter(e => 
            e.source === currentNodeId && 
            !isConditionalEdge(e) &&
            e.target !== currentNodeId
          );
          
          if (outgoingEdges.length === 0) {
            // No more edges, check if we should end
            if (currentNodeId !== 'start') {
              path.push({ nodeId: currentNodeId, edge: null });
            }
            break;
          }
          
          // Use first non-conditional edge (main flow)
          const nextEdge = outgoingEdges[0];
          path.push({ nodeId: currentNodeId, edge: nextEdge });
          currentNodeId = nextEdge.target;
        }
        
        // Add end node if it exists
        if (endNode && !visited.has(endNode.id)) {
          const edgeToEnd = edges.find(e => 
            e.source === currentNodeId && 
            e.target === endNode.id &&
            !isConditionalEdge(e)
          );
          path.push({ nodeId: endNode.id, edge: edgeToEnd || null });
        }
        
        return path;
      };
      
      const mainPath = buildMainPath();
      console.log('🛤️ Main path:', mainPath.map(p => p.nodeId).join(' -> '));
      
      if (mainPath.length < 2) {
        toast.error('No valid path found from start to end');
        return;
      }
      
      // Calculate node heights for all nodes
      const nodeHeights = new Map<string, number>();
      nodes.forEach(node => {
        nodeHeights.set(node.id, calculateNodeHeight(node));
      });
      
      // CRITICAL: Never move the start node - use its current position
      const nodePositions = new Map<string, { x: number; y: number }>();
      nodePositions.set('start', startNode.position);
      console.log('📍 Start node position (unchanged):', startNode.position);
      
      // First pass: Position main path nodes with temporary positions
      // We'll adjust them later after positioning sub-questions to center parents at sub-question midpoints
      const tempMainPathPositions = new Map<string, { x: number; y: number }>();
      for (let i = 1; i < mainPath.length; i++) {
        const pathItem = mainPath[i];
        const currentNodeId = pathItem.nodeId;
        const currentNode = nodes.find(n => n.id === currentNodeId);
        if (!currentNode) {
          console.warn(`⚠️ Node ${currentNodeId} not found, skipping`);
          continue;
        }
        
        const prevPathItem = mainPath[i - 1];
        const prevNodeId = prevPathItem.nodeId;
        const prevPos = nodePositions.get(prevNodeId);
        if (!prevPos) {
          console.warn(`⚠️ Previous node ${prevNodeId} not positioned, skipping`);
          continue;
        }
        
        const prevHeight = nodeHeights.get(prevNodeId) || 120;
        const currentHeight = nodeHeights.get(currentNodeId) || 120;
        
        // Optimize spacing for this edge
        let bestSpacing = minSpacing;
        let bestScore = Infinity;
        
        for (let spacing = minSpacing; spacing <= maxSpacing; spacing += spacingStep) {
          const currentY = prevPos.y + prevHeight + spacing;
          const currentPos = { x: prevPos.x, y: currentY }; // Align X with previous node
          
          // Calculate edge length
          const sourceHandleY = getHandlePosition(prevPos.y, prevHeight, 'bottom-output');
          const targetHandleY = getHandlePosition(currentPos.y, currentHeight, 'top-input');
          const edgeLength = Math.abs(targetHandleY - sourceHandleY);
          
          // Calculate penalty score (lower is better)
          const edgeLengthPenalty = edgeLength * EDGE_LENGTH_PENALTY;
          const totalScore = edgeLengthPenalty;
          
          if (totalScore < bestScore) {
            bestScore = totalScore;
            bestSpacing = spacing;
          }
        }
        
        // Set temporary position for current node (will be adjusted after sub-questions are positioned)
        const currentPos = {
          x: prevPos.x, // Align with previous node's X position
          y: prevPos.y + prevHeight + bestSpacing
        };
        tempMainPathPositions.set(currentNodeId, currentPos);
        nodePositions.set(currentNodeId, currentPos); // Set initial position
        
        console.log(`📍 Temporarily positioned ${currentNodeId} relative to ${prevNodeId}:`, {
          previous: prevPos,
          current: currentPos,
          spacing: bestSpacing,
          edgeLength: bestSpacing
        });
      }
      
      // Now organize branches (conditional edges) from each node in the main path
      // Spacing is now calculated dynamically based on edge label and sub-question text length
      
      // Find all nodes with conditional branches
      const nodesWithBranches = new Map<string, Edge[]>();
      mainPath.forEach(pathItem => {
        const nodeId = pathItem.nodeId;
        const conditionalEdges = edges.filter(e => 
          e.source === nodeId && 
          isConditionalEdge(e)
        );
        if (conditionalEdges.length > 0) {
          nodesWithBranches.set(nodeId, conditionalEdges);
        }
      });
      
      console.log(`🌿 Found ${nodesWithBranches.size} nodes with branches`);
      
      // Organize each branch separately
      nodesWithBranches.forEach((branchEdges, sourceNodeId) => {
        const sourcePos = nodePositions.get(sourceNodeId);
        if (!sourcePos) {
          console.warn(`⚠️ Source node ${sourceNodeId} not positioned, skipping branches`);
          return;
        }
        
        const sourceHeight = nodeHeights.get(sourceNodeId) || 120;
        const NODE_WIDTH = 280; // Node width for collision detection
        
        // Helper to check if an edge line would cross through a node
        const edgeCrossesNode = (
          edgeStart: { x: number; y: number },
          edgeEnd: { x: number; y: number },
          nodePos: { x: number; y: number },
          nodeHeight: number
        ): boolean => {
          // Check if edge line segment intersects with node bounding box
          const nodeLeft = nodePos.x;
          const nodeRight = nodePos.x + NODE_WIDTH;
          const nodeTop = nodePos.y;
          const nodeBottom = nodePos.y + nodeHeight;
          
          // Simple check: if both endpoints are on same side, edge doesn't cross
          const bothLeft = edgeStart.x < nodeLeft && edgeEnd.x < nodeLeft;
          const bothRight = edgeStart.x > nodeRight && edgeEnd.x > nodeRight;
          const bothAbove = edgeStart.y < nodeTop && edgeEnd.y < nodeTop;
          const bothBelow = edgeStart.y > nodeBottom && edgeEnd.y > nodeBottom;
          
          if (bothLeft || bothRight || bothAbove || bothBelow) {
            return false; // Edge doesn't cross node
          }
          
          // Check if edge line passes through node (more detailed intersection check)
          // For horizontal edges (branches), check if Y is within node bounds and X crosses
          if (Math.abs(edgeStart.y - edgeEnd.y) < 10) {
            // Mostly horizontal edge
            const minX = Math.min(edgeStart.x, edgeEnd.x);
            const maxX = Math.max(edgeStart.x, edgeEnd.x);
            return edgeStart.y >= nodeTop && edgeStart.y <= nodeBottom &&
                   minX < nodeRight && maxX > nodeLeft;
          }
          
          // For vertical edges, check if X is within node bounds and Y crosses
          if (Math.abs(edgeStart.x - edgeEnd.x) < 10) {
            // Mostly vertical edge
            const minY = Math.min(edgeStart.y, edgeEnd.y);
            const maxY = Math.max(edgeStart.y, edgeEnd.y);
            return edgeStart.x >= nodeLeft && edgeStart.x <= nodeRight &&
                   minY < nodeBottom && maxY > nodeTop;
          }
          
          // Diagonal edge - check if line segment intersects rectangle
          // Simplified: check if any corner of node is on the "wrong side" of the line
          return true; // Conservative: assume it crosses if not clearly separated
        };
        
        // Separate branches into left and right groups for better organization
        const leftBranches: { edge: Edge; index: number }[] = [];
        const rightBranches: { edge: Edge; index: number }[] = [];
        
        branchEdges.forEach((branchEdge, branchIndex) => {
          // Alternate left/right: even indices go right, odd indices go left
          const isRight = branchIndex % 2 === 0;
          if (isRight) {
            rightBranches.push({ edge: branchEdge, index: branchIndex });
          } else {
            leftBranches.push({ edge: branchEdge, index: branchIndex });
          }
        });
        
        // Helper to position branches - tries vertical stacking first, then horizontal if needed
        const positionBranches = (allBranches: { edge: Edge; index: number }[]) => {
          if (allBranches.length === 0) return;
          
          const MIN_BRANCH_SPACING = 100; // Minimum spacing between branch nodes
          const FIXED_HORIZONTAL_SPACING = 400; // Fixed spacing for horizontal branches
          const MIN_VERTICAL_SPACING_LOCAL = 40; // Local constant for vertical spacing
          const sourceBottomY = sourcePos.y + sourceHeight;
          
          // Try vertical stacking first (preferred layout)
          // Position all sub-questions directly below parent, stacked vertically
          let currentY = sourceBottomY + MIN_VERTICAL_SPACING_LOCAL;
          const verticalPositions = new Map<string, { x: number; y: number }>();
          let canUseVertical = true;
          
          allBranches.forEach((branchItem, index) => {
            const branchEdge = branchItem.edge;
            const branchPath = buildBranchPath(sourceNodeId, branchEdge);
            
            if (branchPath.length > 1) {
              const firstBranchNodeId = branchPath[1].nodeId;
              const firstBranchNode = nodes.find(n => n.id === firstBranchNodeId);
              if (firstBranchNode && !nodePositions.has(firstBranchNodeId)) {
                const firstBranchHeight = nodeHeights.get(firstBranchNodeId) || 120;
                
                // Try vertical position (same X as parent, below)
                const verticalPos = {
                  x: sourcePos.x, // Same X as parent
                  y: currentY
                };
                
                // Check if this position would overlap with existing nodes
                // Use more lenient checking - allow if there's reasonable spacing
                let wouldOverlapVertical = false;
                nodePositions.forEach((existingPos, existingNodeId) => {
                  if (existingNodeId === sourceNodeId) return;
                  const existingHeight = nodeHeights.get(existingNodeId) || 120;
                  
                  // Check X overlap (with tolerance)
                  const xDiff = Math.abs(existingPos.x - verticalPos.x);
                  const xOverlap = xDiff < (NODE_WIDTH * 0.8); // 80% overlap threshold (more lenient)
                  
                  // Check Y overlap (with spacing requirement)
                  const yGap = Math.min(
                    Math.abs(verticalPos.y - (existingPos.y + existingHeight)),
                    Math.abs((verticalPos.y + firstBranchHeight) - existingPos.y)
                  );
                  const yOverlap = yGap < MIN_VERTICAL_SPACING_LOCAL; // Need at least minimum spacing
                  
                  if (xOverlap && yOverlap) {
                    wouldOverlapVertical = true;
                  }
                });
                
                if (!wouldOverlapVertical) {
                  verticalPositions.set(firstBranchNodeId, verticalPos);
                  currentY += firstBranchHeight + MIN_VERTICAL_SPACING_LOCAL;
                } else {
                  canUseVertical = false;
                }
              }
            }
          });
          
          // If vertical stacking works for all branches, use it
          // Also try to make it work by adjusting positions slightly if needed
          if (verticalPositions.size === allBranches.length) {
            // Try to resolve any conflicts by adjusting Y positions
            let adjusted = false;
            verticalPositions.forEach((pos, nodeId) => {
              const nodeHeight = nodeHeights.get(nodeId) || 120;
              let adjustedY = pos.y;
              
              // Check for conflicts and adjust
              nodePositions.forEach((existingPos, existingNodeId) => {
                if (existingNodeId === sourceNodeId || existingNodeId === nodeId) return;
                const existingHeight = nodeHeights.get(existingNodeId) || 120;
                
                const xDiff = Math.abs(existingPos.x - pos.x);
                if (xDiff < (NODE_WIDTH * 0.8)) {
                  // Potential X overlap, check Y
                  const yGap = Math.min(
                    Math.abs(adjustedY - (existingPos.y + existingHeight)),
                    Math.abs((adjustedY + nodeHeight) - existingPos.y)
                  );
                  
                  if (yGap < MIN_VERTICAL_SPACING_LOCAL) {
                    // Need to move down
                    adjustedY = existingPos.y + existingHeight + MIN_VERTICAL_SPACING_LOCAL;
                    adjusted = true;
                  }
                }
              });
              
              verticalPositions.set(nodeId, { x: pos.x, y: adjustedY });
            });
            
            // Recalculate positions with adjusted Y values
            if (adjusted) {
              let newCurrentY = sourceBottomY + MIN_VERTICAL_SPACING_LOCAL;
              verticalPositions.forEach((pos, nodeId) => {
                const nodeHeight = nodeHeights.get(nodeId) || 120;
                verticalPositions.set(nodeId, { x: pos.x, y: newCurrentY });
                newCurrentY += nodeHeight + MIN_VERTICAL_SPACING_LOCAL;
              });
            }
            
            console.log(`✅ Using vertical stacking for ${allBranches.length} sub-questions from ${sourceNodeId}`);
            // Final check - make sure no overlaps and adjust if needed
            const adjustedPositions = new Map<string, { x: number; y: number }>();
            verticalPositions.forEach((pos, nodeId) => {
              const nodeHeight = nodeHeights.get(nodeId) || 120;
              let finalY = pos.y;
              
              nodePositions.forEach((existingPos, existingNodeId) => {
                if (existingNodeId === sourceNodeId || existingNodeId === nodeId) return;
                const existingHeight = nodeHeights.get(existingNodeId) || 120;
                const xDiff = Math.abs(existingPos.x - pos.x);
                if (xDiff < NODE_WIDTH) {
                  if (finalY < existingPos.y + existingHeight + MIN_VERTICAL_SPACING_LOCAL) {
                    finalY = existingPos.y + existingHeight + MIN_VERTICAL_SPACING_LOCAL;
                  }
                }
              });
              
              adjustedPositions.set(nodeId, { x: pos.x, y: finalY });
            });
            
            // Update verticalPositions with adjusted values
            adjustedPositions.forEach((pos, nodeId) => {
              verticalPositions.set(nodeId, pos);
            });
            
            verticalPositions.forEach((pos, nodeId) => {
              nodePositions.set(nodeId, pos);
            });
            
            // Continue organizing the rest of each branch vertically
            allBranches.forEach((branchItem) => {
              const branchPath = buildBranchPath(sourceNodeId, branchItem.edge);
              for (let i = 2; i < branchPath.length; i++) {
                const branchNodeId = branchPath[i].nodeId;
                const branchNode = nodes.find(n => n.id === branchNodeId);
                if (!branchNode || nodePositions.has(branchNodeId)) continue;
                
                const prevBranchNodeId = branchPath[i - 1].nodeId;
                const prevBranchPos = nodePositions.get(prevBranchNodeId);
                if (!prevBranchPos) continue;
                
                const prevBranchHeight = nodeHeights.get(prevBranchNodeId) || 120;
                const branchNodeHeight = nodeHeights.get(branchNodeId) || 120;
                
                // Position directly below previous node in branch
                const branchNodePos = {
                  x: prevBranchPos.x, // Keep same X
                  y: prevBranchPos.y + prevBranchHeight + MIN_VERTICAL_SPACING_LOCAL
                };
                
                nodePositions.set(branchNodeId, branchNodePos);
              }
            });
            return; // Done with vertical stacking
          } else if (verticalPositions.size > 0) {
            // Partial vertical stacking - use what we can, rest goes horizontal
            console.log(`⚠️ Partial vertical stacking: ${verticalPositions.size}/${allBranches.length} sub-questions from ${sourceNodeId}`);
            verticalPositions.forEach((pos, nodeId) => {
              nodePositions.set(nodeId, pos);
            });
          }
          
          // Fall back to horizontal positioning (original logic)
          console.log(`⚠️ Vertical stacking not possible, using horizontal layout for ${sourceNodeId}`);
          
          // Split branches into left and right sides
          const rightBranches: { edge: Edge; index: number }[] = [];
          const leftBranches: { edge: Edge; index: number }[] = [];
          
          allBranches.forEach((branchItem, branchIndex) => {
            const isRight = branchIndex % 2 === 0;
            if (isRight) {
              rightBranches.push({ edge: branchItem.edge, index: branchIndex });
            } else {
              leftBranches.push({ edge: branchItem.edge, index: branchIndex });
            }
          });
          
          // Helper to position branches on one side with even distribution
          const positionBranchesOnSide = (
            sideBranches: { edge: Edge; index: number }[],
            isRightSide: boolean
          ) => {
            if (sideBranches.length === 0) return;
            
            const sourceCenterY = sourcePos.y + (sourceHeight / 2);
            const totalBranches = sideBranches.length;
            const totalVerticalSpace = (totalBranches - 1) * MIN_BRANCH_SPACING;
            const startY = sourceCenterY - (totalVerticalSpace / 2);
            
            sideBranches.forEach((branchItem, sideIndex) => {
              const branchEdge = branchItem.edge;
              const branchPath = buildBranchPath(sourceNodeId, branchEdge);
              const edgeLabel = branchEdge.label || branchEdge.data?.conditionalOption || '';
              
              if (branchPath.length > 1) {
                const firstBranchNodeId = branchPath[1].nodeId;
                const firstBranchNode = nodes.find(n => n.id === firstBranchNodeId);
                if (firstBranchNode && !nodePositions.has(firstBranchNodeId)) {
                  const firstBranchHeight = nodeHeights.get(firstBranchNodeId) || 120;
                  const finalHorizontalOffset = isRightSide ? FIXED_HORIZONTAL_SPACING : -FIXED_HORIZONTAL_SPACING;
                  const verticalPosition = startY + (sideIndex * MIN_BRANCH_SPACING);
                  const firstBranchY = verticalPosition - (firstBranchHeight / 2);
                  const firstBranchPos = {
                    x: sourcePos.x + finalHorizontalOffset,
                    y: firstBranchY
                  };
                  
                  nodePositions.set(firstBranchNodeId, firstBranchPos);
                  
                  console.log(`📍 Positioned branch node ${firstBranchNodeId} relative to ${sourceNodeId}:`, {
                    source: sourcePos,
                    branch: firstBranchPos,
                    offset: finalHorizontalOffset,
                    edgeLabel,
                    verticalPosition
                  });
                
                // Continue organizing the rest of the branch
              for (let i = 2; i < branchPath.length; i++) {
                const branchPathItem = branchPath[i];
                const branchNodeId = branchPathItem.nodeId;
                const branchNode = nodes.find(n => n.id === branchNodeId);
                if (!branchNode || nodePositions.has(branchNodeId)) {
                  continue; // Skip if already positioned or not found
                }
                
                const prevBranchPathItem = branchPath[i - 1];
                const prevBranchNodeId = prevBranchPathItem.nodeId;
                const prevBranchPos = nodePositions.get(prevBranchNodeId);
                if (!prevBranchPos) {
                  continue;
                }
                
                const prevBranchHeight = nodeHeights.get(prevBranchNodeId) || 120;
                const branchNodeHeight = nodeHeights.get(branchNodeId) || 120;
                
                // Optimize spacing for this branch edge with edge crossing penalties
                const EDGE_CROSSES_NODE_PENALTY = 10000; // Heavy penalty for edges crossing through nodes
                let bestBranchSpacing = minSpacing;
                let bestBranchScore = Infinity;
                
                for (let spacing = minSpacing; spacing <= maxSpacing; spacing += spacingStep) {
                  const branchNodeY = prevBranchPos.y + prevBranchHeight + spacing;
                  const branchNodePos = { x: prevBranchPos.x, y: branchNodeY };
                  
                  const sourceHandleY = getHandlePosition(prevBranchPos.y, prevBranchHeight, 'bottom-output');
                  const targetHandleY = getHandlePosition(branchNodePos.y, branchNodeHeight, 'top-input');
                  const edgeLength = Math.abs(targetHandleY - sourceHandleY);
                  
                  // Calculate edge start and end points for crossing detection
                  const edgeStart = { x: prevBranchPos.x, y: sourceHandleY };
                  const edgeEnd = { x: branchNodePos.x, y: targetHandleY };
                  
                  // Check if this edge would cross through any other nodes
                  let crossingPenalty = 0;
                  nodePositions.forEach((otherNodePos, otherNodeId) => {
                    // Skip source and target nodes
                    if (otherNodeId === prevBranchNodeId || otherNodeId === branchNodeId) {
                      return;
                    }
                    
                    const otherNodeHeight = nodeHeights.get(otherNodeId) || 120;
                    if (edgeCrossesNode(edgeStart, edgeEnd, otherNodePos, otherNodeHeight)) {
                      crossingPenalty += EDGE_CROSSES_NODE_PENALTY;
                    }
                  });
                  
                  const edgeLengthPenalty = edgeLength * EDGE_LENGTH_PENALTY;
                  const totalScore = edgeLengthPenalty + crossingPenalty;
                  
                  if (totalScore < bestBranchScore) {
                    bestBranchScore = totalScore;
                    bestBranchSpacing = spacing;
                  }
                }
                
                const branchNodePos = {
                  x: prevBranchPos.x, // Keep same X as previous branch node
                  y: prevBranchPos.y + prevBranchHeight + bestBranchSpacing
                };
                
                // Final check: if edge still crosses through nodes, adjust position
                const edgeStart = { x: prevBranchPos.x, y: getHandlePosition(prevBranchPos.y, prevBranchHeight, 'bottom-output') };
                const edgeEnd = { x: branchNodePos.x, y: getHandlePosition(branchNodePos.y, branchNodeHeight, 'top-input') };
                
                let adjusted = false;
                nodePositions.forEach((otherNodePos, otherNodeId) => {
                  if (otherNodeId === prevBranchNodeId || otherNodeId === branchNodeId) return;
                  const otherNodeHeight = nodeHeights.get(otherNodeId) || 120;
                  if (edgeCrossesNode(edgeStart, edgeEnd, otherNodePos, otherNodeHeight)) {
                    // Move node further down to avoid crossing
                    branchNodePos.y += 60; // Additional spacing to avoid crossing
                    adjusted = true;
                  }
                });
                
                nodePositions.set(branchNodeId, branchNodePos);
                
                console.log(`📍 Positioned branch node ${branchNodeId} relative to ${prevBranchNodeId}:`, {
                  previous: prevBranchPos,
                  current: branchNodePos,
                  spacing: bestBranchSpacing,
                  adjusted
                });
              }
            }
          }
          }); // End of sideBranches.forEach
        }; // End of positionBranchesOnSide function
        
        positionBranchesOnSide(rightBranches, true);
        positionBranchesOnSide(leftBranches, false);
      };
      
      // Position all branches (tries vertical first, falls back to horizontal)
      const allBranches = Array.from(branchEdges).map((edge, index) => ({ edge, index }));
      positionBranches(allBranches);
      }); // End of nodesWithBranches.forEach
      
      // Update all nodes - NEVER move start node
      const updatedNodes = nodes.map(node => {
        // CRITICAL: Never move start node
        if (node.id === 'start') {
          return node; // Keep start node completely unchanged
        }
        
        // Update position for nodes in the path
        const newPos = nodePositions.get(node.id);
        if (newPos) {
          return { ...node, position: newPos };
        }
        
        return node;
      });
      
      // Update all edges to use proper handles
      const updatedEdges = edges.map(edge => {
        // Check if this edge is in the main path
        const pathItem = mainPath.find(p => p.edge?.id === edge.id);
        if (pathItem && pathItem.edge) {
          return {
            ...edge,
            sourceHandle: 'bottom-output',
            targetHandle: 'top-input',
          };
        }
        
        // Check if this is a conditional edge (branch)
        if (isConditionalEdge(edge)) {
          // Determine if branch goes right or left based on target position
          const sourcePos = nodePositions.get(edge.source);
          const targetPos = nodePositions.get(edge.target);
          if (sourcePos && targetPos) {
            const isRight = targetPos.x > sourcePos.x;
            return {
              ...edge,
              sourceHandle: isRight ? 'right-output' : 'left-output',
              targetHandle: isRight ? 'left-input' : 'right-input',
            };
          }
        }
        
        return edge;
      });
      
      // Apply updates
      setNodes(updatedNodes);
      setEdges(updatedEdges);
    */
    
    /* TEMPORARY: Comment out the complex logic below - will restore after testing
    const endNode = nodes.find((n) => n.id === 'end');

    if (!startNode || !endNode) {
      toast.error('Start and End nodes are required');
      console.error('Missing start or end node');
      console.error('Start node found:', !!startNode);
      console.error('End node found:', !!endNode);
      return;
    }
    
    try {

    // Enhanced constants for organization rules
    const NODE_WIDTH = 280; // From BASE_NODE_STYLE
    const BASE_NODE_HEIGHT = 120; // Base node height (minimum)
    const LINE_HEIGHT = 20; // Height per line of text
    const OPTION_CHIP_HEIGHT = 32; // Height per option chip
    const MIN_VERTICAL_SPACING = 40; // Compact vertical spacing for tight, organized layout
    const CONDITIONAL_HORIZONTAL_SPACING = 250; // Horizontal spacing for conditional branches
    const CONDITIONAL_VERTICAL_OFFSET = 60; // Vertical offset for conditionals relative to source
    const CONDITIONAL_MIN_HORIZONTAL_SPACING = NODE_WIDTH + 80; // Minimum horizontal spacing for conditional links (more breathing room)
    const MERGE_POINT_EXTRA_SPACING = 50; // Extra spacing for merge points
    
    // ============================================================================
    // PENALTY-BASED SCORING SYSTEM - All weights in one place for easy adjustment
    // ============================================================================
    const PENALTIES = {
      // Node overlap penalties (CRITICAL - overlaps are completely unacceptable)
      NODE_OVERLAP: 500000, // Per overlapping node pair (MASSIVELY increased - overlaps must be prevented)
      NODE_TOO_CLOSE: 10000, // Per node pair that's too close (less than MIN_VERTICAL_SPACING)
      
      // Edge/line penalties
      EDGE_LENGTH: 0.1, // Per pixel of edge length (reduced from 0.5 to allow shorter lines)
      MULTIPLE_EDGES_SAME_HANDLE: 50000, // Per handle that has multiple edges (MASSIVELY increased - discourages cluttered connections)
      EDGE_CROSSES_THROUGH_NODE: 100000, // Per edge that visually passes through a node (very high penalty)
      
      // Positioning rule violations (CRITICAL RULES)
      START_NOT_AT_TOP: 50000, // Start node not at y=0 (increased)
      DONE_NOT_AT_BOTTOM: 500000, // Done node not at lowest Y position (MASSIVELY increased - critical rule)
      INCONSISTENT_SPACING: 10, // Per pixel deviation from ideal spacing
      
      // Edge crossing penalties
      EDGE_CROSSING: 5000, // Per edge crossing (increased)
      
      // Layout quality penalties
      NODES_NOT_IN_FLOW_ORDER: 1000, // Per node not in logical flow order (increased)
      CONDITIONAL_POORLY_POSITIONED: 5000, // Per conditional node poorly positioned (increased)
      CONDITIONAL_TOO_FAR_FROM_SOURCE: 2000, // Per conditional node too far from its source
      CONDITIONAL_WRONG_SIDE: 1000, // Per conditional node on wrong side (should alternate left/right)
      CONDITIONAL_IN_MAIN_PATH: 200000, // CRITICAL: Conditional node in main path (should branch to side)
      CONDITIONAL_NOT_HORIZONTAL_OFFSET: 50000, // Conditional node not horizontally offset from source (should be left/right)
      CONDITIONAL_IN_CENTER_COLUMN: 100000, // Conditional node in center column (should be left/right of main path)
    };
    
    // ============================================================================
    // REWARD SYSTEM - Bonus points for preferred visual structures
    // These are subtracted from penalties to encourage better layouts
    // ============================================================================
    const REWARDS = {
      // Vertical stacking rewards (preferred over horizontal branching) - MASSIVELY INCREASED
      SUB_QUESTION_VERTICAL_STACK: 200000, // Reward for each sub-question placed directly below parent (same X, below Y)
      SUB_QUESTIONS_VERTICAL_ALIGNED: 150000, // Reward when multiple sub-questions are vertically aligned (same X coordinate)
      COMPACT_VERTICAL_LAYOUT: 100000, // Reward for compact vertical layouts (all sub-questions stacked vertically)
      ALL_SUB_QUESTIONS_VERTICAL: 250000, // Bonus reward when ALL sub-questions from a parent are vertically stacked
      
      // Connection rewards
      SUB_QUESTION_DIRECT_TO_DONE: 50000, // Reward when sub-questions connect directly to Done (cleaner flow)
      SHORT_SUB_QUESTION_EDGES: 10000, // Reward for short edges from sub-questions (per pixel saved)
      
      // Layout quality rewards
      CLEAN_VERTICAL_FLOW: 30000, // Reward for clean vertical flow (minimal horizontal offsets in main path)
      CONSISTENT_SPACING: 15000, // Reward for consistent spacing between nodes
      SYMMETRICAL_LAYOUT: 40000, // Reward for symmetrical layouts (balanced left/right)
      MINIMAL_HORIZONTAL_OFFSET: 25000, // Reward for minimal horizontal offsets (prefer vertical alignment)
      STRAIGHT_VERTICAL_LINES: 20000, // Reward for straight vertical connections (no horizontal offset)
      PARENT_CENTERED_AT_SUB_MIDPOINT: 150000, // Reward when parent is centered at sub-question midpoint (balanced layout)
    };
    
    // Calculate actual node height based on content
    const calculateNodeHeight = (node: Node): number => {
      const label = node.data?.label || '';
      const lines = label.split('\n');
      
      // Base height for padding and question text
      let height = BASE_NODE_HEIGHT;
      
      // Add height for question text (estimate ~2-3 lines)
      const questionText = parseQuestionText(label);
      if (questionText) {
        // Estimate text wrapping: ~40 characters per line
        const estimatedLines = Math.ceil(questionText.length / 40);
        height += Math.max(0, (estimatedLines - 1) * LINE_HEIGHT);
      }
      
      // Add height for options/choices
      const optionLine = lines.find((line: string) => 
        line.trim().toLowerCase().startsWith('options:') || 
        line.trim().toLowerCase().startsWith('choices:')
      );
      if (optionLine) {
        const [, list] = optionLine.split(':');
        if (list) {
          const options = list.split(',').map((item: string) => item.trim()).filter(Boolean);
          // Options are displayed as chips, estimate 2-3 chips per row
          const rows = Math.ceil(options.length / 2.5);
          height += rows * OPTION_CHIP_HEIGHT + 20; // Extra padding for chips
        }
      }
      
      // Add height for rating scale info
      if (label.toLowerCase().includes('scale:')) {
        height += 30; // Extra space for scale info
      }
      
      // Add height for optional/required tag
      if (label.toLowerCase().includes('[optional]') || label.toLowerCase().includes('[required]')) {
        height += 25;
      }
      
      // Minimum height to ensure visibility
      return Math.max(height, BASE_NODE_HEIGHT);
    };
    
    // ============================================================================
    // SCORING FUNCTION - Evaluates a layout and returns penalty score (lower is better)
    // ============================================================================
    const scoreLayout = (
      nodePositions: Map<string, { x: number; y: number }>,
      nodeHeights: Map<string, number>,
      edges: Edge[],
      mainPath: string[]
    ): number => {
      let totalPenalty = 0;
      
      // 1. Check for node overlaps (CRITICAL - very high penalty)
      const nodeArray = Array.from(nodePositions.entries());
      for (let i = 0; i < nodeArray.length; i++) {
        const [nodeId1, pos1] = nodeArray[i];
        const height1 = nodeHeights.get(nodeId1) || BASE_NODE_HEIGHT;
        
        for (let j = i + 1; j < nodeArray.length; j++) {
          const [nodeId2, pos2] = nodeArray[j];
          const height2 = nodeHeights.get(nodeId2) || BASE_NODE_HEIGHT;
          
          // Check if nodes overlap (strict detection with padding)
          // Check horizontal overlap: nodes overlap if their X ranges intersect
          const x1Left = pos1.x;
          const x1Right = pos1.x + NODE_WIDTH;
          const x2Left = pos2.x;
          const x2Right = pos2.x + NODE_WIDTH;
          const xOverlap = !(x1Right <= x2Left || x2Right <= x1Left);
          
          // Check vertical overlap: nodes overlap if their Y ranges intersect
          const y1Top = pos1.y;
          const y1Bottom = pos1.y + height1;
          const y2Top = pos2.y;
          const y2Bottom = pos2.y + height2;
          const yOverlap = !(y1Bottom <= y2Top || y2Bottom <= y1Top);
          
          // Apply massive penalty for any overlap
          if (xOverlap && yOverlap) {
            totalPenalty += PENALTIES.NODE_OVERLAP;
          }
          
          // Check if nodes are too close (less than minimum spacing) - applies to both X and Y
          if (xOverlap) {
            // Check vertical spacing
            const verticalGap = Math.min(
              Math.abs(y1Bottom - y2Top),
              Math.abs(y2Bottom - y1Top)
            );
            if (verticalGap > 0 && verticalGap < MIN_VERTICAL_SPACING) {
              totalPenalty += PENALTIES.NODE_TOO_CLOSE;
            }
          }
          
          // Also check horizontal spacing for nodes that are vertically aligned
          if (yOverlap || Math.abs(y1Top - y2Top) < MIN_VERTICAL_SPACING) {
            const horizontalGap = Math.min(
              Math.abs(x1Right - x2Left),
              Math.abs(x2Right - x1Left)
            );
            if (horizontalGap > 0 && horizontalGap < MIN_VERTICAL_SPACING) {
              totalPenalty += PENALTIES.NODE_TOO_CLOSE;
            }
          }
        }
      }
      
      // 2. Check Start node position (must be at y=0)
      const startPos = nodePositions.get('start');
      if (startPos && startPos.y !== 0) {
        totalPenalty += PENALTIES.START_NOT_AT_TOP;
      }
      
      // 3. Check Done node position (CRITICAL: must be at lowest Y - heavily penalized)
      const endPos = nodePositions.get('end');
      if (endPos) {
        let maxY = -Infinity;
        let maxYNodeId = '';
        nodePositions.forEach((pos, nodeId) => {
          if (nodeId !== 'end') {
            const nodeHeight = nodeHeights.get(nodeId) || BASE_NODE_HEIGHT;
            const nodeBottomY = pos.y + nodeHeight;
            if (nodeBottomY > maxY) {
              maxY = nodeBottomY;
              maxYNodeId = nodeId;
            }
          }
        });
        // Done must be below ALL other nodes with proper spacing
        if (endPos.y < maxY + MIN_VERTICAL_SPACING) {
          // Calculate how much below it should be
          const requiredY = maxY + MIN_VERTICAL_SPACING;
          const deviation = requiredY - endPos.y;
          // Apply penalty proportional to deviation (but minimum is the base penalty)
          totalPenalty += PENALTIES.DONE_NOT_AT_BOTTOM + (deviation * 1000);
          console.warn(`Done node violation: Done at y=${endPos.y}, but ${maxYNodeId} bottom is at y=${maxY}. Required y=${requiredY}`);
        }
      }
      
      // 4. Calculate edge length penalties
      edges.forEach(edge => {
        const sourcePos = nodePositions.get(edge.source);
        const targetPos = nodePositions.get(edge.target);
        if (sourcePos && targetPos) {
          const dx = targetPos.x - sourcePos.x;
          const dy = targetPos.y - sourcePos.y;
          const edgeLength = Math.sqrt(dx * dx + dy * dy);
          totalPenalty += edgeLength * PENALTIES.EDGE_LENGTH;
        }
      });
      
      // 5. Check for multiple edges from same handle (HEAVILY penalized - cluttered connections)
      const handleUsage = new Map<string, number>();
      edges.forEach(edge => {
        if (edge.sourceHandle) {
          const key = `${edge.source}-${edge.sourceHandle}`;
          handleUsage.set(key, (handleUsage.get(key) || 0) + 1);
        } else {
          // If no handle specified, assume default handle
          const key = `${edge.source}-default`;
          handleUsage.set(key, (handleUsage.get(key) || 0) + 1);
        }
      });
      handleUsage.forEach((count, key) => {
        if (count > 1) {
          // Exponential penalty - more edges from same handle = much worse
          const penalty = PENALTIES.MULTIPLE_EDGES_SAME_HANDLE * (count - 1) * count;
          totalPenalty += penalty;
          console.warn(`Multiple edges from same handle: ${key} has ${count} edges (penalty: ${penalty})`);
        }
      });
      
      // 5b. Check for edges crossing through nodes (visual overlap)
      edges.forEach(edge => {
        const sourcePos = nodePositions.get(edge.source);
        const targetPos = nodePositions.get(edge.target);
        if (sourcePos && targetPos) {
          // Check if edge line would pass through any node
          nodePositions.forEach((nodePos, nodeId) => {
            if (nodeId === edge.source || nodeId === edge.target) return;
            
            const nodeHeight = nodeHeights.get(nodeId) || BASE_NODE_HEIGHT;
            const nodeLeft = nodePos.x;
            const nodeRight = nodePos.x + NODE_WIDTH;
            const nodeTop = nodePos.y;
            const nodeBottom = nodePos.y + nodeHeight;
            
            // Check if edge line segment intersects with node bounding box
            // Simple check: if both endpoints are on same side, edge doesn't cross
            const sourceLeft = sourcePos.x < nodeLeft;
            const sourceRight = sourcePos.x > nodeRight;
            const sourceAbove = sourcePos.y < nodeTop;
            const sourceBelow = sourcePos.y > nodeBottom;
            
            const targetLeft = targetPos.x < nodeLeft;
            const targetRight = targetPos.x > nodeRight;
            const targetAbove = targetPos.y < nodeTop;
            const targetBelow = targetPos.y > nodeBottom;
            
            // Edge crosses through node if endpoints are on opposite sides
            const crossesHorizontally = (sourceLeft && targetRight) || (sourceRight && targetLeft);
            const crossesVertically = (sourceAbove && targetBelow) || (sourceBelow && targetAbove);
            
            // Also check if edge passes through the node's bounding box
            const minX = Math.min(sourcePos.x, targetPos.x);
            const maxX = Math.max(sourcePos.x, targetPos.x);
            const minY = Math.min(sourcePos.y, targetPos.y);
            const maxY = Math.max(sourcePos.y, targetPos.y);
            
            const passesThrough = 
              (minX < nodeRight && maxX > nodeLeft && minY < nodeBottom && maxY > nodeTop) &&
              (crossesHorizontally || crossesVertically);
            
            if (passesThrough) {
              totalPenalty += PENALTIES.EDGE_CROSSES_THROUGH_NODE;
              console.warn(`Edge ${edge.id} crosses through node ${nodeId}`);
            }
          });
        }
      });
      
      // 6. Check for consistent spacing in main path
      if (mainPath.length > 1) {
        for (let i = 1; i < mainPath.length; i++) {
          const prevNodeId = mainPath[i - 1];
          const currentNodeId = mainPath[i];
          const prevPos = nodePositions.get(prevNodeId);
          const currentPos = nodePositions.get(currentNodeId);
          
          if (prevPos && currentPos) {
            const prevHeight = nodeHeights.get(prevNodeId) || BASE_NODE_HEIGHT;
            const idealSpacing = prevPos.y + prevHeight + MIN_VERTICAL_SPACING;
            const actualSpacing = currentPos.y;
            const deviation = Math.abs(actualSpacing - idealSpacing);
            totalPenalty += deviation * PENALTIES.INCONSISTENT_SPACING;
          }
        }
      }
      
      // 7. Check conditional node positioning (CRITICAL - conditionals must branch to sides)
      const conditionalNodeIds = new Set<string>();
      edges.forEach(edge => {
        if (isConditionalEdge(edge)) {
          conditionalNodeIds.add(edge.target);
          
          const sourcePos = nodePositions.get(edge.source);
          const targetPos = nodePositions.get(edge.target);
          
          if (sourcePos && targetPos) {
            // CRITICAL: Conditional nodes must NOT be in main path
            if (mainPath.includes(edge.target)) {
              totalPenalty += PENALTIES.CONDITIONAL_IN_MAIN_PATH;
              console.warn(`CRITICAL: Conditional node ${edge.target} is in main path!`);
            }
            
            // CRITICAL: Conditional nodes must be horizontally offset from source (left or right)
            const horizontalOffset = Math.abs(targetPos.x - sourcePos.x);
            const minHorizontalOffset = CONDITIONAL_HORIZONTAL_SPACING * 0.5; // At least half spacing
            
            if (horizontalOffset < minHorizontalOffset) {
              totalPenalty += PENALTIES.CONDITIONAL_NOT_HORIZONTAL_OFFSET;
              console.warn(`Conditional node ${edge.target} not horizontally offset from source`);
            }
            
            // CRITICAL: Conditional nodes must NOT be in center column (BASE_X)
            const centerColumnTolerance = NODE_WIDTH * 0.3; // Allow some tolerance
            if (Math.abs(targetPos.x - BASE_X) < centerColumnTolerance) {
              totalPenalty += PENALTIES.CONDITIONAL_IN_CENTER_COLUMN;
              console.warn(`Conditional node ${edge.target} is in center column (should be left/right)`);
            }
            
            // Conditional nodes should be positioned near their source vertically
            const dx = Math.abs(targetPos.x - sourcePos.x);
            const dy = Math.abs(targetPos.y - sourcePos.y);
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // Penalize if conditional is too far from source
            const maxReasonableDistance = CONDITIONAL_HORIZONTAL_SPACING * 2;
            if (distance > maxReasonableDistance) {
              const excess = distance - maxReasonableDistance;
              totalPenalty += excess * PENALTIES.CONDITIONAL_TOO_FAR_FROM_SOURCE;
            }
            
            // Conditional nodes should alternate left/right from source
            // Check if multiple conditionals from same source are on same side
            const conditionalsFromSource = edges.filter(e => 
              e.source === edge.source && isConditionalEdge(e) && e.id !== edge.id
            );
            
            conditionalsFromSource.forEach(otherEdge => {
              const otherTargetPos = nodePositions.get(otherEdge.target);
              if (otherTargetPos) {
                const thisSide = targetPos.x > sourcePos.x ? 'right' : 'left';
                const otherSide = otherTargetPos.x > sourcePos.x ? 'right' : 'left';
                
                if (thisSide === otherSide) {
                  totalPenalty += PENALTIES.CONDITIONAL_WRONG_SIDE;
                }
              }
            });
          }
        }
      });
      
      // 7b. Ensure main path does NOT include conditional nodes
      mainPath.forEach(nodeId => {
        if (conditionalNodeIds.has(nodeId) && nodeId !== 'start' && nodeId !== 'end') {
          totalPenalty += PENALTIES.CONDITIONAL_IN_MAIN_PATH * 2; // Double penalty for being in main path
          console.warn(`CRITICAL: Conditional node ${nodeId} found in main path!`);
        }
      });
      
      // 8. Check node order in flow (nodes should be in logical order)
      const nodeLevels = new Map<string, number>();
      const visited = new Set<string>();
      const queue: { nodeId: string; level: number }[] = [{ nodeId: 'start', level: 0 }];
      
      while (queue.length > 0) {
        const { nodeId, level } = queue.shift()!;
        if (visited.has(nodeId)) continue;
        visited.add(nodeId);
        nodeLevels.set(nodeId, level);
        
        edges.forEach(edge => {
          if (edge.source === nodeId && !isConditionalEdge(edge)) {
            queue.push({ nodeId: edge.target, level: level + 1 });
          }
        });
      }
      
      // Check if nodes are positioned in wrong order
      nodePositions.forEach((pos, nodeId) => {
        if (nodeId === 'start' || nodeId === 'end') return;
        
        const nodeLevel = nodeLevels.get(nodeId) || 0;
        nodePositions.forEach((otherPos, otherNodeId) => {
          if (otherNodeId === nodeId || otherNodeId === 'start' || otherNodeId === 'end') return;
          
          const otherLevel = nodeLevels.get(otherNodeId) || 0;
          
          // If this node should come before other node in flow, but is positioned below it
          if (nodeLevel < otherLevel && pos.y > otherPos.y) {
            totalPenalty += PENALTIES.NODES_NOT_IN_FLOW_ORDER;
          }
        });
      });
      
      // ============================================================================
      // REWARD CALCULATION - Subtract rewards from penalties
      // ============================================================================
      let totalReward = 0;
      
      // 9. Reward vertical stacking of sub-questions (preferred layout)
      // Check each parent question and its sub-questions
      nodePositions.forEach((parentPos, parentNodeId) => {
        // Skip if not a main question (has decimal ID, it's a sub-question)
        if (parentNodeId.includes('.') && parentNodeId.match(/^\d+\.\d+$/)) return;
        if (parentNodeId === 'start' || parentNodeId === 'end') return;
        
        // Find all sub-questions (conditional children) of this parent
        const subQuestionEdges = edges.filter(e => 
          e.source === parentNodeId && isConditionalEdge(e)
        );
        
        if (subQuestionEdges.length === 0) return; // No sub-questions
        
        const parentHeight = nodeHeights.get(parentNodeId) || BASE_NODE_HEIGHT;
        const parentBottomY = parentPos.y + parentHeight;
        const parentX = parentPos.x;
        
        // Check each sub-question
        let verticalStackCount = 0;
        let sameXCount = 0;
        const subQuestionPositions: { nodeId: string; pos: { x: number; y: number } }[] = [];
        
        subQuestionEdges.forEach(edge => {
          const subQuestionPos = nodePositions.get(edge.target);
          if (!subQuestionPos) return;
          
          subQuestionPositions.push({ nodeId: edge.target, pos: subQuestionPos });
          
          // Check if sub-question is directly below parent (same X, below Y)
          const xDiff = Math.abs(subQuestionPos.x - parentX);
          const yDiff = subQuestionPos.y - parentBottomY;
          
          // Reward if placed directly below (same X coordinate, within tolerance)
          const X_TOLERANCE = 10; // Allow small X differences due to rounding
          const MIN_VERTICAL_GAP = MIN_VERTICAL_SPACING; // Must be below with proper spacing
          
          if (xDiff <= X_TOLERANCE && yDiff >= MIN_VERTICAL_GAP) {
            totalReward += REWARDS.SUB_QUESTION_VERTICAL_STACK;
            verticalStackCount++;
            console.log(`✅ Reward: Sub-question ${edge.target} vertically stacked below ${parentNodeId}`);
          }
          
          // Check if same X coordinate (even if not directly below)
          if (xDiff <= X_TOLERANCE) {
            sameXCount++;
          }
        });
        
        // Reward if multiple sub-questions are vertically aligned (same X)
        if (sameXCount >= 2) {
          totalReward += REWARDS.SUB_QUESTIONS_VERTICAL_ALIGNED;
          console.log(`✅ Reward: ${sameXCount} sub-questions vertically aligned for ${parentNodeId}`);
        }
        
        // Reward for compact vertical layout (all sub-questions stacked vertically)
        if (verticalStackCount === subQuestionEdges.length && subQuestionEdges.length >= 2) {
          totalReward += REWARDS.COMPACT_VERTICAL_LAYOUT;
          totalReward += REWARDS.ALL_SUB_QUESTIONS_VERTICAL; // Bonus for all being vertical
          console.log(`✅ Reward: Compact vertical layout for ${parentNodeId} (all ${verticalStackCount} sub-questions stacked)`);
        }
        
        // Reward when parent is centered at sub-question midpoint (balanced layout)
        if (subQuestionPositions.length >= 2) {
          // Calculate midpoint using actual sub-question positions and heights
          const subQuestionData = subQuestionPositions.map(sq => {
            const height = nodeHeights.get(sq.nodeId) || BASE_NODE_HEIGHT;
            return { y: sq.pos.y, height };
          });
          
          const topMostY = Math.min(...subQuestionData.map(sq => sq.y));
          const bottomMostY = Math.max(...subQuestionData.map(sq => sq.y + sq.height));
          const midpointY = (topMostY + bottomMostY) / 2;
          
          const parentCenterY = parentPos.y + (parentHeight / 2);
          const centerDiff = Math.abs(parentCenterY - midpointY);
          
          // Reward if parent is centered at midpoint (within tolerance)
          if (centerDiff < 50) { // 50px tolerance
            totalReward += REWARDS.PARENT_CENTERED_AT_SUB_MIDPOINT;
            console.log(`✅ Reward: Parent ${parentNodeId} centered at sub-question midpoint (diff: ${centerDiff.toFixed(1)}px)`);
          }
        }
        
        // Reward for sub-questions connecting directly to Done (cleaner flow)
        subQuestionEdges.forEach(edge => {
          const subQuestionPos = nodePositions.get(edge.target);
          if (!subQuestionPos) return;
          
          // Check if sub-question connects directly to Done
          const directToDone = edges.some(e => 
            e.source === edge.target && 
            (e.target === 'end' || e.target === 'done') &&
            !isConditionalEdge(e)
          );
          
          if (directToDone) {
            totalReward += REWARDS.SUB_QUESTION_DIRECT_TO_DONE;
            console.log(`✅ Reward: Sub-question ${edge.target} connects directly to Done`);
          }
          
          // Reward for short edges from sub-questions
          const subQuestionOutgoing = edges.filter(e => 
            e.source === edge.target && !isConditionalEdge(e)
          );
          subQuestionOutgoing.forEach(outEdge => {
            const targetPos = nodePositions.get(outEdge.target);
            if (targetPos) {
              const dx = Math.abs(targetPos.x - subQuestionPos.x);
              const dy = Math.abs(targetPos.y - subQuestionPos.y);
              const edgeLength = Math.sqrt(dx * dx + dy * dy);
              
              // Reward shorter edges (especially vertical ones)
              if (dx < 50) { // Mostly vertical
                const savedLength = 500 - edgeLength; // Assume 500px is "long"
                if (savedLength > 0) {
                  totalReward += savedLength * REWARDS.SHORT_SUB_QUESTION_EDGES / 100;
                }
              }
            }
          });
        });
      });
      
      // 10. Reward clean vertical flow (minimal horizontal offsets in main path)
      let cleanVerticalFlowCount = 0;
      mainPath.forEach((pathItem, index) => {
        if (index === 0) return; // Skip start
        
        const currentNodeId = pathItem.nodeId;
        const currentNodePos = nodePositions.get(currentNodeId);
        if (!currentNodePos) return;
        
        // Find previous node in main path
        if (index > 0) {
          const prevPathItem = mainPath[index - 1];
          const prevNodeId = prevPathItem.nodeId;
          const prevNodePos = nodePositions.get(prevNodeId);
          
          if (prevNodePos) {
            const xDiff = Math.abs(currentNodePos.x - prevNodePos.x);
            // Reward if nodes are vertically aligned (same X, within tolerance)
            if (xDiff <= 10) {
              cleanVerticalFlowCount++;
            }
          }
        }
      });
      
      if (cleanVerticalFlowCount > 0) {
        totalReward += cleanVerticalFlowCount * REWARDS.CLEAN_VERTICAL_FLOW;
        console.log(`✅ Reward: Clean vertical flow (${cleanVerticalFlowCount} aligned nodes in main path)`);
      }
      
      // 11. Reward minimal horizontal offsets (prefer vertical alignment)
      nodePositions.forEach((pos, nodeId) => {
        if (nodeId === 'start' || nodeId === 'end') return;
        
        // Find parent node (node that connects to this one via non-conditional edge)
        const incomingEdge = edges.find(e => 
          e.target === nodeId && !isConditionalEdge(e)
        );
        
        if (incomingEdge) {
          const parentPos = nodePositions.get(incomingEdge.source);
          if (parentPos) {
            const xDiff = Math.abs(pos.x - parentPos.x);
            
            // Reward minimal horizontal offset (prefer vertical alignment)
            if (xDiff < 10) {
              totalReward += REWARDS.MINIMAL_HORIZONTAL_OFFSET;
              totalReward += REWARDS.STRAIGHT_VERTICAL_LINES; // Bonus for straight vertical
            } else if (xDiff < 50) {
              totalReward += REWARDS.MINIMAL_HORIZONTAL_OFFSET * 0.5; // Partial reward
            }
          }
        }
      });
      
      // 12. Reward symmetrical layouts (balanced left/right distribution)
      const mainQuestions = Array.from(nodePositions.entries()).filter(([nodeId]) => {
        if (nodeId === 'start' || nodeId === 'end') return false;
        if (nodeId.includes('.') && nodeId.match(/^\d+\.\d+$/)) return false; // Skip sub-questions
        return true;
      });
      
      if (mainQuestions.length > 0) {
        // Calculate center X of main questions
        const centerX = mainQuestions.reduce((sum, [, pos]) => sum + pos.x, 0) / mainQuestions.length;
        
        // Check if layout is roughly centered
        const leftCount = mainQuestions.filter(([, pos]) => pos.x < centerX).length;
        const rightCount = mainQuestions.filter(([, pos]) => pos.x > centerX).length;
        const balanced = Math.abs(leftCount - rightCount) <= 1;
        
        if (balanced && mainQuestions.length >= 2) {
          totalReward += REWARDS.SYMMETRICAL_LAYOUT;
          console.log(`✅ Reward: Symmetrical layout (balanced distribution)`);
        }
      }
      
      // 13. Penalize horizontal positioning when vertical is clearly better
      // This is a negative reward (penalty) for choosing horizontal when vertical would work
      nodePositions.forEach((pos, nodeId) => {
        // Check if this is a sub-question
        if (!nodeId.includes('.') || !nodeId.match(/^\d+\.\d+$/)) return;
        
        // Find parent
        const parentMatch = nodeId.match(/^(\d+)\.\d+$/);
        if (!parentMatch) return;
        
        const parentId = parentMatch[1];
        const parentPos = nodePositions.get(parentId);
        if (!parentPos) return;
        
        const xDiff = Math.abs(pos.x - parentPos.x);
        const yDiff = pos.y - (parentPos.y + (nodeHeights.get(parentId) || BASE_NODE_HEIGHT));
        
        // If sub-question is horizontally offset but could be vertical (same X, below)
        // This means we chose horizontal when vertical was possible
        if (xDiff > 50 && yDiff > 0) {
          // Check if vertical position would have worked (no overlap)
          const verticalX = parentPos.x;
          const verticalY = parentPos.y + (nodeHeights.get(parentId) || BASE_NODE_HEIGHT) + MIN_VERTICAL_SPACING;
          
          let verticalWouldWork = true;
          nodePositions.forEach((otherPos, otherNodeId) => {
            if (otherNodeId === nodeId || otherNodeId === parentId) return;
            const otherHeight = nodeHeights.get(otherNodeId) || BASE_NODE_HEIGHT;
            
            const xOverlap = Math.abs(otherPos.x - verticalX) < (NODE_WIDTH * 0.8);
            const yOverlap = !(verticalY + (nodeHeights.get(nodeId) || BASE_NODE_HEIGHT) <= otherPos.y || 
                              otherPos.y + otherHeight <= verticalY);
            
            if (xOverlap && yOverlap) {
              verticalWouldWork = false;
            }
          });
          
          // Penalize choosing horizontal when vertical would work
          if (verticalWouldWork) {
            totalPenalty += PENALTIES.CONDITIONAL_NOT_HORIZONTAL_OFFSET * 0.5; // Half penalty as negative reward
            console.warn(`⚠️ Penalty: Sub-question ${nodeId} horizontally positioned when vertical would work`);
          }
        }
      });
      
      // Calculate final score (penalties minus rewards)
      const finalScore = totalPenalty - totalReward;
      
      console.log(`📊 Layout Score: ${finalScore.toFixed(2)} (Penalties: ${totalPenalty.toFixed(2)}, Rewards: ${totalReward.toFixed(2)})`);
      
      return finalScore;
    };
    
    // Calculate relative link length based on distance between nodes
    // Minimum link length is relative to node size, maximum is based on actual distance
    const getRelativeLinkLength = (distance: number, isConditional: boolean): number => {
      const minLength = isConditional ? NODE_WIDTH * 0.5 : NODE_WIDTH * 0.3; // 30-50% of node width
      const maxLength = distance * 1.5; // 150% of actual distance
      // Use actual distance, but ensure minimum spacing to prevent overlaps
      return Math.max(minLength, Math.min(distance * 0.8, maxLength));
    };
    
    // Build adjacency lists
    const outgoingEdges = new Map<string, Edge[]>();
    const incomingEdges = new Map<string, Edge[]>();
    edges.forEach((edge) => {
      if (!outgoingEdges.has(edge.source)) {
        outgoingEdges.set(edge.source, []);
      }
      outgoingEdges.get(edge.source)!.push(edge);
      
      if (!incomingEdges.has(edge.target)) {
        incomingEdges.set(edge.target, []);
      }
      incomingEdges.get(edge.target)!.push(edge);
    });

    // Calculate node levels using BFS (breadth-first search) for better hierarchy
    const nodeLevels = new Map<string, number>();
    const queue: { nodeId: string; level: number }[] = [{ nodeId: 'start', level: 0 }];
    const levelVisited = new Set<string>(['start']);
    nodeLevels.set('start', 0);

    while (queue.length > 0) {
      const { nodeId, level } = queue.shift()!;
      const edgesFromNode = outgoingEdges.get(nodeId) || [];
      
      // Sort edges: non-conditional first, then conditional
      const sortedEdges = [...edgesFromNode].sort((a, b) => {
        const aIsConditional = isConditionalEdge(a) ? 1 : 0;
        const bIsConditional = isConditionalEdge(b) ? 1 : 0;
        return aIsConditional - bIsConditional;
      });

      for (const edge of sortedEdges) {
        if (!levelVisited.has(edge.target)) {
          levelVisited.add(edge.target);
          const nextLevel = level + 1;
          nodeLevels.set(edge.target, nextLevel);
          queue.push({ nodeId: edge.target, level: nextLevel });
        } else {
          // Node already visited - update level if this path is shorter (for merge points)
          const existingLevel = nodeLevels.get(edge.target) || Infinity;
          const newLevel = level + 1;
          if (newLevel < existingLevel) {
            nodeLevels.set(edge.target, newLevel);
          }
        }
      }
    }

    // Find main path (following non-conditional edges, preferring shortest path)
    // CRITICAL: 'end' should ALWAYS be last, so we completely skip it during path finding
    // We'll rebuild the path by only following question nodes, then add 'end' at the end
    const mainPath: string[] = ['start'];
    const visited = new Set<string>(['start']);
    let current = 'start';
    const questionNodes = nodes.filter(n => n.id !== 'start' && n.id !== 'end' && n.type === 'question');
    const questionNodeIds = new Set(questionNodes.map(n => n.id));
    
    // Remove any existing 'end' from visited to ensure we don't accidentally include it
    visited.delete('end');

    // Build path through all questions, completely ignoring 'end' until the end
    // CRITICAL: Only follow edges TO question nodes, never TO 'end' during traversal
    // Also never traverse FROM 'end' - if current is 'end', we stop immediately
    while (true) {
      // Never traverse from 'end' - if we somehow got here, stop
      if (current === 'end') {
        break;
      }
      
      const edgesFromCurrent = outgoingEdges.get(current) || [];
      
      // Filter out 'end' completely - we'll add it manually at the end
      // Also filter out conditional edges for main path
      // Only follow edges to question nodes (not 'end' or other types)
      // Also never follow edges FROM 'end'
      const nonConditionalNonEnd = edgesFromCurrent.filter((e) => 
        !isConditionalEdge(e) && 
        e.target !== 'end' &&
        e.source !== 'end' && // Never follow edges from 'end'
        questionNodeIds.has(e.target) // Only follow edges to question nodes
      );
      
      let nextEdge;
      if (nonConditionalNonEnd.length > 0) {
      // If multiple non-conditional edges, prefer the one with lower level (shorter path)
        nextEdge = nonConditionalNonEnd.sort((a, b) => {
            const levelA = nodeLevels.get(a.target) || Infinity;
            const levelB = nodeLevels.get(b.target) || Infinity;
            return levelA - levelB;
        })[0];
      } else {
        // No more question nodes to visit - we've reached the end of the question chain
        break;
      }

      if (!nextEdge || visited.has(nextEdge.target)) {
        break;
      }

      current = nextEdge.target;
      // Never add 'end' to the path during traversal
      if (current === 'end') {
        break;
      }
      
      mainPath.push(current);
      visited.add(current);
    }
    
    // Always add 'end' at the end of the path if there's a connection to it
    // Find the last question node in the path that has a connection to 'end'
    // If no questions in path, check if 'start' has a connection to 'end'
    let lastNodeWithEndConnection = null;
    if (mainPath.length > 1) {
      // We have questions - find the last one with a connection to 'end'
      for (let i = mainPath.length - 1; i >= 0; i--) {
        const nodeId = mainPath[i];
        if (nodeId === 'start' || nodeId === 'end') continue;
        const edgesFromNode = outgoingEdges.get(nodeId) || [];
        if (edgesFromNode.some(e => e.target === 'end' && !isConditionalEdge(e))) {
          lastNodeWithEndConnection = nodeId;
          break;
        }
      }
    } else {
      // Only 'start' in path - check if 'start' connects to 'end'
      const edgesFromStart = outgoingEdges.get('start') || [];
      if (edgesFromStart.some(e => e.target === 'end' && !isConditionalEdge(e))) {
        lastNodeWithEndConnection = 'start';
      }
    }
    
    // If we found a connection to 'end', add it at the end
    if (lastNodeWithEndConnection && !mainPath.includes('end')) {
      mainPath.push('end');
    }

    // Node positions and heights are now calculated in the optimization section below
    // Old collision detection code removed - now using penalty-based scoring system
    
    // Helper function to check if a position would overlap with existing nodes
    const wouldOverlap = (x: number, y: number, nodeId: string, height: number): boolean => {
      for (const [existingNodeId, existingPos] of nodePositions.entries()) {
        if (existingNodeId === nodeId) continue;
        
        const existingHeight = nodeHeights.get(existingNodeId) || BASE_NODE_HEIGHT;
        const existingX = existingPos.x;
        const existingY = existingPos.y;
        
        // Check if nodes would overlap (with some padding)
        const padding = 20; // Extra padding between nodes
        const xOverlap = Math.abs(x - existingX) < (NODE_WIDTH + padding);
        const yOverlap = !(y + height + padding < existingY || y > existingY + existingHeight + padding);
        
        if (xOverlap && yOverlap) {
          return true;
        }
      }
      return false;
    };
    
    // Helper function to check and adjust for collisions with proper spacing
    const findAvailablePosition = (desiredX: number, desiredY: number, nodeId: string): { x: number; y: number } => {
      const nodeHeight = nodeHeights.get(nodeId) || BASE_NODE_HEIGHT;
      
      // Check if desired position is available
      if (!wouldOverlap(desiredX, desiredY, nodeId, nodeHeight)) {
        nodePositions.set(nodeId, { x: desiredX, y: desiredY });
        return { x: desiredX, y: desiredY };
      }
      
      // Collision detected - try positions below first (most common case)
      let attempts = 0;
      const maxAttempts = 10;
      const stepSize = nodeHeight + MIN_VERTICAL_SPACING;
      
      // Try positions below
      for (let i = 1; i <= maxAttempts; i++) {
        const newY = desiredY + (stepSize * i);
        if (!wouldOverlap(desiredX, newY, nodeId, nodeHeight)) {
          nodePositions.set(nodeId, { x: desiredX, y: newY });
          return { x: desiredX, y: newY };
        }
      }
      
      // Try positions to the right
      for (let i = 1; i <= 3; i++) {
        const newX = desiredX + (CONDITIONAL_HORIZONTAL_SPACING * i);
        if (!wouldOverlap(newX, desiredY, nodeId, nodeHeight)) {
          nodePositions.set(nodeId, { x: newX, y: desiredY });
          return { x: newX, y: desiredY };
        }
      }
      
      // Try positions to the left
      for (let i = 1; i <= 3; i++) {
        const newX = desiredX - (CONDITIONAL_HORIZONTAL_SPACING * i);
        if (!wouldOverlap(newX, desiredY, nodeId, nodeHeight)) {
          nodePositions.set(nodeId, { x: newX, y: desiredY });
          return { x: newX, y: desiredY };
        }
      }
      
      // Fallback: use desired position (will overlap but better than nothing)
      nodePositions.set(nodeId, { x: desiredX, y: desiredY });
      return { x: desiredX, y: desiredY };
    };

    // ============================================================================
    // FLOW-BASED INCREMENTAL OPTIMIZATION - Optimize each level as we traverse
    // ============================================================================
    // Track node heights for all calculations
    const nodeHeights = new Map<string, number>();
    nodes.forEach((node) => {
      nodeHeights.set(node.id, calculateNodeHeight(node));
    });
    
    // Get all children (connected nodes) for a given node
    const getChildren = (nodeId: string): { nodeId: string; isConditional: boolean; edge: Edge }[] => {
      const children: { nodeId: string; isConditional: boolean; edge: Edge }[] = [];
      edges.forEach(edge => {
        if (edge.source === nodeId) {
          children.push({
            nodeId: edge.target,
            isConditional: isConditionalEdge(edge),
            edge
          });
        }
      });
      return children;
    };
    
    // Optimize positions for a set of nodes relative to their parent
    const optimizeChildrenPositions = (
      parentId: string,
      children: { nodeId: string; isConditional: boolean; edge: Edge }[],
      currentPositions: Map<string, { x: number; y: number }>
    ): Map<string, { x: number; y: number }> => {
      const optimized = new Map(currentPositions);
      const parentPos = optimized.get(parentId);
      
      if (!parentPos) {
        return optimized; // Parent not positioned yet, skip
      }
      
      const parentHeight = nodeHeights.get(parentId) || BASE_NODE_HEIGHT;
      
      // Separate main path children (non-conditional) from conditional children
      const mainPathChildren = children.filter(c => !c.isConditional && c.nodeId !== 'end');
      const conditionalChildren = children.filter(c => c.isConditional);
      const endChild = children.find(c => c.nodeId === 'end');
      
      // Position main path child(ren) - should be in center column, below parent
      if (mainPathChildren.length > 0) {
        const mainChild = mainPathChildren[0]; // Take first non-conditional child
        const y = parentPos.y + parentHeight + MIN_VERTICAL_SPACING;
        optimized.set(mainChild.nodeId, { x: BASE_X, y });
      }
      
      // Position conditional children - to left/right of parent
      conditionalChildren.forEach((child, index) => {
        const childHeight = nodeHeights.get(child.nodeId) || BASE_NODE_HEIGHT;
        
        // Alternate left/right: even index = right, odd index = left
        const isRight = index % 2 === 0;
        const x = isRight 
          ? BASE_X + CONDITIONAL_HORIZONTAL_SPACING 
          : BASE_X - CONDITIONAL_HORIZONTAL_SPACING;
        
        // Position vertically: align with parent center, stagger if multiple
        const baseY = parentPos.y + (parentHeight / 2) - (childHeight / 2);
        const staggerOffset = Math.floor(index / 2) * (childHeight + MIN_VERTICAL_SPACING);
        const y = baseY + staggerOffset;
        
        optimized.set(child.nodeId, { x, y });
      });
      
      // Position 'end' node if present - must be below all other children
      if (endChild) {
        let maxY = parentPos.y + parentHeight;
        children.forEach(child => {
          if (child.nodeId !== 'end') {
            const childPos = optimized.get(child.nodeId);
            if (childPos) {
              const childHeight = nodeHeights.get(child.nodeId) || BASE_NODE_HEIGHT;
              maxY = Math.max(maxY, childPos.y + childHeight);
            }
          }
        });
        const endY = maxY + MIN_VERTICAL_SPACING;
        optimized.set('end', { x: BASE_X, y: endY });
      }
      
      return optimized;
    };
    
    // Traverse flow and optimize incrementally - following the flow structure
    // This approach optimizes each level as we traverse, using scoring to find best positions
    const nodePositions = new Map<string, { x: number; y: number }>();
    const processed = new Set<string>();
    const flowQueue: { nodeId: string; level: number }[] = [{ nodeId: 'start', level: 0 }];
    
    // Position start node
    nodePositions.set('start', { x: BASE_X, y: 0 });
    processed.add('start');
    
    // Process nodes level by level, following the flow
    while (flowQueue.length > 0) {
      const { nodeId, level } = flowQueue.shift()!;
      if (processed.has(nodeId)) continue;
      
      // Get all children of this node (both conditional and non-conditional)
      const children = getChildren(nodeId);
      
      if (children.length > 0) {
        // Separate children by type
        const nonConditionalChildren = children.filter(c => !c.isConditional && c.nodeId !== 'end');
        const conditionalChildren = children.filter(c => c.isConditional);
        const endChild = children.find(c => c.nodeId === 'end');
        
        const parentPos = nodePositions.get(nodeId);
        if (parentPos) {
          const parentHeight = nodeHeights.get(nodeId) || BASE_NODE_HEIGHT;
          
          // Optimize positions for all children using scoring
          // Try different position combinations and pick the best
          let bestChildLayout = new Map(nodePositions);
          let bestChildScore = Infinity;
          
          // Generate candidate positions for children
          const candidatePositions: Map<string, { x: number; y: number }>[] = [];
          
          // Candidate 1: Standard positioning
          const standardLayout = new Map(nodePositions);
          
          // Position main path child (non-conditional) - in center column, below parent
          if (nonConditionalChildren.length > 0) {
            const mainChild = nonConditionalChildren[0];
            const y = parentPos.y + parentHeight + MIN_VERTICAL_SPACING;
            standardLayout.set(mainChild.nodeId, { x: BASE_X, y });
          }
          
          // Position conditional children - to left/right of parent
          conditionalChildren.forEach((child, index) => {
            const childHeight = nodeHeights.get(child.nodeId) || BASE_NODE_HEIGHT;
            
            // Alternate left/right: even index = right, odd index = left
            const isRight = index % 2 === 0;
            const x = isRight 
              ? BASE_X + CONDITIONAL_HORIZONTAL_SPACING 
              : BASE_X - CONDITIONAL_HORIZONTAL_SPACING;
            
            // Position vertically: align with parent center, stagger if multiple
            const baseY = parentPos.y + (parentHeight / 2) - (childHeight / 2);
            const staggerOffset = Math.floor(index / 2) * (childHeight + MIN_VERTICAL_SPACING);
            const y = baseY + staggerOffset;
            
            standardLayout.set(child.nodeId, { x, y });
          });
          
          candidatePositions.push(standardLayout);
          
          // Try alternative positioning for conditionals (different left/right pattern)
          if (conditionalChildren.length > 1) {
            const altLayout = new Map(nodePositions);
            if (nonConditionalChildren.length > 0) {
              const mainChild = nonConditionalChildren[0];
              const y = parentPos.y + parentHeight + MIN_VERTICAL_SPACING;
              altLayout.set(mainChild.nodeId, { x: BASE_X, y });
            }
            
            // Try reversed left/right pattern
            conditionalChildren.forEach((child, index) => {
              const childHeight = nodeHeights.get(child.nodeId) || BASE_NODE_HEIGHT;
              const isRight = index % 2 === 1; // Reversed
              const x = isRight 
                ? BASE_X + CONDITIONAL_HORIZONTAL_SPACING 
                : BASE_X - CONDITIONAL_HORIZONTAL_SPACING;
              const baseY = parentPos.y + (parentHeight / 2) - (childHeight / 2);
              const staggerOffset = Math.floor(index / 2) * (childHeight + MIN_VERTICAL_SPACING);
              const y = baseY + staggerOffset;
              altLayout.set(child.nodeId, { x, y });
            });
            
            candidatePositions.push(altLayout);
          }
          
          // Score each candidate and pick the best
          candidatePositions.forEach(candidate => {
            // Create temporary main path for scoring (only includes positioned nodes)
            const tempMainPath = Array.from(nodePositions.keys()).filter(id => 
              id === 'start' || id === 'end' || candidate.has(id)
            );
            const score = scoreLayout(candidate, nodeHeights, edges, tempMainPath);
            
            if (score < bestChildScore) {
              bestChildScore = score;
              bestChildLayout = candidate;
            }
          });
          
          // Apply best layout
          bestChildLayout.forEach((pos, childId) => {
            if (!processed.has(childId)) {
              nodePositions.set(childId, pos);
            }
          });
          
          // Add children to queue for next level processing
          children.forEach(child => {
            if (!processed.has(child.nodeId) && child.nodeId !== 'end') {
              flowQueue.push({ nodeId: child.nodeId, level: level + 1 });
            }
          });
          
          // Position 'end' node if present - must be below all positioned nodes
          if (endChild) {
            let maxY = parentPos.y + parentHeight;
            nodePositions.forEach((pos, id) => {
              if (id !== 'end') {
                const h = nodeHeights.get(id) || BASE_NODE_HEIGHT;
                maxY = Math.max(maxY, pos.y + h);
              }
            });
            const endY = maxY + MIN_VERTICAL_SPACING;
            nodePositions.set('end', { x: BASE_X, y: endY });
          }
        }
      }
      
      processed.add(nodeId);
    }
    
    // Final optimization pass to fix any remaining issues using scoring
    let bestScore = scoreLayout(nodePositions, nodeHeights, edges, mainPath);
    let improved = true;
    let iterations = 0;
    const maxIterations = 10;
    
    while (improved && iterations < maxIterations) {
      improved = false;
      iterations++;
      
      nodePositions.forEach((pos, nodeId) => {
        if (nodeId === 'start' || nodeId === 'end') return;
        
        const isConditional = edges.some(e => e.target === nodeId && isConditionalEdge(e));
        
        // Try small adjustments
        const variations = [
          { x: pos.x, y: pos.y - MIN_VERTICAL_SPACING / 2 },
          { x: pos.x, y: pos.y + MIN_VERTICAL_SPACING / 2 },
        ];
        
        // For conditionals, also try horizontal adjustments (but keep offset from center)
        if (isConditional) {
          const currentOffset = Math.abs(pos.x - BASE_X);
          if (currentOffset < CONDITIONAL_HORIZONTAL_SPACING * 0.5) {
            // Too close to center - force to left or right
            variations.push(
              { x: BASE_X - CONDITIONAL_HORIZONTAL_SPACING, y: pos.y },
              { x: BASE_X + CONDITIONAL_HORIZONTAL_SPACING, y: pos.y }
            );
          } else {
            // Already offset - allow small adjustments
            variations.push(
              { x: pos.x - CONDITIONAL_HORIZONTAL_SPACING * 0.5, y: pos.y },
              { x: pos.x + CONDITIONAL_HORIZONTAL_SPACING * 0.5, y: pos.y }
            );
          }
        }
        
        for (const variation of variations) {
          if (variation.y < 0) continue;
          
          const testLayout = new Map(nodePositions);
          testLayout.set(nodeId, variation);
          const testScore = scoreLayout(testLayout, nodeHeights, edges, mainPath);
          
          if (testScore < bestScore) {
            nodePositions.set(nodeId, variation);
            bestScore = testScore;
            improved = true;
            break;
          }
        }
      });
    }
    
    // Log the final score for debugging
    const flowFinalScore = scoreLayout(nodePositions, nodeHeights, edges, mainPath);
    console.log(`Flow-based layout optimization complete. Final penalty score: ${flowFinalScore.toFixed(2)}`);
    
    // OLD POSITIONING CODE - Now using penalty-based optimization above
    // Keeping this section commented for reference, but it's been replaced by the scoring system
    /*
    // Position main path nodes vertically in center column with dynamic spacing
    // Calculate spacing based on number of conditionals at each level
    const conditionalCountByLevel = new Map<number, number>();
    edges.forEach((edge) => {
      if (isConditionalEdge(edge)) {
        const sourceLevel = nodeLevels.get(edge.source) || 0;
        const count = conditionalCountByLevel.get(sourceLevel) || 0;
        conditionalCountByLevel.set(sourceLevel, count + 1);
      }
    });

    // Separate 'end' from main path for positioning - it should always be last
    const mainPathWithoutEnd = mainPath.filter(nodeId => nodeId !== 'end');
    const hasEnd = mainPath.includes('end');

    let currentY = 0;
    // Position all nodes except 'end' first
    mainPathWithoutEnd.forEach((nodeId, index) => {
      const level = nodeLevels.get(nodeId) || index;
      const conditionalCount = conditionalCountByLevel.get(level) || 0;
      const nodeHeight = nodeHeights.get(nodeId) || BASE_NODE_HEIGHT;
      
      // Calculate spacing: base spacing + node height + minimal extra for conditionals
      // Compact spacing for shorter, straighter links
      const baseSpacing = MIN_VERTICAL_SPACING;
      const conditionalSpacing = conditionalCount * 10; // Minimal extra spacing for conditionals
      const spacing = baseSpacing + conditionalSpacing;
      
      if (index > 0) {
        // Get previous node's height to ensure proper spacing
        const prevNodeId = mainPathWithoutEnd[index - 1];
        const prevNodeHeight = nodeHeights.get(prevNodeId) || BASE_NODE_HEIGHT;
        const prevNodePos = nodePositions.get(prevNodeId);
        if (prevNodePos) {
          // Position directly below previous node for straight vertical connection
          // Use compact spacing to create shorter, straighter links
          currentY = prevNodePos.y + prevNodeHeight + spacing;
        } else {
          currentY += spacing;
        }
      }
      
      const pos = findAvailablePosition(BASE_X, currentY, nodeId);
      nodePositions.set(nodeId, pos);
      // Update currentY to the actual positioned Y for next iteration
      currentY = pos.y;
    });
    
    // Position 'end' node last, after all questions
    if (hasEnd) {
      const lastQuestionNodeId = mainPathWithoutEnd[mainPathWithoutEnd.length - 1];
      if (lastQuestionNodeId) {
        const lastQuestionPos = nodePositions.get(lastQuestionNodeId);
        const lastQuestionHeight = nodeHeights.get(lastQuestionNodeId) || BASE_NODE_HEIGHT;
        if (lastQuestionPos) {
          currentY = lastQuestionPos.y + lastQuestionHeight + MIN_VERTICAL_SPACING;
        }
      }
      const endPos = findAvailablePosition(BASE_X, currentY, 'end');
      nodePositions.set('end', endPos);
    }

    // Group conditional branches by their source node
    const conditionalGroups = new Map<string, { nodeId: string; edge: Edge }[]>();
    edges.forEach((edge) => {
      if (isConditionalEdge(edge)) {
        if (!conditionalGroups.has(edge.source)) {
          conditionalGroups.set(edge.source, []);
        }
        conditionalGroups.get(edge.source)!.push({ nodeId: edge.target, edge });
      }
    });

    // Track target nodes to identify when multiple edges target the same node
    const targetNodeGroups = new Map<string, { sourceId: string; nodeId: string; edge: Edge }[]>();
    conditionalGroups.forEach((conditionalNodes, sourceId) => {
      conditionalNodes.forEach((conditional) => {
        if (!targetNodeGroups.has(conditional.nodeId)) {
          targetNodeGroups.set(conditional.nodeId, []);
        }
        targetNodeGroups.get(conditional.nodeId)!.push({ sourceId, nodeId: conditional.nodeId, edge: conditional.edge });
      });
    });

    // Position conditional branches to left and right of source with better grouping
    conditionalGroups.forEach((conditionalNodes, sourceId) => {
      const sourceIndex = mainPath.indexOf(sourceId);
      if (sourceIndex >= 0) {
        const sourcePos = nodePositions.get(sourceId)!;
        const sourceY = sourcePos.y;
        const sourceX = sourcePos.x;
        
        // Group conditionals by their target merge point for better organization
        const conditionalsByTarget = new Map<string, typeof conditionalNodes>();
        conditionalNodes.forEach((conditional) => {
          const targetId = conditional.nodeId;
          if (!conditionalsByTarget.has(targetId)) {
            conditionalsByTarget.set(targetId, []);
          }
          conditionalsByTarget.get(targetId)!.push(conditional);
        });
        
        // Position conditionals: alternate left/right, but group by target
        // Position to minimize path length to target node
        let conditionalIndex = 0;
        conditionalsByTarget.forEach((groupedConditionals, targetId) => {
          // Get target node position to optimize conditional positioning
          const targetPos = nodePositions.get(targetId);
          const targetHeight = nodeHeights.get(targetId) || BASE_NODE_HEIGHT;
          
          groupedConditionals.forEach((conditional, groupIndex) => {
            const isRight = conditionalIndex % 2 === 0; // Even index = right, odd index = left
            
            // Calculate optimal X position based on target and source positions
            const sourceHeight = nodeHeights.get(sourceId) || BASE_NODE_HEIGHT;
            let finalX: number;
            
            // Minimum spacing to prevent node overlap - use more spacing for better visual separation
            const MIN_HORIZONTAL_SPACING = CONDITIONAL_MIN_HORIZONTAL_SPACING; // More breathing room for conditional links
            
            if (targetPos) {
              // Position conditional to create shorter path to target
              const targetX = targetPos.x;
              const targetY = targetPos.y;
            
              // Calculate distance from source to target
              const dxToTarget = targetX - sourceX;
              const dyToTarget = targetY - sourceY;
              const absDxToTarget = Math.abs(dxToTarget);
              const absDyToTarget = Math.abs(dyToTarget);
              
              // Determine if target is primarily horizontal or vertical
              const isTargetHorizontal = absDxToTarget > absDyToTarget * 0.8;
              
              if (isTargetHorizontal && Math.abs(dxToTarget) > 0) {
                // Target is primarily horizontal - position conditional close to source for short horizontal line
                // Just enough spacing to prevent overlap
                if (dxToTarget > 0) {
                  // Target to the right - position conditional just to the right of source
                  finalX = sourceX + MIN_HORIZONTAL_SPACING;
                } else {
                  // Target to the left - position conditional just to the left of source
                  finalX = sourceX - MIN_HORIZONTAL_SPACING;
                }
              } else {
                // Target is primarily vertical or position unknown - use relative positioning
                const baseTargetX = isRight ? CONDITIONAL_RIGHT_X : CONDITIONAL_LEFT_X;
            const horizontalDistance = Math.abs(baseTargetX - sourceX);
                const relativeLinkLength = getRelativeLinkLength(horizontalDistance, true);
                
                // Use minimum spacing but don't exceed relative link length
                const minSpacing = Math.min(MIN_HORIZONTAL_SPACING, relativeLinkLength);
                finalX = isRight ? sourceX + minSpacing : sourceX - minSpacing;
              }
            } else {
              // Target position not yet known - use minimum spacing for shorter lines
              finalX = isRight 
                ? sourceX + MIN_HORIZONTAL_SPACING 
                : sourceX - MIN_HORIZONTAL_SPACING;
            }
            
            // Calculate Y position: align with source for horizontal targets, otherwise stagger
            let conditionalY: number;
            const conditionalHeight = nodeHeights.get(conditional.nodeId) || BASE_NODE_HEIGHT;
            
            if (targetPos) {
              const targetY = targetPos.y;
              const dyToTarget = targetY - sourceY;
              const absDxToTarget = Math.abs(targetPos.x - sourceX);
              const absDyToTarget = Math.abs(dyToTarget);
              
              // Determine if target is primarily horizontal
              const isTargetHorizontal = absDxToTarget > absDyToTarget * 0.8;
              
              if (isTargetHorizontal) {
                // For horizontal targets, align conditional vertically with source for shortest horizontal line
                // Position at same vertical level as source (center-aligned)
                conditionalY = sourceY + (sourceHeight / 2) - (conditionalHeight / 2);
                
                // Ensure minimum vertical spacing to prevent overlap
                const minVerticalSpacing = 20;
                if (Math.abs(conditionalY - sourceY) < minVerticalSpacing) {
                  conditionalY = sourceY + sourceHeight + minVerticalSpacing;
                }
              } else {
                // For vertical targets, position between source and target
                const midY = sourceY + sourceHeight + CONDITIONAL_VERTICAL_OFFSET;
                conditionalY = Math.min(midY, targetY - (targetHeight / 2));
                
                // Ensure minimum spacing from source
                if (conditionalY < sourceY + sourceHeight + 20) {
                  conditionalY = sourceY + sourceHeight + CONDITIONAL_VERTICAL_OFFSET;
                }
              }
              
              // Stagger if multiple conditionals target same node
              conditionalY += groupIndex * (conditionalHeight + 30);
            } else {
              // Standard positioning - align with source for potential horizontal connections
              conditionalY = sourceY + (sourceHeight / 2) - (conditionalHeight / 2);
              const minVerticalSpacing = 20;
              if (Math.abs(conditionalY - sourceY) < minVerticalSpacing) {
                conditionalY = sourceY + sourceHeight + minVerticalSpacing;
              }
              
              // Stagger if multiple conditionals
              const staggerOffset = groupIndex * (conditionalHeight + 30);
              conditionalY += staggerOffset;
            }
            
            // If this conditional targets a merge point, position it to align better
            const targetGroup = targetNodeGroups.get(conditional.nodeId);
            if (targetGroup && targetGroup.length > 1 && targetPos) {
              // Multiple conditionals merge here - position to create a cleaner merge
              const mergePointLevel = nodeLevels.get(conditional.nodeId) || 0;
              const sourceLevel = nodeLevels.get(sourceId) || 0;
              const levelDiff = mergePointLevel - sourceLevel;
              
              // Position conditional nodes to create a more natural flow to merge point
              if (levelDiff > 1) {
                // Merge point is far away - position conditionals more vertically with proper spacing
                conditionalY = sourceY + sourceHeight + (CONDITIONAL_VERTICAL_OFFSET * (groupIndex + 1));
              }
            }
            
            // Only set position if this is a conditional branch node (not a main path node)
            if (mainPath.indexOf(conditional.nodeId) < 0) {
              const pos = findAvailablePosition(finalX, conditionalY, conditional.nodeId);
              nodePositions.set(conditional.nodeId, pos);
            }
            
            conditionalIndex++;
          });
        });
      }
    });
    
    // Adjust merge points (nodes that receive multiple conditional edges) to be positioned lower
    // This creates better visual separation and prevents edge overlap
    targetNodeGroups.forEach((group, targetId) => {
      if (group.length > 1) {
        const currentPos = nodePositions.get(targetId);
        if (currentPos) {
          // Calculate how much lower to position it based on number of incoming conditionals
          // Use MERGE_POINT_EXTRA_SPACING for better visual separation
          const extraSpacing = (group.length - 1) * MERGE_POINT_EXTRA_SPACING;
          
          // Find the maximum Y position of all sources to ensure merge point is below them
          let maxSourceY = currentPos.y;
          group.forEach(({ sourceId }) => {
            const sourcePos = nodePositions.get(sourceId);
            if (sourcePos) {
              maxSourceY = Math.max(maxSourceY, sourcePos.y);
            }
          });
          
          const newY = Math.max(currentPos.y, maxSourceY + MIN_VERTICAL_SPACING) + extraSpacing;
          const pos = findAvailablePosition(currentPos.x, newY, targetId);
          nodePositions.set(targetId, pos);
        }
      }
    });

    // Find the next main path node after conditionals
    // This handles cases where conditionals merge back to the main flow
    let nextMainPathIndex = mainPath.length;
    
    // Check if there are nodes that come after conditionals and should be in main flow
    const nodesAfterConditionals = new Set<string>();
    conditionalGroups.forEach((conditionalNodes, sourceId) => {
      const sourceIndex = mainPath.indexOf(sourceId);
      if (sourceIndex >= 0) {
        // Check if conditionals lead to nodes that should continue the main flow
        conditionalNodes.forEach((conditional) => {
          const edgesFromConditional = outgoingEdges.get(conditional.nodeId) || [];
          edgesFromConditional.forEach((edge) => {
            if (!isConditionalEdge(edge) && edge.target !== 'end') {
              // Check if this target has multiple incoming edges (merge point)
              const incomingToTarget = incomingEdges.get(edge.target) || [];
              if (incomingToTarget.length > 1 || mainPath.indexOf(edge.target) < 0) {
                nodesAfterConditionals.add(edge.target);
              }
            }
          });
        });
      }
    });

    // Position nodes that come after conditionals (merge back to main flow)
    if (nodesAfterConditionals.size > 0) {
      // Find the maximum Y position of all conditionals
      let maxConditionalY = 0;
      conditionalGroups.forEach((conditionalNodes, sourceId) => {
        const sourceIndex = mainPath.indexOf(sourceId);
        if (sourceIndex >= 0) {
          const sourceY = sourceIndex * VERTICAL_SPACING;
          const conditionalY = sourceY + CONDITIONAL_VERTICAL_OFFSET;
          maxConditionalY = Math.max(maxConditionalY, conditionalY + BASE_NODE_HEIGHT);
        }
      });

      // Position merge nodes below all conditionals, in center column
      nodesAfterConditionals.forEach((nodeId) => {
        if (!nodePositions.has(nodeId) && mainPath.indexOf(nodeId) < 0) {
          nodePositions.set(nodeId, {
            x: BASE_X,
            y: maxConditionalY + VERTICAL_SPACING,
          });
          // Update next main path index
          nextMainPathIndex = Math.max(nextMainPathIndex, Math.ceil((maxConditionalY + VERTICAL_SPACING) / VERTICAL_SPACING));
        }
      });
    }

    // Handle remaining nodes not in main path or conditionals
    // This includes nodes that merge from multiple conditionals (like "Done")
    nodes.forEach((node) => {
      if (!nodePositions.has(node.id)) {
        const incoming = incomingEdges.get(node.id) || [];
        if (incoming.length > 0) {
          // Check if this node receives edges from conditionals
          const hasConditionalSource = incoming.some((edge) => {
            const sourcePos = nodePositions.get(edge.source);
            return sourcePos && (sourcePos.x === CONDITIONAL_LEFT_X || sourcePos.x === CONDITIONAL_RIGHT_X);
          });
          
          if (hasConditionalSource) {
            // This is a merge node (like "Done" after conditionals)
            // Position it below all conditionals, in center column
            let maxConditionalY = 0;
            conditionalGroups.forEach((conditionalNodes, sourceId) => {
              const sourceIndex = mainPath.indexOf(sourceId);
              if (sourceIndex >= 0) {
                const sourceY = sourceIndex * VERTICAL_SPACING;
                const conditionalY = sourceY + CONDITIONAL_VERTICAL_OFFSET;
                maxConditionalY = Math.max(maxConditionalY, conditionalY + BASE_NODE_HEIGHT);
              }
            });
            
            nodePositions.set(node.id, {
              x: BASE_X,
              y: maxConditionalY + VERTICAL_SPACING,
            });
          } else {
            // Regular node, place below source in main flow
            const sourceEdge = incoming[0];
            const sourcePos = nodePositions.get(sourceEdge.source);
            if (sourcePos) {
              const nextMainIndex = Math.max(
                mainPath.length,
                Math.ceil((sourcePos.y + VERTICAL_SPACING) / VERTICAL_SPACING)
              );
              nodePositions.set(node.id, {
                x: BASE_X,
                y: nextMainIndex * VERTICAL_SPACING,
              });
            } else {
              // Fallback: keep current position
              nodePositions.set(node.id, node.position);
            }
          }
        } else {
          // No connections, keep current position
          nodePositions.set(node.id, node.position);
        }
      }
    });
    */
    
    /* OLD CODE COMMENTED OUT - Remove duplicate "done" nodes before organizing (keep only the one with ID 'end')
    const nodesWithoutDuplicates = nodes.filter((node) => {
      // Keep all nodes except "done" nodes that aren't 'end'
      if (node.type === 'output' && node.id !== 'end') {
        return false;
      }
      return true;
    });
    
    // Ensure 'end' node exists
    if (!nodesWithoutDuplicates.some((n) => n.id === 'end')) {
      nodesWithoutDuplicates.push(createEndNode());
    }
    
    // Apply positions to all nodes
    const organizedNodes = nodesWithoutDuplicates.map((node) => {
      const position = nodePositions.get(node.id);
      if (position) {
        return {
          ...node,
          position,
        };
      }
      return node;
    });

    // Remove edges that reference duplicate done nodes, and update references to point to 'end'
    const cleanedEdges = edges
      .filter((edge) => {
        // Remove edges that reference done nodes that aren't 'end'
        const sourceNode = nodes.find((n) => n.id === edge.source);
        const targetNode = nodes.find((n) => n.id === edge.target);
        if (sourceNode && sourceNode.type === 'output' && sourceNode.id !== 'end') {
          return false;
        }
        if (targetNode && targetNode.type === 'output' && targetNode.id !== 'end') {
          return false;
        }
        return true;
      })
      .map((edge) => {
        // Update references to point to 'end' instead of duplicate done nodes
        const sourceNode = nodes.find((n) => n.id === edge.source);
        const targetNode = nodes.find((n) => n.id === edge.target);
        let updatedEdge = { ...edge };
        if (sourceNode && sourceNode.type === 'output' && sourceNode.id !== 'end') {
          updatedEdge.source = 'end';
        }
        if (targetNode && targetNode.type === 'output' && targetNode.id !== 'end') {
          updatedEdge.target = 'end';
        }
        return updatedEdge;
      });
    
    // Helper function to get handle position on a node
    const getHandlePosition = (
      nodePos: { x: number; y: number },
      nodeHeight: number,
      handle: string
    ): { x: number; y: number } => {
      const halfHeight = nodeHeight / 2;
      
      switch (handle) {
        case 'top-input':
          return { x: nodePos.x, y: nodePos.y };
        case 'bottom-output':
          return { x: nodePos.x, y: nodePos.y + nodeHeight };
        case 'left-input':
        case 'left-output':
          return { x: nodePos.x, y: nodePos.y + halfHeight };
        case 'right-input':
        case 'right-output':
          return { x: nodePos.x + NODE_WIDTH, y: nodePos.y + halfHeight };
        default:
          return { x: nodePos.x, y: nodePos.y + halfHeight };
      }
    };

    // Calculate path length for a given handle combination
    // Path length = Manhattan distance + penalty for turns
    // Prefer paths with fewer turns and shorter total distance
    const calculatePathLength = (
      sourceHandlePos: { x: number; y: number },
      targetHandlePos: { x: number; y: number },
      sourceHandle: string,
      targetHandle: string,
      isConditional: boolean = false
    ): number => {
      const dx = targetHandlePos.x - sourceHandlePos.x;
      const dy = targetHandlePos.y - sourceHandlePos.y;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // Manhattan distance (L1 norm)
      const manhattanDistance = absDx + absDy;
      
      // Determine handle orientations
      const isSourceVertical = sourceHandle === 'top-input' || sourceHandle === 'bottom-output';
      const isTargetVertical = targetHandle === 'top-input' || targetHandle === 'bottom-input';
      const isSourceHorizontal = sourceHandle.includes('left') || sourceHandle.includes('right');
      const isTargetHorizontal = targetHandle.includes('left') || targetHandle.includes('right');
      
      // Count number of turns in the path for React Flow smoothstep routing
      // React Flow smoothstep: goes from source handle direction, then turns toward target
      // 0 turns = handles on same axis AND aligned (straight line)
      // 1 turn = handles on same axis but not aligned, OR different axes going in right direction
      // 2+ turns = going in wrong direction initially
      let numTurns = 0;
      
      if (isSourceVertical && isTargetVertical) {
        // Both handles are vertical (top/bottom)
        if (absDx < 5) {
          // Aligned horizontally - straight vertical line (0 turns)
          numTurns = 0;
        } else {
          // Not aligned - go vertical, then horizontal, then vertical (1 turn)
          numTurns = 1;
        }
      } else if (isSourceHorizontal && isTargetHorizontal) {
        // Both handles are horizontal (left/right)
        if (absDy < 5) {
          // Aligned vertically - straight horizontal line (0 turns)
          numTurns = 0;
        } else {
          // Not aligned - go horizontal, then vertical, then horizontal (1 turn)
          numTurns = 1;
        }
        } else {
        // Different axes - need to turn
        const sourceGoesRight = sourceHandle.includes('right');
        const sourceGoesLeft = sourceHandle.includes('left');
        const sourceGoesDown = sourceHandle === 'bottom-output';
        const sourceGoesUp = sourceHandle === 'top-input';
        
        // Check if initial direction matches target direction
        if (isSourceVertical) {
          // Source is vertical - check if going in right vertical direction
          const goingRightDirection = (sourceGoesDown && dy > 0) || (sourceGoesUp && dy < 0);
          if (goingRightDirection) {
            // Going in right direction, then turn horizontal (1 turn)
            numTurns = 1;
          } else if (absDy < 5) {
            // Target is at same Y - just turn horizontal (1 turn, but shorter path)
            numTurns = 1;
          } else {
            // Going wrong direction initially (2 turns: reverse, then turn)
            numTurns = 2;
        }
      } else {
          // Source is horizontal - check if going in right horizontal direction
          const goingRightDirection = (sourceGoesRight && dx > 0) || (sourceGoesLeft && dx < 0);
          if (goingRightDirection) {
            // Going in right direction, then turn vertical (1 turn)
            numTurns = 1;
          } else if (absDx < 5) {
            // Target is at same X - just turn vertical (1 turn, but shorter path)
            numTurns = 1;
        } else {
            // Going wrong direction initially (2 turns: reverse, then turn)
            numTurns = 2;
          }
        }
      }
      
      // Penalty based on number of turns (heavily penalize turns)
      // Use exponential penalty to strongly prefer fewer turns
      let turnPenalty = numTurns === 0 ? 0 : numTurns === 1 ? 150 : 500;
      
      // HEAVY penalty for conditional edges using vertical handles when target is horizontal
      // This ensures conditional edges (dashed red lines) use direct horizontal connections
      if (isConditional && isSourceVertical && absDx > absDy * 0.7) {
        // Conditional edge using vertical handle for horizontal target - add huge penalty
        turnPenalty += 1000; // Massive penalty to prevent this
      }

      // Additional penalty for longer paths (prefer shorter paths)
      // This helps when multiple paths have same number of turns
      const distancePenalty = manhattanDistance * 0.1;
      
      return manhattanDistance + turnPenalty + distancePenalty;
    };

    // Find optimal handle combination for shortest path with minimal turns
    const findOptimalHandles = (
      sourcePos: { x: number; y: number },
      targetPos: { x: number; y: number },
      sourceHeight: number,
      targetHeight: number,
      isConditional: boolean,
      otherEdgesFromSource: Edge[] = [] // Other edges from the same source to avoid conflicts
    ): { sourceHandle: string; targetHandle: string } => {
      // Calculate relative positions
      const dx = targetPos.x - sourcePos.x;
      const dy = targetPos.y - sourcePos.y;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      
      // Determine which handles are already used by other edges from this source
      const usedSourceHandles = new Set(
        otherEdgesFromSource.map(e => e.sourceHandle).filter(Boolean)
      );
      
      // Available handles - prioritize based on target position
      let sourceHandles: string[];
      let targetHandles: string[];
      
      if (isConditional) {
        // Conditionals: prefer horizontal connections
        sourceHandles = dx > 0 
          ? ['right-output', 'left-output'] // Target to right, prefer right-output
          : ['left-output', 'right-output']; // Target to left, prefer left-output
        targetHandles = dx > 0
          ? ['left-input', 'right-input'] // Target to right, prefer left-input
          : ['right-input', 'left-input']; // Target to left, prefer right-input
        } else {
        // Regular edges: choose based on relative position
        // Strongly prefer direct connections (horizontal for horizontal targets, vertical for vertical targets)
        if (absDx > absDy * 1.2) {
          // Target is primarily horizontal - STRONGLY prefer horizontal handles
          // This ensures targets to the right use right-output, targets to the left use left-output
          sourceHandles = dx > 0 
            ? ['right-output', 'left-output', 'bottom-output'] // Right first for right targets
            : ['left-output', 'right-output', 'bottom-output']; // Left first for left targets
          targetHandles = dx > 0
            ? ['left-input', 'right-input', 'top-input'] // Left input for right targets
            : ['right-input', 'left-input', 'top-input']; // Right input for left targets
        } else if (absDy > absDx * 1.2) {
          // Target is primarily vertical - prefer vertical handles
          sourceHandles = dy > 0
            ? ['bottom-output', 'right-output', 'left-output'] // Down first for targets below
            : ['bottom-output', 'right-output', 'left-output']; // No top-output available
          targetHandles = dy > 0
            ? ['top-input', 'left-input', 'right-input'] // Top input for targets below
            : ['top-input', 'left-input', 'right-input'];
        } else {
          // Diagonal - try all options, but still prefer based on dominant direction
          if (absDx > absDy) {
            sourceHandles = dx > 0 
              ? ['right-output', 'bottom-output', 'left-output']
              : ['left-output', 'bottom-output', 'right-output'];
            targetHandles = dx > 0
              ? ['left-input', 'top-input', 'right-input']
              : ['right-input', 'top-input', 'left-input'];
          } else {
            sourceHandles = ['bottom-output', 'right-output', 'left-output'];
            targetHandles = ['top-input', 'left-input', 'right-input'];
          }
        }
      }
      
      // Filter out handles already used by other edges
      // CRITICAL: Never allow multiple edges to use the same source handle
      const availableSourceHandles = sourceHandles.filter(h => 
        !usedSourceHandles.has(h)
      );
      
      // If all preferred handles are used, we need to use a different handle
      // Fall back to all valid output handles, excluding used ones
      const allValidSourceHandles = ['bottom-output', 'right-output', 'left-output'];
      const finalSourceHandles = availableSourceHandles.length > 0 
        ? availableSourceHandles 
        : allValidSourceHandles.filter(h => !usedSourceHandles.has(h));
      
      // If still no handles available (shouldn't happen, but safety check)
      if (finalSourceHandles.length === 0) {
        console.warn(`⚠️ All source handles are used for node, falling back to bottom-output`);
        return { sourceHandle: 'bottom-output', targetHandle: 'top-input' };
      }
      
      let bestCombination: { sourceHandle: string; targetHandle: string } | null = null;
      let shortestPath = Infinity;
      
      // Try all combinations
      for (const sourceHandle of finalSourceHandles) {
        // Validate source handle is actually an output handle
        if (!sourceHandle.includes('output')) {
          console.warn(`⚠️ Invalid source handle: ${sourceHandle}, skipping`);
          continue;
        }
        
        for (const targetHandle of targetHandles) {
          // Validate target handle is actually an input handle
          if (!targetHandle.includes('input')) {
            console.warn(`⚠️ Invalid target handle: ${targetHandle}, skipping`);
            continue;
          }
          
          const sourceHandlePos = getHandlePosition(sourcePos, sourceHeight, sourceHandle);
          const targetHandlePos = getHandlePosition(targetPos, targetHeight, targetHandle);
          
          const pathLength = calculatePathLength(
            sourceHandlePos,
            targetHandlePos,
            sourceHandle,
            targetHandle,
            isConditional
          );
          
          if (pathLength < shortestPath) {
            shortestPath = pathLength;
            bestCombination = { sourceHandle, targetHandle };
          }
        }
      }
      
      return bestCombination || { sourceHandle: 'bottom-output', targetHandle: 'top-input' };
    };

    // Rule 1: Snap edges to closest link points based on node positions with optimal routing
    // Group edges by source to optimize all edges from each source together
    const edgesBySource = new Map<string, Edge[]>();
    cleanedEdges.forEach(edge => {
      if (!edgesBySource.has(edge.source)) {
        edgesBySource.set(edge.source, []);
      }
      edgesBySource.get(edge.source)!.push(edge);
    });
    
    // Optimize edges by source node - find best handle assignment for all edges from each source
    const optimizedEdgeMap = new Map<string, Edge>();
    
    edgesBySource.forEach((sourceEdges, sourceId) => {
      const sourcePos = nodePositions.get(sourceId);
      if (!sourcePos) {
        // Keep original edges if source position not found
        sourceEdges.forEach(edge => optimizedEdgeMap.set(edge.id, edge));
        return;
      }
      
      const sourceHeight = nodeHeights.get(sourceId) || BASE_NODE_HEIGHT;
      
      if (sourceEdges.length === 1) {
        // Single edge - optimize independently
        const edge = sourceEdges[0];
        const targetPos = nodePositions.get(edge.target);
        if (!targetPos) {
          optimizedEdgeMap.set(edge.id, edge);
          return;
        }
        
        const targetHeight = nodeHeights.get(edge.target) || BASE_NODE_HEIGHT;
        const isConditional = isConditionalEdge(edge);
        
        const optimalHandles = findOptimalHandles(
          sourcePos,
          targetPos,
          sourceHeight,
          targetHeight,
          isConditional,
          []
        );
        
        optimizedEdgeMap.set(edge.id, {
          ...edge,
          sourceHandle: optimalHandles.sourceHandle,
          targetHandle: optimalHandles.targetHandle,
        });
        } else {
        // Multiple edges from same source - optimize together
        // Find the combination of handle assignments that minimizes total path cost
        type EdgeAssignment = { edgeId: string; sourceHandle: string; targetHandle: string };
        const edgeConfigs = sourceEdges.map(edge => {
          const targetPos = nodePositions.get(edge.target);
          if (!targetPos) return null;
          
          const targetHeight = nodeHeights.get(edge.target) || BASE_NODE_HEIGHT;
          const isConditional = isConditionalEdge(edge);
          
          // Get all possible handle combinations for this edge
          const dx = targetPos.x - sourcePos.x;
          const dy = targetPos.y - sourcePos.y;
          const absDx = Math.abs(dx);
          const absDy = Math.abs(dy);
          
          let sourceHandles: string[];
          let targetHandles: string[];
          
          if (isConditional) {
            // Conditional edges STRONGLY prefer horizontal handles for horizontal targets
            // This ensures direct horizontal connections (0-1 turns) instead of down-then-right paths (2+ turns)
            if (absDx > absDy * 0.7) {
              // Target is primarily horizontal - STRONGLY prefer horizontal handles
              // Put horizontal handles first, but allow vertical as last resort
              sourceHandles = dx > 0 
                ? ['right-output', 'left-output', 'bottom-output'] 
                : ['left-output', 'right-output', 'bottom-output'];
              targetHandles = dx > 0 
                ? ['left-input', 'right-input', 'top-input'] 
                : ['right-input', 'left-input', 'top-input'];
            } else {
              // Target is primarily vertical - allow both horizontal and vertical
              sourceHandles = dx > 0 
                ? ['right-output', 'left-output', 'bottom-output'] 
                : ['left-output', 'right-output', 'bottom-output'];
              targetHandles = dx > 0 
                ? ['left-input', 'right-input', 'top-input'] 
                : ['right-input', 'left-input', 'top-input'];
        }
      } else {
            if (absDx > absDy * 1.2) {
              sourceHandles = dx > 0 ? ['right-output', 'left-output', 'bottom-output'] : ['left-output', 'right-output', 'bottom-output'];
              targetHandles = dx > 0 ? ['left-input', 'right-input', 'top-input'] : ['right-input', 'left-input', 'top-input'];
            } else if (absDy > absDx * 1.2) {
              sourceHandles = ['bottom-output', 'right-output', 'left-output'];
              targetHandles = ['top-input', 'left-input', 'right-input'];
        } else {
              sourceHandles = ['bottom-output', 'right-output', 'left-output'];
              targetHandles = ['top-input', 'left-input', 'right-input'];
        }
      }

        return {
            edge,
            targetPos,
            targetHeight,
            isConditional,
            sourceHandles,
            targetHandles,
          };
        }).filter((config): config is NonNullable<typeof config> => config !== null);
        
        // Try all combinations of handle assignments and find the one with minimum total cost
        // Limit to reasonable number of edges to avoid performance issues
        if (edgeConfigs.length > 5) {
          // Too many edges - fall back to independent optimization
          edgeConfigs.forEach(config => {
            const optimalHandles = findOptimalHandles(
              sourcePos,
              config.targetPos,
              sourceHeight,
              config.targetHeight,
              config.isConditional,
              []
            );
            optimizedEdgeMap.set(config.edge.id, {
              ...config.edge,
              sourceHandle: optimalHandles.sourceHandle,
              targetHandle: optimalHandles.targetHandle,
            });
          });
          return;
        }
        
        let bestCombination: EdgeAssignment[] | null = null;
        let bestTotalCost = Infinity;
        
        // Generate all combinations (typically only 2-3 edges per source, so this is efficient)
        const generateCombinations = (
          configs: typeof edgeConfigs, 
          index: number, 
          current: EdgeAssignment[], 
          usedSourceHandles: Set<string>
        ): void => {
          if (index >= configs.length) {
            // Calculate total cost for this combination
            let totalCost = 0;
            for (const assignment of current) {
              const config = configs.find(c => c.edge.id === assignment.edgeId);
              if (config) {
                const sourceHandlePos = getHandlePosition(sourcePos, sourceHeight, assignment.sourceHandle);
                const targetHandlePos = getHandlePosition(config.targetPos, config.targetHeight, assignment.targetHandle);
                const cost = calculatePathLength(
                  sourceHandlePos,
                  targetHandlePos,
                  assignment.sourceHandle,
                  assignment.targetHandle,
                  config.isConditional
                );
                totalCost += cost;
              }
            }
            
            if (totalCost < bestTotalCost) {
              bestTotalCost = totalCost;
              bestCombination = [...current];
            }
            return;
          }
          
          const config = configs[index];
          // Try each source handle for this edge
          for (const sourceHandle of config.sourceHandles) {
            // CRITICAL: Never allow multiple edges to use the same source handle
            // Skip if this handle is already used
            if (usedSourceHandles.has(sourceHandle)) {
              continue;
            }
            
            // Validate source handle is actually an output handle
            if (!sourceHandle.includes('output')) {
              console.warn(`⚠️ Invalid source handle in config: ${sourceHandle}, skipping`);
              continue;
            }
            
            // Try each target handle
            for (const targetHandle of config.targetHandles) {
              // Validate target handle is actually an input handle
              if (!targetHandle.includes('input')) {
                console.warn(`⚠️ Invalid target handle in config: ${targetHandle}, skipping`);
                continue;
              }
              
              // Mark this handle as used
              const newUsedHandles = new Set(usedSourceHandles);
              newUsedHandles.add(sourceHandle);
              
              generateCombinations(
                configs,
                index + 1,
                [...current, { edgeId: config.edge.id, sourceHandle, targetHandle }],
                newUsedHandles
              );
            }
          }
        };
        
        generateCombinations(edgeConfigs, 0, [], new Set());
        
        // Apply the best combination
        if (bestCombination !== null) {
          const combinationLength = (bestCombination as EdgeAssignment[]).length;
          if (combinationLength === sourceEdges.length) {
            const validCombination = bestCombination as EdgeAssignment[];
            // Validate all assignments have unique source handles
            const usedHandles = new Set<string>();
            let hasDuplicate = false;
            for (const assignment of validCombination) {
              if (usedHandles.has(assignment.sourceHandle)) {
                console.warn(`⚠️ Duplicate source handle detected: ${assignment.sourceHandle} for edge ${assignment.edgeId}`);
                hasDuplicate = true;
                break;
              }
              usedHandles.add(assignment.sourceHandle);
            }
            
            if (!hasDuplicate) {
              validCombination.forEach((assignment: EdgeAssignment) => {
                const { edgeId, sourceHandle, targetHandle } = assignment;
                // Final validation: ensure handles are valid
                if (!sourceHandle.includes('output')) {
                  console.error(`❌ Invalid source handle: ${sourceHandle} for edge ${edgeId}`);
                  return;
                }
                if (!targetHandle.includes('input')) {
                  console.error(`❌ Invalid target handle: ${targetHandle} for edge ${edgeId}`);
                  return;
                }
                
                const edge = sourceEdges.find(e => e.id === edgeId);
                if (edge) {
                  optimizedEdgeMap.set(edgeId, {
                    ...edge,
                    sourceHandle,
                    targetHandle,
                    // Preserve reconnectable status for conditional edges
                    reconnectable: isConditionalEdge(edge) ? false : (edge.reconnectable !== false),
                  });
                }
              });
            } else {
            // Fallback if duplicates found
            console.warn(`⚠️ Falling back to independent optimization due to duplicate handles`);
            sourceEdges.forEach(edge => {
              const targetPos = nodePositions.get(edge.target);
              if (!targetPos) {
                optimizedEdgeMap.set(edge.id, edge);
                return;
              }
              
              const targetHeight = nodeHeights.get(edge.target) || BASE_NODE_HEIGHT;
              const isConditional = isConditionalEdge(edge);
              
              // Get already assigned edges from this source to avoid conflicts
              const alreadyAssigned = Array.from(optimizedEdgeMap.values())
                .filter(e => e.source === sourceId);
              
              const optimalHandles = findOptimalHandles(
                sourcePos,
                targetPos,
                sourceHeight,
                targetHeight,
                isConditional,
                alreadyAssigned
              );
              
              optimizedEdgeMap.set(edge.id, {
                ...edge,
                sourceHandle: optimalHandles.sourceHandle,
                targetHandle: optimalHandles.targetHandle,
                // Preserve reconnectable status for conditional edges
                reconnectable: isConditional ? false : (edge.reconnectable !== false),
              });
            });
            }
          }
        } else {
          // Fallback: optimize each edge independently
          const usedHandles = new Set<string>();
          sourceEdges.forEach(edge => {
            const targetPos = nodePositions.get(edge.target);
            if (!targetPos) {
              optimizedEdgeMap.set(edge.id, edge);
              return;
            }
            
            const targetHeight = nodeHeights.get(edge.target) || BASE_NODE_HEIGHT;
            const isConditional = isConditionalEdge(edge);
            
            // Get already assigned edges from this source to avoid conflicts
            const alreadyAssigned = Array.from(optimizedEdgeMap.values())
              .filter(e => e.source === sourceId);
            
            const optimalHandles = findOptimalHandles(
              sourcePos,
              targetPos,
              sourceHeight,
              targetHeight,
              isConditional,
              alreadyAssigned
            );
            
            // Ensure we don't reuse handles
            let finalSourceHandle = optimalHandles.sourceHandle;
            if (usedHandles.has(finalSourceHandle)) {
              // Find an unused handle
              const allHandles = ['bottom-output', 'right-output', 'left-output'];
              const unusedHandle = allHandles.find(h => !usedHandles.has(h));
              if (unusedHandle) {
                finalSourceHandle = unusedHandle;
                console.warn(`⚠️ Handle conflict for edge ${edge.id}, using ${finalSourceHandle} instead`);
              }
            }
            usedHandles.add(finalSourceHandle);
            
            optimizedEdgeMap.set(edge.id, {
              ...edge,
              sourceHandle: finalSourceHandle,
              targetHandle: optimalHandles.targetHandle,
              // Preserve reconnectable status for conditional edges
              reconnectable: isConditional ? false : (edge.reconnectable !== false),
            });
          });
        }
      }
    });
    
    // Convert map back to array, keeping edges that weren't optimized
    // Final validation: filter out edges with invalid handles
    const organizedEdges = cleanedEdges
      .map(edge => {
        const optimizedEdge = optimizedEdgeMap.get(edge.id) || edge;
        
        // Validate handles are correct type
        if (optimizedEdge.sourceHandle && !optimizedEdge.sourceHandle.includes('output')) {
          console.error(`❌ Invalid source handle in final edge: ${optimizedEdge.sourceHandle} for edge ${optimizedEdge.id}`);
          // Fix it by using a valid default
          return {
            ...optimizedEdge,
            sourceHandle: 'bottom-output',
          };
        }
        if (optimizedEdge.targetHandle && !optimizedEdge.targetHandle.includes('input')) {
          console.error(`❌ Invalid target handle in final edge: ${optimizedEdge.targetHandle} for edge ${optimizedEdge.id}`);
          // Fix it by using a valid default
          return {
            ...optimizedEdge,
            targetHandle: 'top-input',
          };
        }
        
        return optimizedEdge;
      })
      .filter(edge => {
        // Ensure edge has valid source and target nodes
        const sourceNode = organizedNodes.find(n => n.id === edge.source);
        const targetNode = organizedNodes.find(n => n.id === edge.target);
        if (!sourceNode || !targetNode) {
          console.warn(`⚠️ Removing edge ${edge.id} with invalid source or target node`);
          return false;
        }
        return true;
      });

      // Update nodes and edges first
      setNodes(organizedNodes);
      setEdges(organizedEdges);
      
      // Feature 9: Trigger after organizing to ensure start-to-done connection and renumber
      setTimeout(() => {
        setEdges((currentEdges) => {
          const reconnectedEdges = ensureStartToDoneConnection(organizedNodes, currentEdges);
          setNodes((currentNodes) => {
            const { nodes: renumberedNodes, edges: renumberedEdges } = renumberQuestionsInFlow(currentNodes, reconnectedEdges);
            setEdges(renumberedEdges);
            return renumberedNodes;
          });
          return reconnectedEdges;
        });
      }, 0);
    */
  }, [nodes, edges, setNodes, setEdges]);

  const deleteSelectedNodes = () => {
    setNodes((nds) => {
      const kept = nds.filter((node) => !node.selected || isStartNode(node) || isEndNode(node));
      const laidOut = layoutLinearNodes(kept);
      const linearEdges = buildLinearEdgesFromNodes(laidOut);
      setEdges((prevEdges) => mergeLinearEdges(prevEdges, linearEdges));
      return laidOut;
    });
  };

  const unlinkSelectedNodes = useCallback(() => {
    const selectedNodeIds = nodes
      .filter((node) => node.selected && node.id !== 'start' && node.id !== 'end')
      .map((node) => node.id);
    
    if (selectedNodeIds.length === 0) return;
    
    setEdges((currentEdges) => {
      const updatedEdges = currentEdges.filter(
        (edge) => !selectedNodeIds.includes(edge.source) && !selectedNodeIds.includes(edge.target)
      );
      // Ensure start-to-done connection after unlinking
      const reconnectedEdges = ensureStartToDoneConnection(nodes, updatedEdges);
      return reconnectedEdges;
    });
    
    // Trigger renumbering after unlinking
    setTimeout(() => {
      setNodes((currentNodes) => {
        setEdges((currentEdges) => {
          const { nodes: renumberedNodes, edges: renumberedEdges } = renumberQuestionsInFlow(currentNodes, currentEdges);
          setNodes(renumberedNodes);
          return renumberedEdges;
        });
        return currentNodes;
      });
    }, 0);
    
    toast.success(`${selectedNodeIds.length} question(s) unlinked from all connections`);
  }, [nodes, setEdges, setNodes]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">
          Manage Surveys
        </h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchSurveys}
            disabled={loading}
          >
            <IconRefresh className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
          >
            <IconPlus className="mr-2 h-4 w-4" />
            New Survey
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-sm underline">Dismiss</button>
          </div>
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center p-8">
          <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : surveys.length === 0 ? (
        <Card className="p-8">
          <p className="text-muted-foreground text-center">
            No surveys found. Create your first survey to get started.
          </p>
        </Card>
      ) : (
        <SurveyList
          surveys={surveys}
          statusUpdatingId={statusUpdatingId}
          onEditSurvey={handleOpenDialog}
          onToggleActive={handleToggleActive}
        />
      )}
    </div>
  );
}

